import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Table, Button, Alert, Spin, Tag, Row, Col, Statistic, Input, Select, Tabs, Modal, Icon, DatePicker } from 'antd';
import { loadCSV } from './utils/csvLoader';
import moment from 'moment-timezone';
import { 
  fetchIotDeviceDataInPeriod, 
  findAssetIdByCode, 
  getDeviceIdFromAsset 
} from './utils/fuelService';
import { apiGet } from './utils/apiService';
import { detectFuelLeakageEvents, validateSamples, EVENT_STATUS, DEFAULT_CONFIG } from './utils/fuelTheftDetection';

const { TabPane } = Tabs;

// Utility function to convert table data to CSV (handles columns with render functions)
const convertTableToCSV = (columns, dataSource, filename) => {
  // Extract headers from columns
  const headers = columns.map(col => col.title || col.key || '').filter(h => h);
  
  // Convert data rows
  const rows = dataSource.map((row, rowIndex) => {
    return columns.map((col, colIndex) => {
      if (!col.dataIndex && !col.key && !col.render) return '';
      
      let value = '';
      
      // If there's a render function, try to extract the text value
      if (col.render && typeof col.render === 'function') {
        try {
          const dataIndex = col.dataIndex || col.key;
          const rawValue = dataIndex ? row[dataIndex] : row;
          const rendered = col.render(rawValue, row, rowIndex);
          
          // Extract text from rendered React elements or use raw value
          if (typeof rendered === 'string' || typeof rendered === 'number') {
            value = rendered;
          } else if (rendered && rendered.props && rendered.props.children) {
            // Try to extract text from React element
            const children = rendered.props.children;
            if (typeof children === 'string' || typeof children === 'number') {
              value = children;
            } else if (Array.isArray(children)) {
              value = children.map(c => {
                if (typeof c === 'string' || typeof c === 'number') {
                  return c;
                } else if (c && c.props && c.props.children) {
                  // Handle nested React elements (like div with multiple children)
                  const nestedChildren = c.props.children;
                  if (typeof nestedChildren === 'string' || typeof nestedChildren === 'number') {
                    return nestedChildren;
                  } else if (Array.isArray(nestedChildren)) {
                    return nestedChildren.map(nc => typeof nc === 'string' || typeof nc === 'number' ? nc : '').join(' ');
                  }
                }
                return '';
              }).filter(v => v).join(' | ');
            } else {
              value = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
            }
          } else {
            // Fallback to raw value
            const dataIndex = col.dataIndex || col.key;
            value = dataIndex ? row[dataIndex] : '';
          }
        } catch (e) {
          // If render fails, use raw value
          const dataIndex = col.dataIndex || col.key;
          value = dataIndex ? (row[dataIndex] !== null && row[dataIndex] !== undefined ? row[dataIndex] : '') : '';
        }
      } else {
        // No render function, use raw value
        const dataIndex = col.dataIndex || col.key;
        value = dataIndex ? row[dataIndex] : '';
      }
      
      // Format the value
      if (value === null || value === undefined || value === '') {
        value = '';
      } else if (typeof value === 'number') {
        value = value.toFixed(2);
      } else if (moment.isMoment(value)) {
        value = value.format('YYYY-MM-DD HH:mm:ss');
      } else if (typeof value === 'object') {
        value = JSON.stringify(value);
      } else {
        value = String(value);
      }
      
      // Escape CSV special characters
      if (String(value).includes(',') || String(value).includes('"') || String(value).includes('\n')) {
        value = `"${String(value).replace(/"/g, '""')}"`;
      }
      
      return value;
    });
  });
  
  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename || 'table'}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Timeline Chart Component showing stable periods and consumption intervals
const TimelineChart = ({ stablePeriods, consumptionRates, rawData, width = 1200, height = 500 }) => {
  if (!stablePeriods || stablePeriods.length === 0 || !consumptionRates || consumptionRates.length === 0) {
    return <div>No data to display</div>;
  }

  const margin = { top: 40, right: 100, left: 80, bottom: 100 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Get all timestamps to determine time range
  const allTimestamps = [
    ...stablePeriods.map(p => p.start),
    ...stablePeriods.map(p => p.end),
    ...consumptionRates.map(r => r.consumptionIntervalStart),
    ...consumptionRates.map(r => r.consumptionIntervalEnd),
    ...consumptionRates.filter(r => r.fromPeriod).map(r => r.fromPeriod.startTime),
    ...consumptionRates.filter(r => r.fromPeriod).map(r => r.fromPeriod.endTime),
    ...consumptionRates.filter(r => r.toPeriod).map(r => r.toPeriod.startTime),
    ...consumptionRates.filter(r => r.toPeriod).map(r => r.toPeriod.endTime)
  ];
  const minTime = Math.min(...allTimestamps);
  const maxTime = Math.max(...allTimestamps);
  const timeRange = maxTime - minTime;

  // Scale functions
  const scaleX = (timestamp) => ((timestamp - minTime) / timeRange) * chartWidth;
  const scaleTime = (x) => minTime + (x / chartWidth) * timeRange;

  // Find min/max consumption rates for Y-axis (left side)
  const allRates = consumptionRates.flatMap(r => [
    r.consumptionRateLtrPerHour,
    r.consumptionRateKmsPerLtr
  ].filter(v => v != null));
  const minRate = Math.min(...allRates, 0);
  const maxRate = Math.max(...allRates, 0);
  const rateRange = maxRate - minRate || 1;

  const scaleY = (value) => {
    if (value == null) return null;
    return chartHeight - ((value - minRate) / rateRange) * chartHeight;
  };

  // Find min/max fuel levels for Y-axis (right side)
  const allFuelLevels = rawData ? rawData.map(d => {
    const timestamp = getEpochTimestamp(d.timestamp);
    const fuelLevel = parseFloat(d.fuelLevelLiters) || 0;
    return { timestamp, fuelLevel };
  }).filter(d => d.timestamp >= minTime && d.timestamp <= maxTime) : [];

  const fuelLevels = allFuelLevels.map(d => d.fuelLevel);
  const minFuel = Math.min(...fuelLevels, 0);
  const maxFuel = Math.max(...fuelLevels, 0);
  const fuelRange = maxFuel - minFuel || 1;

  const scaleYFuel = (value) => {
    if (value == null) return null;
    return chartHeight - ((value - minFuel) / fuelRange) * chartHeight;
  };

  // Format time for display
  const formatTime = (timestamp) => {
    return moment.utc(timestamp).add(5, 'hours').add(30, 'minutes').format('HH:mm');
  };

  // Generate time labels for X-axis
  const timeLabels = [];
  const labelCount = 10;
  for (let i = 0; i <= labelCount; i++) {
    const time = minTime + (timeRange * i) / labelCount;
    timeLabels.push({
      time,
      x: scaleX(time),
      label: formatTime(time)
    });
  }

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <svg width={width} height={height} style={{ border: '1px solid #e8e8e8', borderRadius: 4 }}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Grid lines for Y-axis */}
          {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
            const y = chartHeight * (1 - ratio);
            const value = minRate + (maxRate - minRate) * ratio;
            return (
              <g key={ratio}>
                <line x1={0} y1={y} x2={chartWidth} y2={y} stroke="#e8e8e8" strokeWidth={1} />
                <text x={-10} y={y + 4} textAnchor="end" fontSize="10" fill="#666">
                  {value.toFixed(1)}
                </text>
              </g>
            );
          })}

          {/* Grid lines for X-axis (time) */}
          {timeLabels.map((tl, i) => (
            <g key={i}>
              <line x1={tl.x} y1={0} x2={tl.x} y2={chartHeight} stroke="#f0f0f0" strokeWidth={1} strokeDasharray="2,2" />
            </g>
          ))}

          {/* Draw stable periods as background rectangles - white background */}
          {stablePeriods.map((period, i) => {
            const x1 = scaleX(period.start);
            const x2 = scaleX(period.end);
            const width = x2 - x1;
            return (
              <rect
                key={`stable-${i}`}
                x={x1}
                y={0}
                width={width}
                height={chartHeight}
                fill="#ffffff"
                stroke="#d9d9d9"
                strokeWidth={1}
                opacity={1}
              />
            );
          })}

          {/* Draw consumption intervals as colored rectangles - orange for consumption, green for refueling */}
          {consumptionRates.map((rate, i) => {
            const x1 = scaleX(rate.consumptionIntervalStart);
            const x2 = scaleX(rate.consumptionIntervalEnd);
            const width = x2 - x1;
            // Use green for refueling events, orange for consumption
            const fillColor = rate.isRefueling ? '#95de64' : '#ffa940';
            const strokeColor = rate.isRefueling ? '#52c41a' : '#fa8c16';
            return (
              <rect
                key={`consumption-${i}`}
                x={x1}
                y={0}
                width={width}
                height={chartHeight}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={1}
                opacity={0.4}
              />
            );
          })}

          {/* Plot fuel level line */}
          {allFuelLevels.length > 0 && (() => {
            // Sort by timestamp
            const sortedFuelData = [...allFuelLevels].sort((a, b) => a.timestamp - b.timestamp);
            
            // Create path for fuel level line
            const fuelPath = sortedFuelData
              .map((d, i) => {
                const x = scaleX(d.timestamp);
                const y = scaleYFuel(d.fuelLevel);
                return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
              })
              .join(' ');

            return (
              <g>
                <path
                  d={fuelPath}
                  fill="none"
                  stroke="#722ed1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.8}
                />
                {/* Fuel level points (sparse to avoid clutter) */}
                {sortedFuelData.filter((_, i) => i % Math.max(1, Math.floor(sortedFuelData.length / 50)) === 0).map((d, i) => {
                  const x = scaleX(d.timestamp);
                  const y = scaleYFuel(d.fuelLevel);
                  return (
                    <circle
                      key={`fuel-point-${i}`}
                      cx={x}
                      cy={y}
                      r={2}
                      fill="#722ed1"
                      opacity={0.6}
                    />
                  );
                })}
              </g>
            );
          })()}

          {/* Plot consumption rates as points ONLY in consumption intervals (skip refueling events) */}
          {consumptionRates.map((rate, i) => {
            // Skip refueling events - they don't have consumption rates
            if (rate.isRefueling) {
              const x1 = scaleX(rate.consumptionIntervalStart);
              const x2 = scaleX(rate.consumptionIntervalEnd);
              const centerX = (x1 + x2) / 2;
              // Show refueling label instead
              return (
                <g key={`refuel-${i}`}>
                  <text
                    x={centerX}
                    y={chartHeight / 2}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#52c41a"
                    fontWeight="bold"
                  >
                    Refuel +{rate.fuelAdded.toFixed(1)}L
                  </text>
                </g>
              );
            }

            const x1 = scaleX(rate.consumptionIntervalStart);
            const x2 = scaleX(rate.consumptionIntervalEnd);
            const centerX = (x1 + x2) / 2;
            const y1 = scaleY(rate.consumptionRateLtrPerHour);
            const y2 = scaleY(rate.consumptionRateKmsPerLtr);

            return (
              <g key={`rate-${i}`}>
                {/* Ltr/Hr point */}
                {y1 != null && (
                  <>
                    <circle cx={centerX} cy={y1} r={5} fill="#1890ff" stroke="#fff" strokeWidth={2} />
                    <text
                      x={centerX}
                      y={y1 - 10}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#1890ff"
                      fontWeight="bold"
                    >
                      {rate.consumptionRateLtrPerHour.toFixed(1)}
                    </text>
                  </>
                )}
                {/* Kms/Ltr point */}
                {y2 != null && (
                  <>
                    <circle cx={centerX} cy={y2} r={5} fill="#52c41a" stroke="#fff" strokeWidth={2} />
                    <text
                      x={centerX}
                      y={y2 + 15}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#52c41a"
                      fontWeight="bold"
                    >
                      {rate.consumptionRateKmsPerLtr.toFixed(1)}
                    </text>
                  </>
                )}
              </g>
            );
          })}

          {/* X-axis time labels */}
          {timeLabels.map((tl, i) => (
            <g key={i}>
              <line x1={tl.x} y1={chartHeight} x2={tl.x} y2={chartHeight + 5} stroke="#333" strokeWidth={1} />
              <text
                x={tl.x}
                y={chartHeight + 20}
                textAnchor="middle"
                fontSize="10"
                fill="#666"
              >
                {tl.label}
              </text>
            </g>
          ))}

          {/* Y-axis label (left - Consumption Rate) */}
          <text
            x={-chartHeight / 2}
            y={-50}
            textAnchor="middle"
            fontSize="12"
            fill="#333"
            transform="rotate(-90)"
          >
            Consumption Rate (Ltr/Hr & Kms/Ltr)
          </text>

          {/* Y-axis label (right - Fuel Level) */}
          <text
            x={chartHeight / 2}
            y={chartWidth + 50}
            textAnchor="middle"
            fontSize="12"
            fill="#722ed1"
            transform="rotate(90)"
          >
            Fuel Level (Liters)
          </text>

          {/* Right Y-axis grid and labels for fuel level */}
          {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
            const y = chartHeight * (1 - ratio);
            const value = minFuel + (maxFuel - minFuel) * ratio;
            return (
              <g key={`fuel-${ratio}`}>
                <line 
                  x1={chartWidth} 
                  y1={y} 
                  x2={chartWidth + 5} 
                  y2={y} 
                  stroke="#722ed1" 
                  strokeWidth={1} 
                />
                <text 
                  x={chartWidth + 10} 
                  y={y + 4} 
                  textAnchor="start" 
                  fontSize="10" 
                  fill="#722ed1"
                >
                  {value.toFixed(1)}
                </text>
              </g>
            );
          })}

          {/* X-axis label */}
          <text
            x={chartWidth / 2}
            y={chartHeight + 50}
            textAnchor="middle"
            fontSize="12"
            fill="#333"
          >
            Timeline (IST)
          </text>

          {/* Legend */}
          <g transform={`translate(${chartWidth - 120}, 20)`}>
            <rect x={0} y={0} width={15} height={15} fill="#ffffff" stroke="#d9d9d9" opacity={1} />
            <text x={20} y={12} fontSize="11" fill="#333">Stable Period</text>
            
            <rect x={0} y={20} width={15} height={15} fill="#ffa940" stroke="#fa8c16" opacity={0.4} />
            <text x={20} y={32} fontSize="11" fill="#333">Consumption</text>
            
            <rect x={0} y={40} width={15} height={15} fill="#95de64" stroke="#52c41a" opacity={0.4} />
            <text x={20} y={52} fontSize="11" fill="#333">Refueling</text>
            
            <circle cx={7} cy={65} r={4} fill="#1890ff" />
            <text x={20} y={69} fontSize="11" fill="#333">Ltr/Hr</text>
            
            <circle cx={7} cy={80} r={4} fill="#52c41a" />
            <text x={20} y={84} fontSize="11" fill="#333">Kms/Ltr</text>
            
            <line x1={0} y1={95} x2={15} y2={95} stroke="#722ed1" strokeWidth="2" />
            <text x={20} y={99} fontSize="11" fill="#333">Fuel Level</text>
          </g>
        </g>
      </svg>
    </div>
  );
};

// Simple SVG Bar Chart Component
const SimpleBarChart = ({ data, width = 800, height = 300, yLabel, getColor }) => {
  if (!data || data.length === 0) return <div>No data to display</div>;

  const margin = { top: 20, right: 40, left: 60, bottom: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const maxValue = Math.max(...data.map(d => d.value), 0);
  const barWidth = chartWidth / data.length * 0.6;
  const barSpacing = chartWidth / data.length;

  const scaleY = (value) => (value / maxValue) * chartHeight;

  return (
    <svg width={width} height={height} style={{ border: '1px solid #e8e8e8', borderRadius: 4 }}>
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
          const y = chartHeight * (1 - ratio);
          const value = maxValue * ratio;
          return (
            <g key={ratio}>
              <line x1={0} y1={y} x2={chartWidth} y2={y} stroke="#e8e8e8" strokeWidth={1} />
              <text x={-10} y={y + 4} textAnchor="end" fontSize="10" fill="#666">
                {value.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const x = i * barSpacing + (barSpacing - barWidth) / 2;
          const barHeight = scaleY(d.value);
          const y = chartHeight - barHeight;
          const color = getColor ? getColor(d, i) : '#1890ff';

          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={color}
                stroke={color}
                strokeWidth={1}
              />
              {/* Value label on bar */}
              {barHeight > 20 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 5}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#333"
                >
                  {d.value.toFixed(2)}
                </text>
              )}
            </g>
          );
        })}

        {/* X-axis labels */}
        {data.map((d, i) => {
          const x = i * barSpacing + barSpacing / 2;
          return (
            <text
              key={i}
              x={x}
              y={chartHeight + 20}
              textAnchor="middle"
              fontSize="10"
              fill="#666"
            >
              {d.label}
            </text>
          );
        })}

        {/* Y-axis label */}
        <text
          x={-chartHeight / 2}
          y={-40}
          textAnchor="middle"
          fontSize="12"
          fill="#333"
          transform="rotate(-90)"
        >
          {yLabel}
        </text>
      </g>
    </svg>
  );
};

const { Option } = Select;

// Helper to convert timestamp to epoch
const getEpochTimestamp = (timestamp) => {
  if (typeof timestamp === 'string') {
    const epoch = parseInt(timestamp);
    if (!isNaN(epoch) && epoch > 1000000000000) {
      return epoch;
    }
    return moment.utc(timestamp).valueOf();
  }
  return timestamp;
};

// -----------------------------
// Tilt Calculation Functions
// -----------------------------

// Default axis column name candidates
const DEFAULT_AXIS_CANDIDATES = {
  ax: ["ax", "acc_x", "x", "x_axis", "accel_x", "AccelX", "acceleration_x", "accelerationX"],
  ay: ["ay", "acc_y", "y", "y_axis", "accel_y", "AccelY", "acceleration_y", "accelerationY"],
  az: ["az", "acc_z", "z", "z_axis", "accel_z", "AccelZ", "acceleration_z", "accelerationZ"],
};

// Pick the first matching column name from candidates
const pickColumn = (row, candidates, name) => {
  const keys = Object.keys(row);
  for (const c of candidates) {
    if (keys.includes(c)) {
      return c;
    }
  }
  // Try case-insensitive match
  for (const c of candidates) {
    const found = keys.find(k => k.toLowerCase() === c.toLowerCase());
    if (found) {
      return found;
    }
  }
  return null;
};

// Get accelerometer data as array of [ax, ay, az] values
const getAccelerometerData = (dataPoints, axCol, ayCol, azCol) => {
  const xyz = [];
  for (const point of dataPoints) {
    const row = point.originalRow || point;
    const ax = parseFloat(row[axCol]);
    const ay = parseFloat(row[ayCol]);
    const az = parseFloat(row[azCol]);
    
    if (!isNaN(ax) && !isNaN(ay) && !isNaN(az) && 
        isFinite(ax) && isFinite(ay) && isFinite(az)) {
      xyz.push([ax, ay, az]);
    }
  }
  return xyz;
};

// Normalize vector to unit length
const normalizeVector = (v) => {
  const norm = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (norm === 0 || !isFinite(norm)) {
    throw new Error("Zero/invalid norm while normalizing vector.");
  }
  return [v[0] / norm, v[1] / norm, v[2] / norm];
};

// Clamp value between lo and hi
const clamp = (x, lo, hi) => {
  return Math.max(lo, Math.min(hi, x));
};

// Calculate median of array
const median = (arr) => {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

// Estimate tilt from a stable period interval
const estimateTiltFromInterval = (dataPoints, robust = true) => {
  if (!dataPoints || dataPoints.length === 0) {
    throw new Error("No data points provided");
  }

  // Get first row to find column names
  const firstRow = dataPoints[0].originalRow || dataPoints[0];
  const axCol = pickColumn(firstRow, DEFAULT_AXIS_CANDIDATES.ax, "ax");
  const ayCol = pickColumn(firstRow, DEFAULT_AXIS_CANDIDATES.ay, "ay");
  const azCol = pickColumn(firstRow, DEFAULT_AXIS_CANDIDATES.az, "az");

  if (!axCol || !ayCol || !azCol) {
    // Return null if accelerometer data is not available
    return null;
  }

  // Get accelerometer data
  const xyz = getAccelerometerData(dataPoints, axCol, ayCol, azCol);
  
  if (xyz.length < 3) {
    throw new Error("Not enough samples in interval after dropping NaNs.");
  }

  // Calculate magnitude stats
  const norms = xyz.map(v => Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]));
  const meanNorm = norms.reduce((a, b) => a + b, 0) / norms.length;
  const variance = norms.reduce((sum, n) => sum + Math.pow(n - meanNorm, 2), 0) / norms.length;
  const normStd = Math.sqrt(variance);

  // Representative gravity vector
  let gVec;
  if (robust) {
    // Use median for each axis
    const axValues = xyz.map(v => v[0]);
    const ayValues = xyz.map(v => v[1]);
    const azValues = xyz.map(v => v[2]);
    gVec = [median(axValues), median(ayValues), median(azValues)];
  } else {
    // Use mean for each axis
    gVec = [
      xyz.reduce((sum, v) => sum + v[0], 0) / xyz.length,
      xyz.reduce((sum, v) => sum + v[1], 0) / xyz.length,
      xyz.reduce((sum, v) => sum + v[2], 0) / xyz.length
    ];
  }

  // Normalize to unit vector
  const vUnit = normalizeVector(gVec);

  // Convert to roll/pitch (in degrees)
  const ax = vUnit[0];
  const ay = vUnit[1];
  const az = vUnit[2];
  const roll = Math.atan2(ay, az) * (180 / Math.PI);
  const pitch = Math.atan2(-ax, Math.sqrt(ay * ay + az * az)) * (180 / Math.PI);

  return {
    vUnit,
    rollDeg: roll,
    pitchDeg: pitch,
    meanNorm,
    normStd,
    samplesUsed: xyz.length
  };
};

// Calculate angle change between two unit gravity vectors (in degrees)
const angleChangeDeg = (v1Unit, v2Unit) => {
  const dot = v1Unit[0] * v2Unit[0] + v1Unit[1] * v2Unit[1] + v1Unit[2] * v2Unit[2];
  const clampedDot = clamp(dot, -1.0, 1.0);
  return Math.acos(clampedDot) * (180 / Math.PI);
};

// Compare two stable intervals and return tilt change
const compareTwoStableIntervals = (period1, period2) => {
  try {
    const t1 = estimateTiltFromInterval(period1.dataPoints || [], true);
    const t2 = estimateTiltFromInterval(period2.dataPoints || [], true);

    if (!t1 || !t2) {
      return null;
    }

    const totalDelta = angleChangeDeg(t1.vUnit, t2.vUnit);

    // Normalize roll/pitch deltas to [-180, 180]
    const wrap180 = (x) => {
      x = ((x + 180.0) % 360.0) - 180.0;
      return x;
    };

    return {
      totalTiltChangeDeg: totalDelta,
      roll1Deg: t1.rollDeg,
      pitch1Deg: t1.pitchDeg,
      roll2Deg: t2.rollDeg,
      pitch2Deg: t2.pitchDeg,
      deltaRollDeg: wrap180(t2.rollDeg - t1.rollDeg),
      deltaPitchDeg: wrap180(t2.pitchDeg - t1.pitchDeg),
      interval1MeanNorm: t1.meanNorm,
      interval1NormStd: t1.normStd,
      interval1SamplesUsed: t1.samplesUsed,
      interval2MeanNorm: t2.meanNorm,
      interval2NormStd: t2.normStd,
      interval2SamplesUsed: t2.samplesUsed,
    };
  } catch (error) {
    console.error("Error comparing stable intervals:", error);
    return null;
  }
};

// Helper to format timestamp in IST
const formatIST = (utcEpoch) => {
  return moment.utc(utcEpoch).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD HH:mm:ss');
};

const safeMedian = (values) => {
  const valid = values.filter(v => v != null && !isNaN(v)).sort((a, b) => a - b);
  if (valid.length === 0) return null;
  const mid = Math.floor(valid.length / 2);
  return valid.length % 2 === 0 ? (valid[mid - 1] + valid[mid]) / 2 : valid[mid];
};

const safePercentile = (values, percentile) => {
  const valid = values.filter(v => v != null && !isNaN(v)).sort((a, b) => a - b);
  if (valid.length === 0) return null;
  const index = (percentile / 100) * (valid.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return valid[lower];
  return valid[lower] * (upper - index) + valid[upper] * (index - lower);
};

const safeWeightedMedian = (values, weights) => {
  if (!Array.isArray(values) || !Array.isArray(weights) || values.length !== weights.length || values.length === 0) {
    return null;
  }

  const pairs = values
    .map((value, i) => ({ value, weight: weights[i] }))
    .filter(p => p.value != null && !isNaN(p.value) && p.weight != null && !isNaN(p.weight) && p.weight > 0)
    .sort((a, b) => a.value - b.value);

  if (pairs.length === 0) return null;

  const totalWeight = pairs.reduce((sum, p) => sum + p.weight, 0);
  if (totalWeight <= 0) return null;

  let cumulative = 0;
  for (let i = 0; i < pairs.length; i++) {
    cumulative += pairs[i].weight;
    if (cumulative >= totalWeight / 2) {
      return pairs[i].value;
    }
  }

  return pairs[pairs.length - 1].value;
};

const deriveStableBaselineFromPoints = (points, mode = 'general') => {
  const stableFlag = mode === 'active' ? 'stableActive' : 'stableGeneral';
  const usedFlag = mode === 'active' ? 'usedForActiveBaseline' : 'usedForGeneralBaseline';

  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    point[stableFlag] = false;

    if (!point.ignitionOn || point.excludedByEvent) continue;
    if (mode === 'active' && point.idleOn) continue;

    const localWindow = points
      .slice(Math.max(0, i - 6), Math.min(points.length, i + 7))
      .filter(p => p.ignitionOn && !p.excludedByEvent && (mode === 'active' ? !p.idleOn : true))
      .map(p => p.rateLph)
      .filter(v => v != null && !isNaN(v));

    if (localWindow.length < 5) continue;

    const localMedian = safeMedian(localWindow);
    const localP90 = safePercentile(localWindow, 90);
    const localP10 = safePercentile(localWindow, 10);
    if (localMedian == null || localP90 == null || localP10 == null) continue;

    const localSpread = localP90 - localP10;
    const prevRate = i > 0 ? points[i - 1].rateLph : null;
    const abruptJump = prevRate != null && !isNaN(prevRate)
      ? Math.abs(point.rateLph - prevRate) > Math.max(4, localMedian * 0.4)
      : false;

    point[stableFlag] = (
      Math.abs(point.rateLph - localMedian) <= Math.max(2, localMedian * 0.3) &&
      localSpread <= Math.max(4, localMedian * 0.5) &&
      !abruptJump
    );
  }

  const stablePoints = points.filter(p => p[stableFlag]);
  const windows = [];
  let currentWindow = null;
  const maxGapMs = 2 * 60 * 1000;

  for (let i = 0; i < stablePoints.length; i++) {
    const point = stablePoints[i];
    if (!currentWindow) {
      currentWindow = { startTs: point.ts, endTs: point.ts, rates: [point.rateLph], points: [point] };
      continue;
    }

    if (point.ts - currentWindow.endTs <= maxGapMs) {
      currentWindow.endTs = point.ts;
      currentWindow.rates.push(point.rateLph);
      currentWindow.points.push(point);
    } else {
      windows.push(currentWindow);
      currentWindow = { startTs: point.ts, endTs: point.ts, rates: [point.rateLph], points: [point] };
    }
  }
  if (currentWindow) windows.push(currentWindow);

  const qualifyingWindows = windows
    .map((window, idx) => {
      const p90 = safePercentile(window.rates, 90);
      const p10 = safePercentile(window.rates, 10);
      const median = safeMedian(window.rates);
      const durationMinutes = (window.endTs - window.startTs) / 60000;
      return {
        id: idx,
        startTs: window.startTs,
        endTs: window.endTs,
        points: window.points,
        medianRateLph: median,
        p10RateLph: p10,
        p90RateLph: p90,
        spreadRateLph: p90 != null && p10 != null ? p90 - p10 : null,
        durationMinutes,
        pointCount: window.points.length
      };
    })
    .filter(window =>
      window.medianRateLph != null &&
      window.pointCount >= 8 &&
      window.durationMinutes >= 8 &&
      (window.spreadRateLph == null || window.spreadRateLph <= Math.max(4, window.medianRateLph * 0.45))
    );

  const windowMedians = qualifyingWindows.map(w => w.medianRateLph);
  const windowP20 = safePercentile(windowMedians, 20);
  const windowP70 = safePercentile(windowMedians, 70);

  let selectedWindows = qualifyingWindows.filter(w =>
    w.medianRateLph >= (windowP20 ?? -Infinity) &&
    w.medianRateLph <= (windowP70 ?? Infinity)
  );
  if (selectedWindows.length < 2) {
    selectedWindows = qualifyingWindows.filter(w => w.medianRateLph <= (windowP70 ?? Infinity));
  }
  if (selectedWindows.length === 0) {
    selectedWindows = qualifyingWindows;
  }

  const selectedRates = selectedWindows.map(w => w.medianRateLph);
  const selectedWeights = selectedWindows.map(w => Math.max(w.durationMinutes, w.pointCount));
  const rateLph = safeWeightedMedian(selectedRates, selectedWeights);

  points.forEach(point => {
    point[usedFlag] = selectedWindows.some(window => window.points.includes(point));
  });

  const selectedPoints = points.filter(p => p[usedFlag]);
  const rangeStart = selectedPoints.length > 0 ? selectedPoints[0].ts : null;
  const rangeEnd = selectedPoints.length > 0 ? selectedPoints[selectedPoints.length - 1].ts : null;

  return {
    rateLph,
    pointCount: selectedPoints.length,
    rangeStart,
    rangeEnd,
    windows: selectedWindows.map(window => ({
      startTs: window.startTs,
      endTs: window.endTs,
      medianRateLph: window.medianRateLph,
      p10RateLph: window.p10RateLph,
      p90RateLph: window.p90RateLph,
      spreadRateLph: window.spreadRateLph,
      pointCount: window.pointCount,
      durationMinutes: window.durationMinutes
    })),
    method: rateLph != null
      ? (mode === 'active' ? 'active_stable_window_weighted_median' : 'general_stable_window_weighted_median')
      : 'insufficient_stable_windows'
  };
};

const buildConsumptionRateSeries = (debugSeries, events = []) => {
  if (!debugSeries || !debugSeries.timestamps || !debugSeries.fuel_smooth) {
    return {
      rates: [],
      baselineRateLph: null,
      generalBaselineRateLph: null,
      activeBaselineRateLph: null,
      suddenChangeThresholdLph: null,
      ratePoints: [],
      baselinePointCount: 0,
      baselineRangeStart: null,
      baselineRangeEnd: null,
      baselineWindows: [],
      baselineMethod: 'insufficient_stable_windows',
      generalBaseline: { rateLph: null, pointCount: 0, rangeStart: null, rangeEnd: null, windows: [], method: 'insufficient_stable_windows' },
      activeBaseline: { rateLph: null, pointCount: 0, rangeStart: null, rangeEnd: null, windows: [], method: 'insufficient_stable_windows' }
    };
  }

  const rates = new Array(debugSeries.timestamps.length).fill(null);
  for (let i = 1; i < debugSeries.timestamps.length; i++) {
    const prevFuel = debugSeries.fuel_smooth[i - 1];
    const currFuel = debugSeries.fuel_smooth[i];
    const prevTs = debugSeries.timestamps[i - 1];
    const currTs = debugSeries.timestamps[i];

    if (
      prevFuel == null || currFuel == null || isNaN(prevFuel) || isNaN(currFuel) ||
      prevTs == null || currTs == null || isNaN(prevTs) || isNaN(currTs)
    ) {
      continue;
    }

    const deltaHours = (currTs - prevTs) / (1000 * 60 * 60);
    if (deltaHours <= 0) continue;

    const fuelDrop = prevFuel - currFuel; // +ve means fuel consumed/lost
    if (fuelDrop <= 0) continue; // ignore refuel/recovery steps for consumption rate

    rates[i] = fuelDrop / deltaHours;
  }

  const ignition = debugSeries.ignition || [];
  const idle = debugSeries.idle || [];

  // Exclude samples only around the true drop window (plus small buffer) to avoid leakage contamination
  // while keeping nearby normal operation windows available for baseline.
  const exclusionRanges = (events || []).map(event => {
    const dropStart = new Date(event?.start_time_detected || event?.start_time_formatted || 0).getTime();
    const dropEnd = new Date(event?.end_time_detected || event?.end_time_formatted || 0).getTime();
    const center = event?.t_detect || new Date(event?.t_detect_formatted || 0).getTime();
    if (!isNaN(dropStart) && !isNaN(dropEnd) && dropEnd >= dropStart) {
      return {
        start: dropStart - (10 * 60 * 1000),
        end: dropEnd + (10 * 60 * 1000)
      };
    }
    return {
      start: center - (15 * 60 * 1000),
      end: center + (15 * 60 * 1000)
    };
  }).filter(r => !isNaN(r.start) && !isNaN(r.end));

  const isExcludedByEvent = (ts) =>
    exclusionRanges.some(r => ts >= r.start && ts <= r.end);

  // Build minute-level buckets to avoid inflated sample-to-sample rates from short packet gaps.
  const minuteMap = new Map();
  for (let i = 0; i < debugSeries.timestamps.length; i++) {
    const ts = debugSeries.timestamps[i];
    const fuel = debugSeries.fuel_smooth[i];
    if (ts == null || fuel == null || isNaN(ts) || isNaN(fuel)) continue;
    const minuteTs = Math.floor(ts / 60000) * 60000;
    if (!minuteMap.has(minuteTs)) {
      minuteMap.set(minuteTs, { fuels: [], ignition: [], idle: [] });
    }
    const bucket = minuteMap.get(minuteTs);
    bucket.fuels.push(fuel);
    if (ignition[i] != null) bucket.ignition.push(ignition[i]);
    if (idle[i] != null) bucket.idle.push(idle[i]);
  }

  const minuteSeries = Array.from(minuteMap.entries())
    .map(([minuteTs, bucket]) => ({
      minuteTs,
      fuel: safeMedian(bucket.fuels),
      ignitionOn: (safeMedian(bucket.ignition) ?? 0) >= 0.5,
      idleOn: (safeMedian(bucket.idle) ?? 0) >= 0.5
    }))
    .filter(d => d.fuel != null)
    .sort((a, b) => a.minuteTs - b.minuteTs);

  const minuteRatePoints = [];
  for (let i = 1; i < minuteSeries.length; i++) {
    const prev = minuteSeries[i - 1];
    const curr = minuteSeries[i];
    const deltaHours = (curr.minuteTs - prev.minuteTs) / (1000 * 60 * 60);
    if (deltaHours <= 0) continue;
    const fuelDrop = prev.fuel - curr.fuel;
    if (fuelDrop <= 0) continue;

    minuteRatePoints.push({
      ts: curr.minuteTs,
      rateLph: fuelDrop / deltaHours,
      ignitionOn: curr.ignitionOn,
      idleOn: curr.idleOn,
      excludedByEvent: isExcludedByEvent(curr.minuteTs),
      stableGeneral: false,
      stableActive: false,
      usedForGeneralBaseline: false,
      usedForActiveBaseline: false
    });
  }

  const generalBaseline = deriveStableBaselineFromPoints(minuteRatePoints, 'general');
  const activeBaseline = deriveStableBaselineFromPoints(minuteRatePoints, 'active');
  const baselineRateLph = generalBaseline.rateLph;
  const baselinePoints = minuteRatePoints.filter(p => p.usedForGeneralBaseline);
  const suddenBaseline = activeBaseline.rateLph != null ? activeBaseline.rateLph : generalBaseline.rateLph;
  const suddenChangeThresholdLph = suddenBaseline != null
    ? Math.max(suddenBaseline * 2, suddenBaseline + 5)
    : null;

  return {
    rates,
    baselineRateLph,
    generalBaselineRateLph: generalBaseline.rateLph,
    activeBaselineRateLph: activeBaseline.rateLph,
    suddenChangeThresholdLph,
    ratePoints: minuteRatePoints,
    baselinePointCount: generalBaseline.pointCount,
    baselineRangeStart: generalBaseline.rangeStart,
    baselineRangeEnd: generalBaseline.rangeEnd,
    baselineWindows: generalBaseline.windows,
    baselineMethod: generalBaseline.method,
    generalBaseline,
    activeBaseline
  };
};

const buildPossibleDrainMetrics = (results) => {
  if (!results || !results.events) {
    return { eventMetrics: {}, baselineRateLph: null, generalBaselineRateLph: null, activeBaselineRateLph: null };
  }

  const series = buildConsumptionRateSeries(results.debug_series, results.events || []);
  const generalBaselineRateLph = series.generalBaselineRateLph;
  const activeBaselineRateLph = series.activeBaselineRateLph;
  const baselineRateLph = generalBaselineRateLph;
  const eventMetrics = {};
  const debugTimestamps = results.debug_series?.timestamps || [];
  const debugIgnition = results.debug_series?.ignition || [];
  const debugIdle = results.debug_series?.idle || [];

  results.events.forEach(event => {
    const startTime = new Date(event.start_time_detected).getTime();
    const endTime = new Date(event.end_time_detected).getTime();
    const durationHours = endTime > startTime ? (endTime - startTime) / (1000 * 60 * 60) : 0;
    let ignitionOnHours = null;
    let ignitionOnFraction = null;
    let ignitionOnIdleHours = null;
    let ignitionOnActiveHours = null;
    let idleInIgnitionOnFraction = null;
    let activeInIgnitionOnFraction = null;

    if (
      debugTimestamps.length > 1 &&
      debugIgnition.length === debugTimestamps.length &&
      debugIdle.length === debugTimestamps.length &&
      endTime > startTime
    ) {
      let onMs = 0;
      let onIdleMs = 0;
      let onActiveMs = 0;
      let totalMs = 0;
      for (let i = 1; i < debugTimestamps.length; i++) {
        const segStart = Math.max(startTime, debugTimestamps[i - 1]);
        const segEnd = Math.min(endTime, debugTimestamps[i]);
        if (segEnd <= segStart) continue;

        const segMs = segEnd - segStart;
        totalMs += segMs;
        if (debugIgnition[i - 1] === 1) {
          onMs += segMs;
          if (debugIdle[i - 1] === 1) onIdleMs += segMs;
          else onActiveMs += segMs;
        }
      }

      if (totalMs > 0) {
        ignitionOnHours = onMs / (1000 * 60 * 60);
        ignitionOnFraction = onMs / totalMs;
        ignitionOnIdleHours = onIdleMs / (1000 * 60 * 60);
        ignitionOnActiveHours = onActiveMs / (1000 * 60 * 60);
        if (onMs > 0) {
          idleInIgnitionOnFraction = onIdleMs / onMs;
          activeInIgnitionOnFraction = onActiveMs / onMs;
        } else {
          idleInIgnitionOnFraction = 0;
          activeInIgnitionOnFraction = 0;
        }
      } else {
        ignitionOnHours = 0;
        ignitionOnFraction = 0;
        ignitionOnIdleHours = 0;
        ignitionOnActiveHours = 0;
        idleInIgnitionOnFraction = 0;
        activeInIgnitionOnFraction = 0;
      }
    }

    const expectedHours = ignitionOnHours != null ? ignitionOnHours : durationHours;
    const fallbackRate = generalBaselineRateLph ?? activeBaselineRateLph;
    const idleRate = generalBaselineRateLph ?? activeBaselineRateLph;
    const activeRate = activeBaselineRateLph ?? generalBaselineRateLph;
    const idleHoursForCalc = ignitionOnIdleHours != null ? ignitionOnIdleHours : 0;
    const activeHoursForCalc = ignitionOnActiveHours != null ? ignitionOnActiveHours : expectedHours;

    const expectedLoss = (idleRate != null && activeRate != null)
      ? (idleRate * idleHoursForCalc) + (activeRate * activeHoursForCalc)
      : (fallbackRate != null ? fallbackRate * expectedHours : null);
    const possibleDrain = expectedLoss != null ? Math.max(event.loss_liters - expectedLoss, 0) : null;

    eventMetrics[event.event_id] = {
      durationHours,
      ignitionOnHours,
      ignitionOnFraction,
      ignitionOnIdleHours,
      ignitionOnActiveHours,
      idleInIgnitionOnFraction,
      activeInIgnitionOnFraction,
      generalBaselineRateLph,
      activeBaselineRateLph,
      expectedLoss,
      possibleDrain
    };
  });

  return {
    eventMetrics,
    baselineRateLph,
    generalBaselineRateLph,
    activeBaselineRateLph,
    baselineSeries: {
      ratePoints: series.ratePoints || [],
      generalBaseline: series.generalBaseline || null,
      activeBaseline: series.activeBaseline || null,
      baselineMethod: {
        general: series.generalBaseline?.method || null,
        active: series.activeBaseline?.method || null
      }
    }
  };
};

const BaselineConsumptionTrendChart = ({
  baselineSeries,
  generalBaselineRateLph,
  activeBaselineRateLph,
  dropStartTs,
  dropEndTs,
  width = 900,
  height = 340
}) => {
  const ratePoints = baselineSeries?.ratePoints || [];
  const validPoints = ratePoints.filter(p => p && p.ts != null && p.rateLph != null && !isNaN(p.ts) && !isNaN(p.rateLph));

  if (validPoints.length === 0) {
    return <Alert type="warning" message="No consumption trend points available for baseline calculation." showIcon />;
  }

  const margin = { top: 20, right: 20, left: 60, bottom: 50 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const allTimes = validPoints.map(p => p.ts);
  if (dropStartTs != null && !isNaN(dropStartTs)) allTimes.push(dropStartTs);
  if (dropEndTs != null && !isNaN(dropEndTs)) allTimes.push(dropEndTs);
  const minTime = Math.min(...allTimes);
  const maxTime = Math.max(...allTimes);
  const timeRange = Math.max(1, maxTime - minTime);
  const scaleX = (ts) => ((ts - minTime) / timeRange) * chartWidth;

  const rates = validPoints.map(p => p.rateLph);
  const p95 = safePercentile(rates, 95);
  const maxRate = Math.max(5, p95 != null ? p95 * 1.2 : Math.max(...rates));
  const scaleY = (rate) => chartHeight - (rate / maxRate) * chartHeight;

  const formatTime = (ts) => moment(ts).format('HH:mm');
  const timeLabels = [];
  for (let i = 0; i <= 8; i++) {
    const time = minTime + (timeRange * i) / 8;
    timeLabels.push({ time, x: scaleX(time), label: formatTime(time) });
  }

  const generalWindows = baselineSeries?.generalBaseline?.windows || [];
  const activeWindows = baselineSeries?.activeBaseline?.windows || [];

  return (
    <div>
      <svg width={width} height={height} style={{ border: '1px solid #e8e8e8', borderRadius: 4, background: '#fff' }}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
            const y = chartHeight * (1 - ratio);
            const value = maxRate * ratio;
            return (
              <g key={`grid-${ratio}`}>
                <line x1={0} y1={y} x2={chartWidth} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                <text x={-8} y={y + 4} textAnchor="end" fontSize="10" fill="#666">
                  {value.toFixed(1)}
                </text>
              </g>
            );
          })}

          {timeLabels.map((label, i) => (
            <g key={`time-${i}`}>
              <line x1={label.x} y1={0} x2={label.x} y2={chartHeight} stroke="#f5f5f5" strokeWidth={1} />
              <text x={label.x} y={chartHeight + 14} textAnchor="middle" fontSize="9" fill="#666">
                {label.label}
              </text>
            </g>
          ))}

          {generalWindows.map((window, i) => (
            <rect
              key={`general-baseline-window-${i}`}
              x={scaleX(window.startTs)}
              y={0}
              width={Math.max(1, scaleX(window.endTs) - scaleX(window.startTs))}
              height={chartHeight}
              fill="#52c41a"
              opacity={0.1}
            />
          ))}

          {activeWindows.map((window, i) => (
            <rect
              key={`active-baseline-window-${i}`}
              x={scaleX(window.startTs)}
              y={0}
              width={Math.max(1, scaleX(window.endTs) - scaleX(window.startTs))}
              height={chartHeight}
              fill="#1890ff"
              opacity={0.08}
            />
          ))}

          {dropStartTs != null && dropEndTs != null && !isNaN(dropStartTs) && !isNaN(dropEndTs) && (
            <rect
              x={scaleX(dropStartTs)}
              y={0}
              width={Math.max(1, scaleX(dropEndTs) - scaleX(dropStartTs))}
              height={chartHeight}
              fill="#ff4d4f"
              opacity={0.12}
            />
          )}

          {generalBaselineRateLph != null && !isNaN(generalBaselineRateLph) && (
            <line
              x1={0}
              y1={scaleY(generalBaselineRateLph)}
              x2={chartWidth}
              y2={scaleY(generalBaselineRateLph)}
              stroke="#52c41a"
              strokeWidth={1.5}
              strokeDasharray="4,3"
            />
          )}
          {activeBaselineRateLph != null && !isNaN(activeBaselineRateLph) && (
            <line
              x1={0}
              y1={scaleY(activeBaselineRateLph)}
              x2={chartWidth}
              y2={scaleY(activeBaselineRateLph)}
              stroke="#1890ff"
              strokeWidth={1.5}
              strokeDasharray="4,3"
            />
          )}

          {validPoints.map((point, i) => {
            let fill = '#bfbfbf';
            let radius = 2;
            if (point.stableGeneral) fill = '#8cc8ff';
            if (point.stableActive) fill = '#40a9ff';
            if (point.usedForGeneralBaseline && point.usedForActiveBaseline) {
              fill = '#722ed1';
              radius = 2.8;
            } else if (point.usedForGeneralBaseline) {
              fill = '#52c41a';
              radius = 2.6;
            } else if (point.usedForActiveBaseline) {
              fill = '#1890ff';
              radius = 2.6;
            }
            return (
              <circle
                key={`rate-point-${i}`}
                cx={scaleX(point.ts)}
                cy={scaleY(point.rateLph)}
                r={radius}
                fill={fill}
                opacity={0.85}
              />
            );
          })}

          <text x={-40} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, -40, ${chartHeight / 2})`}>
            Consumption (L/h)
          </text>
          <text x={chartWidth / 2} y={chartHeight + 34} textAnchor="middle" fontSize="11" fill="#666">
            Time
          </text>
        </g>
      </svg>

      <Row gutter={16} style={{ marginTop: 10, fontSize: 12 }}>
        <Col span={6}><span style={{ color: '#52c41a' }}>Green points:</span> used in general baseline</Col>
        <Col span={6}><span style={{ color: '#1890ff' }}>Blue points:</span> used in active baseline</Col>
        <Col span={6}><span style={{ color: '#722ed1' }}>Purple points:</span> used in both</Col>
        <Col span={6}><span style={{ color: '#ff4d4f' }}>Red band:</span> current drop range</Col>
      </Row>
    </div>
  );
};

const buildConsumptionComparison = (actualRate, standardRate, unitType) => {
  if (
    actualRate == null || isNaN(actualRate) ||
    standardRate == null || isNaN(standardRate) || standardRate <= 0
  ) {
    return null;
  }

  // Match Full Tank logic:
  // - L/HOURS: higher actual is worse
  // - KMS/L: lower actual is worse
  const variancePercent = unitType === 'L/HOURS'
    ? ((actualRate - standardRate) / standardRate) * 100
    : ((standardRate - actualRate) / standardRate) * 100;

  const diffAbsolute = actualRate - standardRate;
  const isWorse = variancePercent > 0;
  const isBetter = variancePercent < 0;
  const color = isWorse ? '#ff4d4f' : isBetter ? '#52c41a' : '#8c8c8c';

  return {
    standardRate,
    diffAbsolute,
    variancePercent,
    isWorse,
    isBetter,
    color
  };
};

const extractModelRatesFromStandardArray = (standardFuelConsumptionRate) => {
  const result = { kmsPerLtr: null, ltrPerHour: null };
  if (!Array.isArray(standardFuelConsumptionRate)) return result;

  standardFuelConsumptionRate.forEach(rateObj => {
    const fuelQty = parseFloat(rateObj?.fuelQty);
    const unitName = (rateObj?.perType?.name || '').toUpperCase();
    if (!fuelQty || fuelQty <= 0 || !unitName) return;

    if (unitName === 'KMS' || unitName === 'KM' || unitName.includes('KM')) {
      result.kmsPerLtr = 1 / fuelQty; // API often provides L/KM
    } else if (unitName === 'HOURS' || unitName === 'HOUR' || unitName === 'HRS' || unitName.includes('HOUR')) {
      result.ltrPerHour = fuelQty; // API provides L/HOURS
    }
  });

  return result;
};

const StableFuelConsumptionAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [originalRawData, setOriginalRawData] = useState([]); // Store original CSV data
  const [stablePeriods, setStablePeriods] = useState([]);
  const [consumptionRates, setConsumptionRates] = useState([]);
  const [combinedTableData, setCombinedTableData] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'stable', 'consumption'
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [errors, setErrors] = useState([]);
  const [stabilityThreshold, setStabilityThreshold] = useState(0.1); // Liters threshold for stable period
  const [minStableDuration, setMinStableDuration] = useState(300); // Minimum duration in seconds for a stable period
  const [unitType, setUnitType] = useState('auto'); // 'auto', 'hours', 'kms'
  const [fuelChangeIntervalSeconds, setFuelChangeIntervalSeconds] = useState(150); // Default 2.5 minutes (150 seconds)
  const [breakdownModalVisible, setBreakdownModalVisible] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownTitle, setBreakdownTitle] = useState('');
  const [rawDataModalVisible, setRawDataModalVisible] = useState(false);
  const [filteredRawData, setFilteredRawData] = useState([]);
  const [rawDataModalTitle, setRawDataModalTitle] = useState('');
  const [assetList, setAssetList] = useState([]);
  const [parentAsset, setParentAsset] = useState(null);
  const [fuelMileageThreshold, setFuelMileageThreshold] = useState(null);
  const [assetCode, setAssetCode] = useState('');
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);
  const [assetListForSearch, setAssetListForSearch] = useState([]);
  const [assetCodeMap, setAssetCodeMap] = useState({}); // Map for asset code search (includes owner code and alt name)
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [resolvedLocationName, setResolvedLocationName] = useState(null);
  const [resolvedSubLocationName, setResolvedSubLocationName] = useState(null);
  const [modelRatesById, setModelRatesById] = useState({});
  const [modelRatesByName, setModelRatesByName] = useState({});

  // Fuel leakage detection state
  const [leakageDetectionLoading, setLeakageDetectionLoading] = useState(false);
  const [leakageDetectionResults, setLeakageDetectionResults] = useState(null);
  const [leakageDetectionModalVisible, setLeakageDetectionModalVisible] = useState(false);
  const [leakageDetectionConfig, setLeakageDetectionConfig] = useState({
    D_min_liters: 5,
    pre_window_minutes: 10,
    post_window_minutes: 15,
    T_tilt_deg: 6,
    filter_lag_minutes: 3,
    recovery_window_minutes: 20,
    recovery_ratio_dismiss: 0.7,
    recovery_ratio_confirm: 0.2
  });
  const [showLeakageDebugChart, setShowLeakageDebugChart] = useState(false);
  const [exportingLeakageImage, setExportingLeakageImage] = useState(false);
  const [consumptionCalcModalVisible, setConsumptionCalcModalVisible] = useState(false);
  const [selectedConsumptionCalc, setSelectedConsumptionCalc] = useState(null);
  const [baselineTrendModalVisible, setBaselineTrendModalVisible] = useState(false);
  const leakageExportRef = useRef(null);
  const leakageDrainMetrics = useMemo(
    () => buildPossibleDrainMetrics(leakageDetectionResults),
    [leakageDetectionResults]
  );
  const selectedAssetInfo = useMemo(() => {
    const fromParent = parentAsset || {};
    const fromMap = assetCodeMap[assetCode] || {};

    return {
      assetCode: fromParent['Asset Code'] || fromParent.assetCode || fromParent.asset_code || assetCode || 'N/A',
      ownerCode: fromParent['Owner Code'] || fromParent.ownerCode || fromParent.owner_code || fromMap.ownerCode || 'N/A',
      altName: fromParent['Alt Name'] || fromParent.altName || fromParent.alt_name || fromMap.altName || 'N/A'
    };
  }, [parentAsset, assetCodeMap, assetCode]);
  const assetDisplayInfo = useMemo(() => {
    const readText = (...vals) => {
      for (const v of vals) {
        if (v == null) continue;
        if (typeof v === 'string' && v.trim() !== '') return v;
        if (typeof v === 'number') return String(v);
        if (typeof v === 'object') {
          if (typeof v.name === 'string' && v.name.trim() !== '') return v.name;
          if (typeof v.code === 'string' && v.code.trim() !== '') return v.code;
          if (typeof v.title === 'string' && v.title.trim() !== '') return v.title;
        }
      }
      return 'N/A';
    };

    const isObjectId = (v) => typeof v === 'string' && /^[a-fA-F0-9]{24}$/.test(v.trim());
    const sanitizeLocationText = (value) => {
      const txt = readText(value);
      if (!txt || txt === 'N/A') return 'N/A';
      return isObjectId(txt) ? 'N/A' : txt;
    };

    return {
      assetModel: readText(parentAsset?.['Asset Model'], parentAsset?.assetModel, parentAsset?.asset_model),
      assetSubType: readText(parentAsset?.['Asset Sub Type'], parentAsset?.assetSubType, parentAsset?.asset_sub_type),
      location: sanitizeLocationText(resolvedLocationName || parentAsset?.Location || parentAsset?.location),
      subLocation: sanitizeLocationText(resolvedSubLocationName || parentAsset?.['Sub Location'] || parentAsset?.subLocation || parentAsset?.sub_location)
    };
  }, [parentAsset, resolvedLocationName, resolvedSubLocationName]);
  const resolvedStandardRates = useMemo(() => {
    const assetModelRef = parentAsset?.assetModel || parentAsset?.asset_model || null;
    const modelId = typeof assetModelRef === 'object'
      ? (
          assetModelRef?._id ||
          assetModelRef?.id ||
          assetModelRef?.modelId ||
          assetModelRef?.value ||
          assetModelRef?.assetModelId ||
          null
        )
      : (typeof assetModelRef === 'string' ? assetModelRef : null);
    const modelName = typeof assetModelRef === 'object'
      ? (assetModelRef?.name || assetModelRef?.modelName || null)
      : (typeof assetModelRef === 'string' ? assetModelRef : null);

    const byModel =
      (modelId && modelRatesById[modelId]) ||
      (modelName && (modelRatesByName[modelName] || modelRatesByName[String(modelName).toLowerCase()])) ||
      null;
    if (byModel && (byModel.kmsPerLtr != null || byModel.ltrPerHour != null)) {
      return { ...byModel, isDefined: true };
    }
    return { kmsPerLtr: null, ltrPerHour: null, isDefined: false };
  }, [parentAsset, modelRatesById, modelRatesByName]);
  const fuelMileageThresholdText = useMemo(() => {
    if (!fuelMileageThreshold) return 'N/A';
    const isObjectId = (v) => typeof v === 'string' && /^[a-fA-F0-9]{24}$/.test(v.trim());
    if (Array.isArray(fuelMileageThreshold)) {
      const parts = fuelMileageThreshold
        .map((t) => {
          if (t == null) return null;
          if (typeof t === 'number' || typeof t === 'string') return String(t);
          const value = t.value ?? t.threshold ?? null;
          const perTypeNameRaw = t.perType?.name || t.perType || null;
          const perTypeName = isObjectId(perTypeNameRaw) ? null : perTypeNameRaw;
          if (value != null && perTypeName) return `${value} (${perTypeName})`;
          if (value != null) return String(value);
          return null;
        })
        .filter(Boolean);
      return parts.length > 0 ? parts.join(' | ') : 'N/A';
    }
    if (typeof fuelMileageThreshold === 'object') {
      return String(fuelMileageThreshold.value ?? fuelMileageThreshold.threshold ?? 'N/A');
    }
    return String(fuelMileageThreshold);
  }, [fuelMileageThreshold]);
  const summarySnapshot = useMemo(() => {
    if (!combinedTableData || combinedTableData.length === 0) return null;

    const totals = combinedTableData.reduce((acc, record) => {
      acc.totalFuelConsumedFromTable += record.fuelConsumed || 0;
      acc.totalDistance += record.distanceKm || 0;
      acc.totalIgnitionHours += record.ignitionHours || 0;
      acc.totalIdleHours += record.idleHours || 0;
      acc.totalDuration += record.timeDurationHours || 0;
      return acc;
    }, {
      totalFuelConsumedFromTable: 0,
      totalIgnitionHours: 0,
      totalIdleHours: 0,
      totalDistance: 0,
      totalDuration: 0
    });

    let verificationFuelConsumed = 0;
    if (originalRawData && originalRawData.length > 0) {
      const firstFuelLevel = parseFloat(originalRawData[0].fuelLevelLiters) || 0;
      const lastFuelLevel = parseFloat(originalRawData[originalRawData.length - 1].fuelLevelLiters) || 0;
      verificationFuelConsumed = firstFuelLevel - lastFuelLevel;
    }

    let totalDetectedRefuel = 0;
    if (originalRawData && originalRawData.length > 0) {
      originalRawData.forEach(row => {
        const flagToCheck = row.flag_to_check || '';
        if (flagToCheck && flagToCheck.includes('Possible Refueling')) {
          const fuelConsAk = parseFloat(row.fuel_cons_ak) || 0;
          totalDetectedRefuel += Math.abs(fuelConsAk);
        }
      });
    }

    const actualTotalFuelConsumed = totals.totalFuelConsumedFromTable + totalDetectedRefuel;
    const totalLtrPerHour = totals.totalIgnitionHours > 0 && actualTotalFuelConsumed > 0
      ? actualTotalFuelConsumed / totals.totalIgnitionHours
      : null;
    const totalKmsPerLtr = actualTotalFuelConsumed > 0 && totals.totalDistance > 0
      ? totals.totalDistance / actualTotalFuelConsumed
      : null;

    const ltrPerHourComparison = buildConsumptionComparison(
      totalLtrPerHour,
      resolvedStandardRates.ltrPerHour,
      'L/HOURS'
    );
    const kmsPerLtrComparison = buildConsumptionComparison(
      totalKmsPerLtr,
      resolvedStandardRates.kmsPerLtr,
      'KMS/L'
    );

    return {
      totals,
      verificationFuelConsumed,
      totalDetectedRefuel,
      actualTotalFuelConsumed,
      totalLtrPerHour,
      totalKmsPerLtr,
      avgSpeed: totals.totalIgnitionHours > 0 && totals.totalDistance > 0
        ? totals.totalDistance / totals.totalIgnitionHours
        : null,
      ltrPerHourComparison,
      kmsPerLtrComparison
    };
  }, [combinedTableData, originalRawData, resolvedStandardRates]);

  const openConsumptionCalculationModal = (record) => {
    const metric = leakageDrainMetrics.eventMetrics[record.event_id];
    if (!metric || metric.expectedLoss == null) return;

    setSelectedConsumptionCalc({
      eventId: record.event_id,
      status: record.status,
      startTime: record.start_time_formatted,
      endTime: record.end_time_formatted,
      detectedTime: record.t_detect_formatted,
      baselineRateLph: leakageDrainMetrics.baselineRateLph,
      generalBaselineRateLph: leakageDrainMetrics.generalBaselineRateLph,
      activeBaselineRateLph: leakageDrainMetrics.activeBaselineRateLph,
      durationHours: metric.durationHours,
      ignitionOnHours: metric.ignitionOnHours,
      ignitionOnIdleHours: metric.ignitionOnIdleHours,
      ignitionOnActiveHours: metric.ignitionOnActiveHours,
      idleInIgnitionOnFraction: metric.idleInIgnitionOnFraction,
      activeInIgnitionOnFraction: metric.activeInIgnitionOnFraction,
      ignitionOnFraction: metric.ignitionOnFraction,
      expectedLoss: metric.expectedLoss,
      actualLoss: record.loss_liters,
      possibleDrain: metric.possibleDrain,
      dropStartTs: new Date(record.start_time_detected).getTime(),
      dropEndTs: new Date(record.end_time_detected).getTime(),
      baselineSeries: leakageDrainMetrics.baselineSeries
    });
    setConsumptionCalcModalVisible(true);
  };

  const loadModelStandardRates = async () => {
    try {
      const modelsResult = await apiGet('/asset-model', {
        headers: { 'Accept': '*/*' }
      });

      if (!modelsResult.success) {
        console.error('Failed to load asset models for standard rates:', modelsResult.error);
        return;
      }

      let modelsData = [];
      if (Array.isArray(modelsResult.data)) {
        modelsData = modelsResult.data;
      } else if (modelsResult.data && Array.isArray(modelsResult.data.data)) {
        modelsData = modelsResult.data.data;
      } else if (modelsResult.data && Array.isArray(modelsResult.data.models)) {
        modelsData = modelsResult.data.models;
      } else if (modelsResult.data && typeof modelsResult.data === 'object') {
        for (const key of Object.keys(modelsResult.data)) {
          if (Array.isArray(modelsResult.data[key])) {
            modelsData = modelsResult.data[key];
            break;
          }
        }
      }

      const byId = {};
      const byName = {};

      modelsData.forEach(model => {
        if (!model?._id) return;
        const rates = extractModelRatesFromStandardArray(model.standardFuelConsumptionRate);
        if (rates.kmsPerLtr == null && rates.ltrPerHour == null) return;

        byId[model._id] = rates;

        const modelName = (model.name || '').trim();
        if (modelName) {
          byName[modelName] = rates;
          byName[modelName.toLowerCase()] = rates;
        }
      });

      setModelRatesById(byId);
      setModelRatesByName(byName);
    } catch (error) {
      console.error('Error loading asset model standard rates:', error);
    }
  };

  const exportLeakageResultsImage = async () => {
    if (!leakageDetectionResults || !leakageExportRef.current || exportingLeakageImage) return;

    const wasDebugChartVisible = showLeakageDebugChart;
    let styleRestorers = [];
    setExportingLeakageImage(true);

    try {
      if (!wasDebugChartVisible) {
        setShowLeakageDebugChart(true);
        await new Promise(resolve => setTimeout(resolve, 450));
      } else {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const { default: html2canvas } = await import('html2canvas');
      const target = leakageExportRef.current;
      styleRestorers = [];

      // Expand scrollable AntD table containers so full scroll area is captured.
      const tableContainers = target.querySelectorAll(
        '.ant-table-body, .ant-table-content, .ant-table-scroll, .ant-table-header, .ant-table-wrapper'
      );
      tableContainers.forEach((el) => {
        const prev = {
          overflow: el.style.overflow,
          overflowX: el.style.overflowX,
          overflowY: el.style.overflowY,
          maxHeight: el.style.maxHeight,
          height: el.style.height,
          width: el.style.width
        };
        styleRestorers.push(() => {
          el.style.overflow = prev.overflow;
          el.style.overflowX = prev.overflowX;
          el.style.overflowY = prev.overflowY;
          el.style.maxHeight = prev.maxHeight;
          el.style.height = prev.height;
          el.style.width = prev.width;
        });

        el.style.overflow = 'visible';
        el.style.overflowX = 'visible';
        el.style.overflowY = 'visible';
        el.style.maxHeight = 'none';
        el.style.height = 'auto';
        if (el.scrollWidth > el.clientWidth) {
          el.style.width = `${el.scrollWidth}px`;
        }
      });

      const targetPrev = {
        width: target.style.width,
        maxWidth: target.style.maxWidth,
        overflow: target.style.overflow
      };
      styleRestorers.push(() => {
        target.style.width = targetPrev.width;
        target.style.maxWidth = targetPrev.maxWidth;
        target.style.overflow = targetPrev.overflow;
      });
      target.style.overflow = 'visible';
      target.style.maxWidth = 'none';
      if (target.scrollWidth > target.clientWidth) {
        target.style.width = `${target.scrollWidth}px`;
      }

      await new Promise(resolve => setTimeout(resolve, 120));

      const canvas = await html2canvas(target, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: target.scrollWidth,
        windowHeight: target.scrollHeight
      });

      styleRestorers.forEach((restore) => restore());
      styleRestorers = [];

      const link = document.createElement('a');
      const filename = `fuel_leakage_report_${selectedAssetInfo.assetCode}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.download = filename;
      link.click();
    } catch (error) {
      console.error('Failed to export leakage report image:', error);
    } finally {
      styleRestorers.forEach((restore) => {
        try { restore(); } catch (e) { /* no-op */ }
      });
      if (!wasDebugChartVisible) {
        setShowLeakageDebugChart(false);
      }
      setExportingLeakageImage(false);
    }
  };

  // Load asset list from API for search functionality
  const loadAssetList = async () => {
    setLoadingAssets(true);
    try {
      const result = await apiGet('/asset/asset-all', {
        headers: {
          'Accept': '*/*',
        },
      });

      if (!result.success) {
        console.error('Failed to load asset list:', result.error);
        return;
      }

      const assets = result.data || [];
      
      // Create asset code map with owner code and alt name for search
      const codeMap = {};
      const assetCodes = [];
      
      assets.forEach(asset => {
        const code = asset.assetCode || asset['Asset Code'] || '';
        const ownerCode = asset.ownerCode || asset['Owner Code'] || '';
        const altName = asset.altName || asset['Alt Name'] || '';
        
        if (code) {
          assetCodes.push(code);
          codeMap[code] = {
            assetCode: code,
            ownerCode: ownerCode,
            altName: altName
          };
        }
      });

      // Sort asset codes
      assetCodes.sort();
      
      setAssetListForSearch(assetCodes);
      setAssetCodeMap(codeMap);
    } catch (error) {
      console.error('Error loading asset list:', error);
    } finally {
      setLoadingAssets(false);
    }
  };

  // Load asset list on component mount
  useEffect(() => {
    loadAssetList();
    loadModelStandardRates();
  }, []);

  // Fallback name resolution for location/sub-location when only IDs are available.
  useEffect(() => {
    const resolveNamesFromIds = async () => {
      if (!parentAsset) return;

      const getId = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (typeof value === 'object') return value._id || value.id || null;
        return null;
      };
      const isObjectId = (v) => typeof v === 'string' && /^[a-fA-F0-9]{24}$/.test(v.trim());

      const locationId = getId(parentAsset.location || parentAsset.Location);
      const clientId = getId(parentAsset.client || parentAsset.subLocation || parentAsset['Sub Location']);

      try {
        if (!resolvedLocationName && locationId && isObjectId(locationId)) {
          const locationResult = await apiGet(`/location/${locationId}`, {
            headers: { 'Accept': '*/*' }
          });
          if (locationResult.success && locationResult.data) {
            const name = locationResult.data.name || locationResult.data.code || locationResult.data.title || null;
            if (name) setResolvedLocationName(name);
          }
        }
      } catch (error) {
        console.error('Location name lookup failed:', error);
      }

      try {
        if (!resolvedSubLocationName && clientId && isObjectId(clientId)) {
          const clientResult = await apiGet(`/client/${clientId}`, {
            headers: { 'Accept': '*/*' }
          });
          if (clientResult.success && clientResult.data) {
            const name = clientResult.data.name || clientResult.data.code || clientResult.data.title || null;
            if (name) setResolvedSubLocationName(name);
          }
        }
      } catch (error) {
        console.error('Sub-location name lookup failed:', error);
      }
    };

    resolveNamesFromIds();
  }, [parentAsset, resolvedLocationName, resolvedSubLocationName]);

  // Read URL parameters on mount (for cross-page navigation from Fuel Alert Analysis)
  useEffect(() => {
    const hash = window.location.hash;
    // Check if we have parameters in the URL hash: #/stable-fuel-analysis?asset=XXX&start=YYY&end=ZZZ
    if (hash.includes('stable-fuel-analysis') && hash.includes('?')) {
      try {
        const queryString = hash.split('?')[1];
        const params = new URLSearchParams(queryString);
        
        const assetParam = params.get('asset');
        const startParam = params.get('start');
        const endParam = params.get('end');
        
        console.log('URL parameters detected:', { assetParam, startParam, endParam });
        
        if (assetParam) {
          setAssetCode(assetParam);
        }
        
        if (startParam) {
          // Parse the start date/time (IST format: YYYY-MM-DD HH:mm:ss)
          const startMoment = moment(startParam, 'YYYY-MM-DD HH:mm:ss');
          if (startMoment.isValid()) {
            setStartDateTime(startMoment);
            console.log('Set start date/time from URL:', startMoment.format('YYYY-MM-DD HH:mm:ss'));
          }
        }
        
        if (endParam) {
          // Parse the end date/time (IST format: YYYY-MM-DD HH:mm:ss)
          const endMoment = moment(endParam, 'YYYY-MM-DD HH:mm:ss');
          if (endMoment.isValid()) {
            setEndDateTime(endMoment);
            console.log('Set end date/time from URL:', endMoment.format('YYYY-MM-DD HH:mm:ss'));
          }
        }
        
        // Clean up the URL by removing the query parameters (optional)
        // window.location.hash = '#/stable-fuel-analysis';
      } catch (error) {
        console.error('Error parsing URL parameters:', error);
      }
    }
  }, []);

  // Function to download data as CSV (for raw data)
  const downloadCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    // Get all unique keys from all objects
    const allKeys = new Set();
    data.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });
    const headers = Array.from(allKeys);
    
    // Create CSV content
    const csvRows = [];
    
    // Add headers
    csvRows.push(headers.map(h => `"${h}"`).join(','));
    
    // Add data rows
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '""';
        // Escape quotes and wrap in quotes
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      });
      csvRows.push(values.join(','));
    });
    
    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Transform IoT API data to format expected by processStablePeriods
  // Preserves ALL fields from API response (just like CSV had all fields)
  const transformIotDataToRawFormat = (iotData) => {
    if (!iotData || !Array.isArray(iotData)) {
      return [];
    }

    return iotData.map((point, index) => {
      // Start with ALL original fields from API response
      const transformedPoint = { ...point };

      // Normalize/ensure key fields exist (map API field names to expected names if needed)
      // Timestamp handling
      if (!transformedPoint.timestamp) {
        const timestamp = point.timestamp || point.time || point.date_timestamp || point.datetime;
        if (timestamp) {
          let epochTimestamp = timestamp;
          if (typeof timestamp === 'string') {
            epochTimestamp = moment.utc(timestamp).valueOf();
          } else if (timestamp && typeof timestamp === 'object' && timestamp.getTime) {
            epochTimestamp = timestamp.getTime();
          } else if (typeof timestamp === 'number') {
            epochTimestamp = timestamp;
          }
          transformedPoint.timestamp = epochTimestamp;
        }
      } else if (typeof transformedPoint.timestamp === 'string') {
        transformedPoint.timestamp = moment.utc(transformedPoint.timestamp).valueOf();
      }

      // Fuel level - ensure fuelLevelLiters exists (map from various possible field names)
      if (!transformedPoint.fuelLevelLiters) {
        const fuelLevel = point.fuelLevel || point.fuel_level || point.fuelLevelLiters || point.fuel_level_liters || point.fuel || 0;
        transformedPoint.fuelLevelLiters = parseFloat(fuelLevel) || 0;
      }

      // Odometer - ensure odometer_kms exists (API might return in meters, keep as is)
      if (!transformedPoint.odometer_kms) {
        const odometer = point.odometer || point.odometer_kms || point.distance || point.distance_travelled || 0;
        transformedPoint.odometer_kms = parseFloat(odometer) || 0;
      }

      // Ignition - ensure effective_ignition exists
      if (transformedPoint.effective_ignition === undefined) {
        const ignition = point.ignition || point.effective_ignition || point.ignition_status || (point.ignition_time ? 1 : 0) || 0;
        transformedPoint.effective_ignition = parseInt(ignition) || 0;
      }

      // Idle - ensure effective_idle exists
      if (transformedPoint.effective_idle === undefined) {
        const idle = point.idle || point.effective_idle || point.idle_status || (point.idle_time ? 1 : 0) || 0;
        transformedPoint.effective_idle = parseInt(idle) || 0;
      }

      // Ensure accelerometer fields use standard names (ax, ay, az) if they exist with other names
      if (!transformedPoint.ax && (point.acc_x !== undefined || point.acceleration_x !== undefined || point.x_axis !== undefined)) {
        transformedPoint.ax = parseFloat(point.acc_x || point.acceleration_x || point.x_axis || 0);
      }
      if (!transformedPoint.ay && (point.acc_y !== undefined || point.acceleration_y !== undefined || point.y_axis !== undefined)) {
        transformedPoint.ay = parseFloat(point.acc_y || point.acceleration_y || point.y_axis || 0);
      }
      if (!transformedPoint.az && (point.acc_z !== undefined || point.acceleration_z !== undefined || point.z_axis !== undefined)) {
        transformedPoint.az = parseFloat(point.acc_z || point.acceleration_z || point.z_axis || 0);
      }

      // Store the original API response point as originalRow (for CSV download and reference)
      transformedPoint.originalRow = point;

      return transformedPoint;
    });
  };

  const loadData = async () => {
    // Validate inputs
    if (!assetCode || !assetCode.trim()) {
      setErrors(['Please enter an Asset Code']);
      return;
    }

    if (!startDateTime || !endDateTime) {
      setErrors(['Please select both start and end date/time']);
      return;
    }

    // Validate 48-hour limit
    // Ensure moments are in IST timezone
    const startMoment = moment.tz(startDateTime, 'Asia/Kolkata');
    const endMoment = moment.tz(endDateTime, 'Asia/Kolkata');
    const hoursDiff = endMoment.diff(startMoment, 'hours', true);
    
    if (hoursDiff > 48) {
      setErrors([`Time difference cannot exceed 48 hours. Current difference: ${hoursDiff.toFixed(2)} hours`]);
      return;
    }

    if (hoursDiff <= 0) {
      setErrors(['End date/time must be after start date/time']);
      return;
    }

    setLoading(true);
    setErrors([]);
    
    try {
      setResolvedLocationName(null);
      setResolvedSubLocationName(null);

      // Step 1: Get asset ID from asset code
      const assetResult = await findAssetIdByCode(assetCode.trim());
      if (!assetResult.success) {
        setErrors([`Failed to find asset: ${assetResult.error}`]);
        setLoading(false);
        return;
      }
      const assetId = assetResult.assetId;

      // Step 2: Get device ID from asset
      const deviceResult = await getDeviceIdFromAsset(assetCode.trim());
      if (!deviceResult.success) {
        setErrors([`Failed to get device ID: ${deviceResult.error}`]);
        setLoading(false);
        return;
      }
      const deviceId = deviceResult.deviceId;

      // Step 3: Convert date/time to UTC timestamps (API expects UTC)
      // User enters in IST, convert to UTC (GMT)
      // The moments in state are already in IST timezone (set via moment.tz with 'Asia/Kolkata')
      // Explicitly ensure they're in IST, then convert to UTC
      const startMomentIST = startMoment.clone().utcOffset('+05:30');
      const endMomentIST = endMoment.clone().utcOffset('+05:30');
      
      // Convert IST to UTC (GMT) for API call
      const startTimestamp = startMomentIST.utc().valueOf();
      const endTimestamp = endMomentIST.utc().valueOf();

      console.log('Fetching IoT data:', {
        assetCode: assetCode.trim(),
        deviceId,
        assetId,
        'Start (IST)': startMomentIST.format('YYYY-MM-DD HH:mm:ss') + ' IST',
        'End (IST)': endMomentIST.format('YYYY-MM-DD HH:mm:ss') + ' IST',
        'Start (UTC)': startMomentIST.clone().utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC',
        'End (UTC)': endMomentIST.clone().utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC',
        startTimestamp,
        endTimestamp
      });

      // Step 4: Fetch IoT device data
      const result = await fetchIotDeviceDataInPeriod(deviceId, startTimestamp, endTimestamp, assetId);
      
      if (!result.success) {
        setErrors([`Failed to fetch IoT data: ${result.error}`]);
        setLoading(false);
        return;
      }

      // Step 5: Transform API response to expected format
      // The API might return an object with 'raw' property or an array directly
      let rawDataArray = [];
      if (Array.isArray(result.data)) {
        rawDataArray = result.data;
      } else if (result.data && result.data.raw && Array.isArray(result.data.raw)) {
        rawDataArray = result.data.raw;
      } else if (result.data && typeof result.data === 'object') {
        // Try to find array data in the response
        const keys = Object.keys(result.data);
        for (const key of keys) {
          if (Array.isArray(result.data[key])) {
            rawDataArray = result.data[key];
            break;
          }
        }
      }

      if (rawDataArray.length === 0) {
        setErrors(['No data returned from API for the selected period']);
        setLoading(false);
        return;
      }

      // Transform to expected format (preserves ALL fields from API response)
      const transformedData = transformIotDataToRawFormat(rawDataArray);
      
      if (transformedData.length === 0) {
        setErrors(['No valid data points found after transformation']);
        setLoading(false);
        return;
      }

      // Log first data point to verify all fields are preserved
      if (transformedData.length > 0) {
        console.log('Sample transformed data point (all fields preserved):', {
          fieldCount: Object.keys(transformedData[0]).length,
          fields: Object.keys(transformedData[0]),
          sample: transformedData[0]
        });
      }

      setOriginalRawData(transformedData); // Store original data with all fields
      setRawData(transformedData);
      processStablePeriods(transformedData);

      // Automatically run fuel leakage detection on loaded data
      runFuelLeakageDetection(transformedData, assetCode);

      // Load asset information (optional - for display purposes)
      // Asset information might be available in the API response or can be fetched separately
      // For now, we'll try to get it from the asset result
      if (assetResult.asset) {
        setParentAsset(assetResult.asset);
        setResolvedLocationName(assetResult.location || null);
        setResolvedSubLocationName(assetResult.subLocation || null);
        
        // Try to get standard consumption rate and fuel mileage threshold from asset model
        const assetModel = assetResult.asset.assetModel || assetResult.asset.asset_model;
        if (assetModel && assetModel.fuelMileageThreshold) {
          setFuelMileageThreshold(assetModel.fuelMileageThreshold);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setErrors([`Failed to load data: ${error.message}`]);
    } finally {
      setLoading(false);
    }
  };

  // Fuel Leakage Detection - runs automatically when data is loaded
  const runFuelLeakageDetection = (data, assetId) => {
    if (!data || data.length === 0) {
      console.log('Fuel Leakage Detection: No data available');
      setLeakageDetectionResults(null);
      return;
    }

    setLeakageDetectionLoading(true);

    try {
      // Validate that data has required fields
      const validation = validateSamples(data);
      if (!validation.valid) {
        console.warn('Fuel Leakage Detection - Validation failed:', validation.error);
        setLeakageDetectionResults({ 
          success: false, 
          error: validation.error,
          events: [],
          summary: { total_events: 0 }
        });
        setLeakageDetectionLoading(false);
        return;
      }

      console.log('Fuel Leakage Detection - Validation passed:', validation);
      console.log('Running detection on', data.length, 'data points');

      // Run detection algorithm with debug series for visualization
      const results = detectFuelLeakageEvents(
        data,
        leakageDetectionConfig,
        assetId || 'unknown',
        true // Include debug series for charting
      );

      console.log('Fuel Leakage Detection Results:', results);
      setLeakageDetectionResults(results);
    } catch (error) {
      console.error('Error during fuel leakage detection:', error);
      setLeakageDetectionResults({ 
        success: false, 
        error: error.message,
        events: [],
        summary: { total_events: 0 }
      });
    } finally {
      setLeakageDetectionLoading(false);
    }
  };

  // Open leakage detection modal to view results
  const handleViewLeakageResults = () => {
    if (leakageDetectionResults) {
      setLeakageDetectionModalVisible(true);
    }
  };

  // Get status color for leakage events
  const getLeakageStatusColor = (status) => {
    switch (status) {
      case EVENT_STATUS.CONFIRMED_LEAKAGE:
        return '#ff4d4f'; // Red
      case EVENT_STATUS.POSSIBLE_LEAKAGE:
        return '#fa8c16'; // Orange
      case EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED:
        return '#d46b08'; // Dark orange
      case EVENT_STATUS.POSSIBLE_UNCONFIRMED:
        return '#faad14'; // Yellow
      case EVENT_STATUS.DISMISSED_TILT_SLOSH:
        return '#52c41a'; // Green
      case EVENT_STATUS.DISMISSED_SETTLING:
        return '#52c41a'; // Green
      case EVENT_STATUS.DISMISSED_SENSOR_ERROR:
        return '#13c2c2'; // Cyan
      default:
        return '#999';
    }
  };

  // Get status label for leakage events
  const getLeakageStatusLabel = (status) => {
    switch (status) {
      case EVENT_STATUS.CONFIRMED_LEAKAGE:
        return 'Confirmed Leakage';
      case EVENT_STATUS.POSSIBLE_LEAKAGE:
        return 'Possible Leakage';
      case EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED:
        return 'Possible Leakage (Sensor Affected)';
      case EVENT_STATUS.POSSIBLE_UNCONFIRMED:
        return 'Possible (Unconfirmed)';
      case EVENT_STATUS.DISMISSED_TILT_SLOSH:
        return 'Dismissed (Tilt/Slosh)';
      case EVENT_STATUS.DISMISSED_SETTLING:
        return 'Dismissed (Settling)';
      case EVENT_STATUS.DISMISSED_SENSOR_ERROR:
        return 'Dismissed (Sensor Error)';
      default:
        return status;
    }
  };

  const processStablePeriods = (data) => {
    if (!data || data.length === 0) return;

    const processed = [];
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const timestamp = getEpochTimestamp(row.timestamp);
      const fuelLevel = parseFloat(row.fuelLevelLiters) || 0;
      // Odometer values are in metres, convert to kilometers
      const odometer = (parseFloat(row.odometer_kms) || 0) / 1000;
      const ignition = parseInt(row.effective_ignition) || 0;
      const idle = parseInt(row.effective_idle) || 0;

      processed.push({
        timestamp,
        fuelLevel,
        odometer,
        ignition,
        idle,
        originalRow: row
      });
    }

    // Sort by timestamp
    processed.sort((a, b) => a.timestamp - b.timestamp);

    // Identify stable fuel periods
    const periods = identifyStablePeriods(processed);
    setStablePeriods(periods);

    // Calculate consumption rates between stable periods
    const rates = calculateConsumptionRates(periods, processed);
    setConsumptionRates(rates);

    // Create combined table data with both stable periods and consumption intervals
    const combined = createCombinedTableData(periods, rates, processed);
    setCombinedTableData(combined);
  };

  // Calculate fuel change in first X seconds and last X seconds of an interval
  const calculateFuelChangeAtIntervalEdges = (intervalStart, intervalEnd, allData, intervalSeconds) => {
    const intervalMs = intervalSeconds * 1000;
    const firstIntervalEnd = intervalStart + intervalMs;
    const lastIntervalStart = intervalEnd - intervalMs;
    
    // Get data points in first interval
    const firstIntervalData = allData.filter(d => 
      d.timestamp >= intervalStart && d.timestamp <= firstIntervalEnd
    );
    
    // Get data points in last interval
    const lastIntervalData = allData.filter(d => 
      d.timestamp >= lastIntervalStart && d.timestamp <= intervalEnd
    );
    
    let firstIntervalFuelChange = null;
    let lastIntervalFuelChange = null;
    
    if (firstIntervalData.length >= 2) {
      const firstFuel = firstIntervalData[0].fuelLevel;
      const lastFuel = firstIntervalData[firstIntervalData.length - 1].fuelLevel;
      firstIntervalFuelChange = firstFuel - lastFuel; // Positive = loss, Negative = gain
    }
    
    if (lastIntervalData.length >= 2) {
      const firstFuel = lastIntervalData[0].fuelLevel;
      const lastFuel = lastIntervalData[lastIntervalData.length - 1].fuelLevel;
      lastIntervalFuelChange = firstFuel - lastFuel; // Positive = loss, Negative = gain
    }
    
    return {
      firstIntervalFuelChange,
      lastIntervalFuelChange
    };
  };

  const createCombinedTableData = (periods, rates, allData) => {
    const combined = [];
    const firstDataTime = allData[0]?.timestamp;
    const lastDataTime = allData[allData.length - 1]?.timestamp;


    // Interleave stable periods and consumption intervals
    for (let i = 0; i < periods.length; i++) {
      const period = periods[i];
      
      // Add stable period
      const periodData = allData.filter(d => 
        d.timestamp >= period.start && d.timestamp <= period.end
      );
      const periodIgnitionHours = calculateIgnitionDuration(periodData);
      const periodIdleHours = calculateIdleDuration(periodData);
      const periodStartOdometer = periodData[0]?.odometer || 0;
      const periodEndOdometer = periodData[periodData.length - 1]?.odometer || 0;
      const periodDistance = Math.abs(periodEndOdometer - periodStartOdometer);
      const periodDuration = (period.end - period.start) / (1000 * 60 * 60);

      // Calculate fuel consumed for stable period (startFuel - endFuel)
      const fuelConsumed = period.startFuel - period.endFuel;
      const isRefueling = fuelConsumed < 0;
      const fuelAdded = isRefueling ? Math.abs(fuelConsumed) : 0;

      // Calculate fuel change at interval edges
      const fuelChangeEdges = calculateFuelChangeAtIntervalEdges(
        period.start,
        period.end,
        allData,
        fuelChangeIntervalSeconds
      );

      // Calculate tilt change compared to previous stable period
      let tiltComparison = null;
      let tiltChangePercentage = null;
      if (i > 0) {
        const prevPeriod = periods[i - 1];
        tiltComparison = compareTwoStableIntervals(prevPeriod, period);
        if (tiltComparison && tiltComparison.totalTiltChangeDeg !== null) {
          // Calculate percentage change: (change / initial_tilt_angle) * 100
          // Since we're comparing angles, we use the angle change as percentage of 90 degrees (reference)
          // Or we can use the angle change itself as a percentage
          tiltChangePercentage = tiltComparison.totalTiltChangeDeg;
        }
      }

      combined.push({
        type: 'stable',
        periodIndex: i,
        startTime: period.start,
        endTime: period.end,
        startTimeIST: formatIST(period.start),
        endTimeIST: formatIST(period.end),
        startFuel: period.startFuel,
        endFuel: period.endFuel,
        avgFuelLevel: period.avgFuelLevel,
        fuelConsumed: fuelConsumed,
        fuelAdded: fuelAdded,
        isRefueling: isRefueling,
        duration: period.duration,
        timeDurationHours: periodDuration,
        ignitionHours: periodIgnitionHours,
        idleHours: periodIdleHours,
        distanceKm: periodDistance,
        dataPointCount: periodData.length,
        firstIntervalFuelChange: fuelChangeEdges.firstIntervalFuelChange,
        lastIntervalFuelChange: fuelChangeEdges.lastIntervalFuelChange,
        tiltEstimate: period.tiltEstimate,
        tiltComparison: tiltComparison,
        tiltChangePercentage: tiltChangePercentage,
        tiltChangeDeg: tiltComparison ? tiltComparison.totalTiltChangeDeg : null
      });

      // Add consumption interval after this stable period (if not the last period)
      if (i < periods.length - 1) {
        const rate = rates.find(r => r.periodIndex === i + 1);
        if (rate) {
          // Calculate fuel change at interval edges for consumption interval
          const fuelChangeEdges = calculateFuelChangeAtIntervalEdges(
            rate.consumptionIntervalStart,
            rate.consumptionIntervalEnd,
            allData,
            fuelChangeIntervalSeconds
          );
          
          combined.push({
            type: 'consumption',
            ...rate,
            firstIntervalFuelChange: fuelChangeEdges.firstIntervalFuelChange,
            lastIntervalFuelChange: fuelChangeEdges.lastIntervalFuelChange
          });
        }
      }
    }

    // Handle consumption interval after last stable period (if any)
    if (periods.length > 0 && lastDataTime > periods[periods.length - 1].end) {
      const lastRate = rates.find(r => r.periodIndex === periods.length);
      if (lastRate) {
        // Calculate fuel change at interval edges for consumption interval
        const fuelChangeEdges = calculateFuelChangeAtIntervalEdges(
          lastRate.consumptionIntervalStart,
          lastRate.consumptionIntervalEnd,
          allData,
          fuelChangeIntervalSeconds
        );
        
        combined.push({
          type: 'consumption',
          ...lastRate,
          firstIntervalFuelChange: fuelChangeEdges.firstIntervalFuelChange,
          lastIntervalFuelChange: fuelChangeEdges.lastIntervalFuelChange
        });
      }
    }
    
    // Handle consumption interval before first stable period (if any)
    if (periods.length > 0 && firstDataTime < periods[0].start) {
      const firstRate = rates.find(r => r.periodIndex === 0);
      if (firstRate) {
        // Calculate fuel change at interval edges for consumption interval
        const fuelChangeEdges = calculateFuelChangeAtIntervalEdges(
          firstRate.consumptionIntervalStart,
          firstRate.consumptionIntervalEnd,
          allData,
          fuelChangeIntervalSeconds
        );
        
        combined.push({
          type: 'consumption',
          ...firstRate,
          firstIntervalFuelChange: fuelChangeEdges.firstIntervalFuelChange,
          lastIntervalFuelChange: fuelChangeEdges.lastIntervalFuelChange
        });
      }
    }

    // Sort by start time to ensure chronological order
    combined.sort((a, b) => {
      const aTime = a.type === 'stable' ? a.startTime : a.consumptionIntervalStart;
      const bTime = b.type === 'stable' ? b.startTime : b.consumptionIntervalStart;
      return aTime - bTime;
    });

    return combined;
  };

  const identifyStablePeriods = (data) => {
    if (data.length === 0) return [];

    const periods = [];
    let currentPeriod = null;
    let periodStart = null;
    let periodEnd = null;
    let periodFuelLevels = [];
    let periodStartFuel = null;

    for (let i = 0; i < data.length; i++) {
      const current = data[i];
      const currentFuel = current.fuelLevel;

      if (i === 0) {
        // Start first period
        periodStart = current.timestamp;
        periodStartFuel = currentFuel;
        periodFuelLevels = [currentFuel];
        currentPeriod = {
          start: periodStart,
          end: current.timestamp,
          fuelLevels: [currentFuel],
          dataPoints: [current]
        };
        continue;
      }

      const prev = data[i - 1];
      const prevFuel = prev.fuelLevel;
      const fuelChangeFromPrev = Math.abs(currentFuel - prevFuel);
      
      // Check change from the START of the current period (not just previous point)
      // This ensures the period remains stable relative to its starting fuel level
      const fuelChangeFromPeriodStart = Math.abs(currentFuel - periodStartFuel);
      const timeDiff = (current.timestamp - prev.timestamp) / 1000; // seconds

      // Check if fuel level is stable:
      // 1. Change from previous point should be small (to avoid sudden spikes)
      // 2. Change from period start should be within threshold (to ensure overall stability)
      if (fuelChangeFromPrev <= stabilityThreshold && fuelChangeFromPeriodStart <= stabilityThreshold) {
        // Fuel is stable, continue current period
        currentPeriod.end = current.timestamp;
        currentPeriod.fuelLevels.push(currentFuel);
        currentPeriod.dataPoints.push(current);
      } else {
        // Fuel changed significantly - end current period and start new one
        if (currentPeriod) {
          const periodDuration = (currentPeriod.end - currentPeriod.start) / 1000; // seconds
          if (periodDuration >= minStableDuration) {
            // Only add period if it meets minimum duration
            const avgFuel = currentPeriod.fuelLevels.reduce((a, b) => a + b, 0) / currentPeriod.fuelLevels.length;
            
            // Calculate tilt for this stable period
            let tiltEstimate = null;
            try {
              tiltEstimate = estimateTiltFromInterval(currentPeriod.dataPoints || [], true);
            } catch (error) {
              console.warn("Could not calculate tilt for stable period:", error);
            }
            
            periods.push({
              ...currentPeriod,
              avgFuelLevel: avgFuel,
              duration: periodDuration,
              startFuel: currentPeriod.fuelLevels[0],
              endFuel: currentPeriod.fuelLevels[currentPeriod.fuelLevels.length - 1],
              tiltEstimate: tiltEstimate
            });
          }
        }

        // Start new period
        periodStart = current.timestamp;
        periodStartFuel = currentFuel;
        currentPeriod = {
          start: periodStart,
          end: current.timestamp,
          fuelLevels: [currentFuel],
          dataPoints: [current]
        };
      }
    }

    // Add last period if it exists
    if (currentPeriod) {
      const periodDuration = (currentPeriod.end - currentPeriod.start) / 1000;
      if (periodDuration >= minStableDuration) {
        const avgFuel = currentPeriod.fuelLevels.reduce((a, b) => a + b, 0) / currentPeriod.fuelLevels.length;
        
        // Calculate tilt for this stable period
        let tiltEstimate = null;
        try {
          tiltEstimate = estimateTiltFromInterval(currentPeriod.dataPoints || [], true);
        } catch (error) {
          console.warn("Could not calculate tilt for stable period:", error);
        }
        
        periods.push({
          ...currentPeriod,
          avgFuelLevel: avgFuel,
          duration: periodDuration,
          startFuel: currentPeriod.fuelLevels[0],
          endFuel: currentPeriod.fuelLevels[currentPeriod.fuelLevels.length - 1],
          tiltEstimate: tiltEstimate
        });
      }
    }

    return periods;
  };

  const calculateConsumptionRates = (periods, allData) => {
    if (periods.length === 0 || allData.length === 0) return [];

    const rates = [];
    const firstDataTime = allData[0].timestamp;
    const lastDataTime = allData[allData.length - 1].timestamp;

    // Handle consumption interval before first stable period (if any)
    if (periods.length > 0 && firstDataTime < periods[0].start) {
      const firstPeriod = periods[0];
      const startTime = firstDataTime;
      const endTime = firstPeriod.start;
      const intervalData = allData.filter(d => 
        d.timestamp >= startTime && d.timestamp < endTime
      );

      if (intervalData.length > 0) {
        const startFuel = intervalData[0].fuelLevel;
        const endFuel = firstPeriod.startFuel;
        const fuelConsumed = startFuel - endFuel;
        const isRefueling = fuelConsumed < 0;
        const fuelAdded = isRefueling ? Math.abs(fuelConsumed) : 0;

        const ignitionHours = calculateIgnitionDuration(intervalData);
        const idleHours = calculateIdleDuration(intervalData);
        const fuelChangeWhenIgnitionOff = calculateFuelChangeWhenIgnitionOff(intervalData);
        const fuelChangeWhenIdling = calculateFuelChangeWhenIdling(intervalData);
        const startOdometer = intervalData[0].odometer;
        const endOdometer = intervalData[intervalData.length - 1].odometer;
        const distanceKm = Math.abs(endOdometer - startOdometer);
        const timeDurationHours = (endTime - startTime) / (1000 * 60 * 60);
        const consumptionRateLtrPerHour = ignitionHours > 0 && fuelConsumed > 0 ? fuelConsumed / ignitionHours : null;
        const consumptionRateKmsPerLtr = distanceKm > 0 && fuelConsumed > 0 ? distanceKm / fuelConsumed : null;

        rates.push({
          periodIndex: 0,
          consumptionIntervalStart: startTime,
          consumptionIntervalEnd: endTime,
          startFuel: startFuel,
          endFuel: endFuel,
          fromPeriod: null, // No previous stable period
          toPeriod: {
            startTime: firstPeriod.start,
            startFuel: firstPeriod.startFuel,
            startTimeIST: formatIST(firstPeriod.start),
            endTime: firstPeriod.end,
            endFuel: firstPeriod.endFuel,
            endTimeIST: formatIST(firstPeriod.end),
            period: firstPeriod
          },
          fuelConsumed,
          fuelAdded,
          isRefueling,
          ignitionHours,
          idleHours,
          fuelLossWhenIgnitionOff: fuelChangeWhenIgnitionOff.loss,
          fuelIncreaseWhenIgnitionOff: fuelChangeWhenIgnitionOff.increase,
          fuelLossBreakdownWhenIgnitionOff: fuelChangeWhenIgnitionOff.lossBreakdown,
          fuelIncreaseBreakdownWhenIgnitionOff: fuelChangeWhenIgnitionOff.increaseBreakdown,
          fuelLossWhenIdling: fuelChangeWhenIdling.loss,
          fuelIncreaseWhenIdling: fuelChangeWhenIdling.increase,
          fuelLossBreakdownWhenIdling: fuelChangeWhenIdling.lossBreakdown,
          fuelIncreaseBreakdownWhenIdling: fuelChangeWhenIdling.increaseBreakdown,
          distanceKm,
          timeDurationHours,
          consumptionRateLtrPerHour,
          consumptionRateKmsPerLtr,
          displayUnit: distanceKm > 0.1 ? 'kms' : 'hours',
          dataPointCount: intervalData.length
        });
      }
    }

    // Handle consumption intervals between stable periods
    for (let i = 1; i < periods.length; i++) {
      const prevPeriod = periods[i - 1];
      const currentPeriod = periods[i];

      // Consumption interval is from end of previous stable period to start of current stable period
      const startTime = prevPeriod.end;
      const endTime = currentPeriod.start;
      const intervalData = allData.filter(d => 
        d.timestamp >= startTime && d.timestamp < endTime
      );

      if (intervalData.length === 0) continue;

      // Calculate fuel consumed from end of previous period to start of current period
      const startFuel = prevPeriod.endFuel;
      const endFuel = currentPeriod.startFuel;
      const fuelConsumed = startFuel - endFuel;
      const isRefueling = fuelConsumed < 0;
      const fuelAdded = isRefueling ? Math.abs(fuelConsumed) : 0;

      // Include ALL intervals - both consumption and refueling events
      // Calculate ignition duration (hours) for the complete interval
      const ignitionHours = calculateIgnitionDuration(intervalData);
      const idleHours = calculateIdleDuration(intervalData);
      const fuelChangeWhenIgnitionOff = calculateFuelChangeWhenIgnitionOff(intervalData);
      const fuelChangeWhenIdling = calculateFuelChangeWhenIdling(intervalData);

      // Calculate distance travelled (km) for the complete interval
      const startOdometer = intervalData[0].odometer;
      const endOdometer = intervalData[intervalData.length - 1].odometer;
      const distanceKm = Math.abs(endOdometer - startOdometer);

      // Calculate time duration (hours) for the complete interval
      const timeDurationHours = (endTime - startTime) / (1000 * 60 * 60);

      // Calculate consumption rates (only for consumption, not refueling)
      const consumptionRateLtrPerHour = ignitionHours > 0 && fuelConsumed > 0 ? fuelConsumed / ignitionHours : null;
      const consumptionRateKmsPerLtr = distanceKm > 0 && fuelConsumed > 0 ? distanceKm / fuelConsumed : null;

      // Determine unit type
      let displayUnit = unitType;
      if (unitType === 'auto') {
        // Auto-detect: if distance > 0 and significant, use km, otherwise use hours
        displayUnit = distanceKm > 0.1 ? 'kms' : 'hours';
      }

      rates.push({
        periodIndex: i,
        consumptionIntervalStart: startTime,
        consumptionIntervalEnd: endTime,
        startFuel: startFuel,
        endFuel: endFuel,
        fromPeriod: {
          startTime: prevPeriod.start,
          startFuel: prevPeriod.startFuel,
          startTimeIST: formatIST(prevPeriod.start),
          endTime: prevPeriod.end,
          endFuel: prevPeriod.endFuel,
          endTimeIST: formatIST(prevPeriod.end),
          period: prevPeriod
        },
        toPeriod: {
          startTime: currentPeriod.start,
          startFuel: currentPeriod.startFuel,
          startTimeIST: formatIST(currentPeriod.start),
          endTime: currentPeriod.end,
          endFuel: currentPeriod.endFuel,
          endTimeIST: formatIST(currentPeriod.end),
          period: currentPeriod
        },
        fuelConsumed,
        fuelAdded,
        isRefueling,
        ignitionHours,
        idleHours,
        fuelLossWhenIgnitionOff: fuelChangeWhenIgnitionOff.loss,
        fuelIncreaseWhenIgnitionOff: fuelChangeWhenIgnitionOff.increase,
        fuelLossBreakdownWhenIgnitionOff: fuelChangeWhenIgnitionOff.lossBreakdown,
        fuelIncreaseBreakdownWhenIgnitionOff: fuelChangeWhenIgnitionOff.increaseBreakdown,
        fuelLossWhenIdling: fuelChangeWhenIdling.loss,
        fuelIncreaseWhenIdling: fuelChangeWhenIdling.increase,
        fuelLossBreakdownWhenIdling: fuelChangeWhenIdling.lossBreakdown,
        fuelIncreaseBreakdownWhenIdling: fuelChangeWhenIdling.increaseBreakdown,
        distanceKm,
        timeDurationHours,
        consumptionRateLtrPerHour,
        consumptionRateKmsPerLtr,
        displayUnit,
        dataPointCount: intervalData.length
      });
    }

    // Handle consumption interval after last stable period (if any)
    if (periods.length > 0 && lastDataTime > periods[periods.length - 1].end) {
      const lastPeriod = periods[periods.length - 1];
      const startTime = lastPeriod.end;
      const endTime = lastDataTime;
      const intervalData = allData.filter(d => 
        d.timestamp > startTime && d.timestamp <= endTime
      );

      if (intervalData.length > 0) {
        const startFuel = lastPeriod.endFuel;
        const endFuel = intervalData[intervalData.length - 1].fuelLevel;
        const fuelConsumed = startFuel - endFuel;
        const isRefueling = fuelConsumed < 0;
        const fuelAdded = isRefueling ? Math.abs(fuelConsumed) : 0;

        const ignitionHours = calculateIgnitionDuration(intervalData);
        const idleHours = calculateIdleDuration(intervalData);
        const fuelChangeWhenIgnitionOff = calculateFuelChangeWhenIgnitionOff(intervalData);
        const fuelChangeWhenIdling = calculateFuelChangeWhenIdling(intervalData);
        const startOdometer = intervalData[0].odometer;
        const endOdometer = intervalData[intervalData.length - 1].odometer;
        const distanceKm = Math.abs(endOdometer - startOdometer);
        const timeDurationHours = (endTime - startTime) / (1000 * 60 * 60);
        const consumptionRateLtrPerHour = ignitionHours > 0 && fuelConsumed > 0 ? fuelConsumed / ignitionHours : null;
        const consumptionRateKmsPerLtr = distanceKm > 0 && fuelConsumed > 0 ? distanceKm / fuelConsumed : null;

        rates.push({
          periodIndex: periods.length,
          consumptionIntervalStart: startTime,
          consumptionIntervalEnd: endTime,
          startFuel: startFuel,
          endFuel: endFuel,
          fromPeriod: {
            startTime: lastPeriod.start,
            startFuel: lastPeriod.startFuel,
            startTimeIST: formatIST(lastPeriod.start),
            endTime: lastPeriod.end,
            endFuel: lastPeriod.endFuel,
            endTimeIST: formatIST(lastPeriod.end),
            period: lastPeriod
          },
          toPeriod: null, // No next stable period
          fuelConsumed,
          fuelAdded,
          isRefueling,
          ignitionHours,
          idleHours,
          fuelLossWhenIgnitionOff: fuelChangeWhenIgnitionOff.loss,
          fuelIncreaseWhenIgnitionOff: fuelChangeWhenIgnitionOff.increase,
          fuelLossBreakdownWhenIgnitionOff: fuelChangeWhenIgnitionOff.lossBreakdown,
          fuelIncreaseBreakdownWhenIgnitionOff: fuelChangeWhenIgnitionOff.increaseBreakdown,
          fuelLossWhenIdling: fuelChangeWhenIdling.loss,
          fuelIncreaseWhenIdling: fuelChangeWhenIdling.increase,
          fuelLossBreakdownWhenIdling: fuelChangeWhenIdling.lossBreakdown,
          fuelIncreaseBreakdownWhenIdling: fuelChangeWhenIdling.increaseBreakdown,
          distanceKm,
          timeDurationHours,
          consumptionRateLtrPerHour,
          consumptionRateKmsPerLtr,
          displayUnit: distanceKm > 0.1 ? 'kms' : 'hours',
          dataPointCount: intervalData.length
        });
      }
    }

    return rates;
  };

  const calculateIgnitionDuration = (data) => {
    if (!data || data.length === 0) return 0;

    let totalIgnitionDuration = 0; // milliseconds

    for (let i = 1; i < data.length; i++) {
      const current = data[i];
      const prev = data[i - 1];
      
      const timeDiff = current.timestamp - prev.timestamp;
      const prevIgnition = prev.ignition || 0;
      const currentIgnition = current.ignition || 0;
      const prevIsOn = prevIgnition === 1;
      const currentIsOn = currentIgnition === 1;

      // Apply ignition duration rules
      if (prevIsOn && currentIsOn) {
        totalIgnitionDuration += timeDiff;
      } else if (prevIsOn && !currentIsOn) {
        totalIgnitionDuration += timeDiff;
      }
      // If prev is off and current is on, or both off, don't add time
    }

    return totalIgnitionDuration / (1000 * 60 * 60); // Convert to hours
  };

  const calculateIdleDuration = (data) => {
    if (!data || data.length === 0) return 0;

    let totalIdleDuration = 0; // milliseconds

    for (let i = 1; i < data.length; i++) {
      const current = data[i];
      const prev = data[i - 1];
      
      const timeDiff = current.timestamp - prev.timestamp;
      const prevIdle = prev.idle || 0;
      const currentIdle = current.idle || 0;
      const prevIsIdle = prevIdle === 1;
      const currentIsIdle = currentIdle === 1;

      // Apply idle duration rules (same logic as ignition)
      // If vehicle was idle in previous period and/or current period, count the time
      if (prevIsIdle && currentIsIdle) {
        totalIdleDuration += timeDiff;
      } else if (prevIsIdle && !currentIsIdle) {
        totalIdleDuration += timeDiff;
      }
      // If prev is not idle and current is idle, or both not idle, don't add time
    }

    return totalIdleDuration / (1000 * 60 * 60); // Convert to hours
  };

  // Calculate fuel change when engine is off (ignition = 0) - loss indicates leakage, increase indicates refueling
  const calculateFuelChangeWhenIgnitionOff = (data) => {
    if (!data || data.length === 0) return { loss: 0, increase: 0, lossBreakdown: [], increaseBreakdown: [] };

    let totalFuelLoss = 0;
    let totalFuelIncrease = 0;
    const lossBreakdown = [];
    const increaseBreakdown = [];
    let periodStart = null;
    let periodStartFuel = null;

    for (let i = 1; i < data.length; i++) {
      const current = data[i];
      const prev = data[i - 1];
      
      const prevIgnition = prev.ignition || 0;
      const currentIgnition = current.ignition || 0;
      const prevFuel = prev.fuelLevel || 0;
      const currentFuel = current.fuelLevel || 0;

      // If both records have ignition = 0 (engine off), check for fuel change
      if (prevIgnition === 0 && currentIgnition === 0) {
        if (periodStart === null) {
          periodStart = prev.timestamp;
          periodStartFuel = prevFuel;
        }
        
        const fuelChange = prevFuel - currentFuel;
        if (fuelChange > 0) {
          totalFuelLoss += fuelChange;
        } else if (fuelChange < 0) {
          totalFuelIncrease += Math.abs(fuelChange);
        }
        
        // End of period - add to breakdown
        if (i === data.length - 1 || (i < data.length - 1 && data[i + 1].ignition !== 0)) {
          const periodEnd = current.timestamp;
          const periodEndFuel = currentFuel;
          const periodFuelChange = periodStartFuel - periodEndFuel;
          
          if (periodFuelChange > 0) {
            lossBreakdown.push({
              startTime: periodStart,
              endTime: periodEnd,
              startFuel: periodStartFuel,
              endFuel: periodEndFuel,
              fuelChange: periodFuelChange
            });
          } else if (periodFuelChange < 0) {
            increaseBreakdown.push({
              startTime: periodStart,
              endTime: periodEnd,
              startFuel: periodStartFuel,
              endFuel: periodEndFuel,
              fuelChange: Math.abs(periodFuelChange)
            });
          }
          periodStart = null;
          periodStartFuel = null;
        }
      }
      // If transitioning from off to on, count the period before transition
      else if (prevIgnition === 0 && currentIgnition === 1) {
        if (periodStart === null) {
          periodStart = prev.timestamp;
          periodStartFuel = prevFuel;
        }
        
        const periodEnd = current.timestamp;
        const periodEndFuel = currentFuel;
        const periodFuelChange = periodStartFuel - periodEndFuel;
        
        if (periodFuelChange > 0) {
          lossBreakdown.push({
            startTime: periodStart,
            endTime: periodEnd,
            startFuel: periodStartFuel,
            endFuel: periodEndFuel,
            fuelChange: periodFuelChange
          });
          totalFuelLoss += periodFuelChange;
        } else if (periodFuelChange < 0) {
          increaseBreakdown.push({
            startTime: periodStart,
            endTime: periodEnd,
            startFuel: periodStartFuel,
            endFuel: periodEndFuel,
            fuelChange: Math.abs(periodFuelChange)
          });
          totalFuelIncrease += Math.abs(periodFuelChange);
        }
        periodStart = null;
        periodStartFuel = null;
      } else {
        // Reset period tracking if ignition is on
        periodStart = null;
        periodStartFuel = null;
      }
    }

    return { loss: totalFuelLoss, increase: totalFuelIncrease, lossBreakdown, increaseBreakdown };
  };

  // Calculate fuel change when vehicle is idling (idle = 1) - loss indicates consumption, increase indicates refueling
  const calculateFuelChangeWhenIdling = (data) => {
    if (!data || data.length === 0) return { loss: 0, increase: 0, lossBreakdown: [], increaseBreakdown: [] };

    let totalFuelLoss = 0;
    let totalFuelIncrease = 0;
    const lossBreakdown = [];
    const increaseBreakdown = [];
    let periodStart = null;
    let periodStartFuel = null;

    for (let i = 1; i < data.length; i++) {
      const current = data[i];
      const prev = data[i - 1];
      
      const prevIdle = prev.idle || 0;
      const currentIdle = current.idle || 0;
      const prevFuel = prev.fuelLevel || 0;
      const currentFuel = current.fuelLevel || 0;

      // If both records have idle = 1 (vehicle idling), check for fuel change
      if (prevIdle === 1 && currentIdle === 1) {
        if (periodStart === null) {
          periodStart = prev.timestamp;
          periodStartFuel = prevFuel;
        }
        
        const fuelChange = prevFuel - currentFuel;
        if (fuelChange > 0) {
          totalFuelLoss += fuelChange;
        } else if (fuelChange < 0) {
          totalFuelIncrease += Math.abs(fuelChange);
        }
        
        // End of period - add to breakdown
        if (i === data.length - 1 || (i < data.length - 1 && data[i + 1].idle !== 1)) {
          const periodEnd = current.timestamp;
          const periodEndFuel = currentFuel;
          const periodFuelChange = periodStartFuel - periodEndFuel;
          
          if (periodFuelChange > 0) {
            lossBreakdown.push({
              startTime: periodStart,
              endTime: periodEnd,
              startFuel: periodStartFuel,
              endFuel: periodEndFuel,
              fuelChange: periodFuelChange
            });
          } else if (periodFuelChange < 0) {
            increaseBreakdown.push({
              startTime: periodStart,
              endTime: periodEnd,
              startFuel: periodStartFuel,
              endFuel: periodEndFuel,
              fuelChange: Math.abs(periodFuelChange)
            });
          }
          periodStart = null;
          periodStartFuel = null;
        }
      }
      // If transitioning from idle to not idle, count the period before transition
      else if (prevIdle === 1 && currentIdle === 0) {
        if (periodStart === null) {
          periodStart = prev.timestamp;
          periodStartFuel = prevFuel;
        }
        
        const periodEnd = current.timestamp;
        const periodEndFuel = currentFuel;
        const periodFuelChange = periodStartFuel - periodEndFuel;
        
        if (periodFuelChange > 0) {
          lossBreakdown.push({
            startTime: periodStart,
            endTime: periodEnd,
            startFuel: periodStartFuel,
            endFuel: periodEndFuel,
            fuelChange: periodFuelChange
          });
          totalFuelLoss += periodFuelChange;
        } else if (periodFuelChange < 0) {
          increaseBreakdown.push({
            startTime: periodStart,
            endTime: periodEnd,
            startFuel: periodStartFuel,
            endFuel: periodEndFuel,
            fuelChange: Math.abs(periodFuelChange)
          });
          totalFuelIncrease += Math.abs(periodFuelChange);
        }
        periodStart = null;
        periodStartFuel = null;
      } else {
        // Reset period tracking if not idling
        periodStart = null;
        periodStartFuel = null;
      }
    }

    return { loss: totalFuelLoss, increase: totalFuelIncrease, lossBreakdown, increaseBreakdown };
  };

  useEffect(() => {
    if (rawData.length > 0) {
      processStablePeriods(rawData);
    }
  }, [stabilityThreshold, minStableDuration, unitType, fuelChangeIntervalSeconds]);

  // Sync footer scroll with table body scroll (bidirectional)
  useEffect(() => {
    const syncScroll = () => {
      const tableBody = document.querySelector('.ant-table-body');
      const footerDiv = document.querySelector('.ant-table-footer > div');
      
      if (tableBody && footerDiv) {
        let isScrolling = false;
        
        const handleTableScroll = () => {
          if (!isScrolling) {
            isScrolling = true;
            footerDiv.scrollLeft = tableBody.scrollLeft;
            setTimeout(() => { isScrolling = false; }, 10);
          }
        };
        
        const handleFooterScroll = () => {
          if (!isScrolling) {
            isScrolling = true;
            tableBody.scrollLeft = footerDiv.scrollLeft;
            setTimeout(() => { isScrolling = false; }, 10);
          }
        };
        
        tableBody.addEventListener('scroll', handleTableScroll);
        footerDiv.addEventListener('scroll', handleFooterScroll);
        
        return () => {
          tableBody.removeEventListener('scroll', handleTableScroll);
          footerDiv.removeEventListener('scroll', handleFooterScroll);
        };
      }
    };
    
    // Wait for table to render
    const timer = setTimeout(syncScroll, 100);
    return () => clearTimeout(timer);
  }, [combinedTableData, typeFilter]);

  const columns = [
    {
      title: 'Period',
      key: 'period',
      width: 80,
      render: (_, record, index) => {
        const periodLabel = record.type === 'stable' 
          ? `SP${(record.periodIndex ?? 0) + 1}` 
          : `CI${(record.periodIndex ?? 0) + 1}`;
        
        const handlePeriodClick = () => {
          // Get start and end time for this period
          const startTime = record.type === 'stable' ? record.startTime : record.consumptionIntervalStart;
          const endTime = record.type === 'stable' ? record.endTime : record.consumptionIntervalEnd;
          
          // Filter raw data for this time range
          // rawData contains processed data with timestamp, we need to get originalRow
          const filtered = rawData
            .filter(d => {
              const dataTime = d.timestamp;
              return dataTime >= startTime && dataTime <= endTime;
            })
            .map(d => d.originalRow || d); // Get original row data with all fields
          
          setFilteredRawData(filtered);
          setRawDataModalTitle(`${periodLabel} - Raw Data (${formatIST(startTime)} to ${formatIST(endTime)})`);
          setRawDataModalVisible(true);
        };

        const handleDownloadClick = (e) => {
          e.stopPropagation(); // Prevent triggering the period click
          
          // Get start and end time for this period
          const startTime = record.type === 'stable' ? record.startTime : record.consumptionIntervalStart;
          const endTime = record.type === 'stable' ? record.endTime : record.consumptionIntervalEnd;
          
          // Filter raw data for this time range
          const filtered = rawData
            .filter(d => {
              const dataTime = d.timestamp;
              return dataTime >= startTime && dataTime <= endTime;
            })
            .map(d => d.originalRow || d); // Get original row data with all fields
          
          // Generate filename
          const startTimeStr = formatIST(startTime).replace(/[: ]/g, '_');
          const endTimeStr = formatIST(endTime).replace(/[: ]/g, '_');
          const filename = `${periodLabel}_${startTimeStr}_to_${endTimeStr}.csv`;
          
          downloadCSV(filtered, filename);
        };

        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span 
              style={{ 
                color: '#1890ff', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                fontWeight: 'bold'
              }}
              onClick={handlePeriodClick}
            >
              {periodLabel}
            </span>
            <span
              style={{ 
                color: '#52c41a', 
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                userSelect: 'none'
              }}
              onClick={handleDownloadClick}
              title="Download CSV"
            >
              ⬇
            </span>
          </span>
        );
      },
      sorter: (a, b, sortOrder) => {
        const aIndex = a.periodIndex ?? 0;
        const bIndex = b.periodIndex ?? 0;
        return sortOrder === 'ascend' ? aIndex - bIndex : bIndex - aIndex;
      }
    },
    {
      title: 'Period (IST)',
      key: 'periodTime',
      width: 220,
      render: (_, record) => {
        if (record.type === 'stable') {
          return (
            <div style={{ fontSize: '11px' }}>
              <div><strong>Start:</strong> {record.startTimeIST}</div>
              <div><strong>End:</strong> {record.endTimeIST}</div>
            </div>
          );
        } else {
          return (
            <div style={{ fontSize: '11px' }}>
              <div><strong>Start:</strong> {formatIST(record.consumptionIntervalStart)}</div>
              <div><strong>End:</strong> {formatIST(record.consumptionIntervalEnd)}</div>
            </div>
          );
        }
      },
      sorter: (a, b) => {
        const aTime = a.type === 'stable' ? a.startTime : a.consumptionIntervalStart;
        const bTime = b.type === 'stable' ? b.startTime : b.consumptionIntervalStart;
        return aTime - bTime;
      }
    },
    {
      title: 'Fuel Level (L)',
      key: 'fuelLevel',
      width: 150,
      render: (_, record) => {
        if (record.type === 'stable') {
          return (
            <div style={{ fontSize: '11px' }}>
              <div>Start: {record.startFuel.toFixed(2)}L</div>
              <div>End: {record.endFuel.toFixed(2)}L</div>
            </div>
          );
        } else {
          // Consumption interval - show start and end fuel levels
          return (
            <div style={{ fontSize: '11px' }}>
              <div>Start: {record.startFuel.toFixed(2)}L</div>
              <div>End: {record.endFuel.toFixed(2)}L</div>
            </div>
          );
        }
      }
    },
    {
      title: 'Tilt Change',
      key: 'tiltChange',
      width: 180,
      render: (_, record) => {
        if (record.type === 'stable') {
          if (record.tiltChangeDeg === null || record.tiltChangeDeg === undefined) {
            if (record.periodIndex === 0) {
              return <span style={{ color: '#999' }}>N/A (First Period)</span>;
            }
            return <span style={{ color: '#999' }}>N/A</span>;
          }
          
          const tiltChange = record.tiltChangeDeg;
          const tiltComparison = record.tiltComparison;
          
          // Calculate percentage change: (angle_change / 90) * 100 as a reference
          // Or we can show it as degrees with percentage
          const percentageChange = (tiltChange / 90) * 100;
          
          // Color code based on magnitude: significant changes (>5 degrees) in red
          const isSignificant = tiltChange > 5;
          
          return (
            <div style={{ fontSize: '11px' }}>
              <div style={{ 
                color: isSignificant ? '#ff4d4f' : '#333',
                fontWeight: isSignificant ? 'bold' : 'normal'
              }}>
                {tiltChange.toFixed(2)}° ({percentageChange.toFixed(1)}%)
              </div>
              {tiltComparison && (
                <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                  Roll: {tiltComparison.deltaRollDeg.toFixed(1)}°
                </div>
              )}
              {tiltComparison && (
                <div style={{ fontSize: '10px', color: '#666' }}>
                  Pitch: {tiltComparison.deltaPitchDeg.toFixed(1)}°
                </div>
              )}
            </div>
          );
        } else {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
      },
      sorter: (a, b) => {
        if (a.type === 'stable' && b.type === 'stable') {
          const aValue = a.tiltChangeDeg ?? 0;
          const bValue = b.tiltChangeDeg ?? 0;
          return aValue - bValue;
        }
        if (a.type === 'stable') return -1;
        if (b.type === 'stable') return 1;
        return 0;
      }
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 120,
      render: (_, record) => {
        const hours = record.timeDurationHours;
        if (hours < 1) {
          return `${Math.round(hours * 60)} min`;
        }
        return `${hours.toFixed(2)} hrs`;
      },
      sorter: (a, b) => (a.timeDurationHours || 0) - (b.timeDurationHours || 0)
    },
    {
      title: 'Fuel Consumed (L)',
      key: 'fuelConsumed',
      width: 150,
      render: (_, record) => {
        if (record.isRefueling) {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>+{record.fuelAdded.toFixed(2)} (Refuel)</span>;
        }
        // Show fuel consumed as negative (since refuel is positive)
        return <span>-{Math.abs(record.fuelConsumed).toFixed(2)}</span>;
      },
      sorter: (a, b) => {
        // fuelConsumed is: positive for consumption, negative for refueling
        // For sorting: we want negative (consumption) first, then positive (refuel)
        // Simply negate fuelConsumed: consumption becomes negative, refueling becomes positive
        const aValue = -a.fuelConsumed; // Consumption: -positive = negative, Refuel: -negative = positive
        const bValue = -b.fuelConsumed;
        // Sort numerically: negative values (consumption) come before positive (refuel)
        return aValue - bValue;
      }
    },
    {
      title: 'Ltr/Hr',
      key: 'ltrPerHour',
      width: 120,
      render: (_, record) => {
        if (record.type === 'stable') {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        if (record.consumptionRateLtrPerHour === null) return 'N/A';
        const rate = record.consumptionRateLtrPerHour;
        const isHigh = rate > 50;
        return (
          <span style={{ color: isHigh ? '#ff4d4f' : '#52c41a', fontWeight: isHigh ? 'bold' : 'normal' }}>
            {rate.toFixed(2)}
          </span>
        );
      },
      sorter: (a, b) => {
        if (a.type === 'stable' || b.type === 'stable') {
          if (a.type === 'stable' && b.type === 'stable') return 0;
          return a.type === 'stable' ? 1 : -1;
        }
        const aRate = a.consumptionRateLtrPerHour ?? Infinity;
        const bRate = b.consumptionRateLtrPerHour ?? Infinity;
        if (aRate === Infinity && bRate === Infinity) return 0;
        if (aRate === Infinity) return 1;
        if (bRate === Infinity) return -1;
        return aRate - bRate;
      }
    },
    {
      title: 'Kms/Ltr',
      key: 'kmsPerLtr',
      width: 120,
      render: (_, record) => {
        if (record.type === 'stable') {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        if (record.consumptionRateKmsPerLtr === null) return 'N/A';
        const rate = record.consumptionRateKmsPerLtr;
        return rate.toFixed(2);
      },
      sorter: (a, b) => {
        if (a.type === 'stable' || b.type === 'stable') {
          if (a.type === 'stable' && b.type === 'stable') return 0;
          return a.type === 'stable' ? 1 : -1;
        }
        const aRate = a.consumptionRateKmsPerLtr ?? Infinity;
        const bRate = b.consumptionRateKmsPerLtr ?? Infinity;
        if (aRate === Infinity && bRate === Infinity) return 0;
        if (aRate === Infinity) return 1;
        if (bRate === Infinity) return -1;
        return aRate - bRate;
      }
    },
    {
      title: 'Ignition Hours',
      key: 'ignitionHours',
      width: 130,
      render: (_, record) => {
        const hours = record.ignitionHours || 0;
        return hours > 0 ? hours.toFixed(2) : '0.00';
      },
      sorter: (a, b) => (a.ignitionHours || 0) - (b.ignitionHours || 0)
    },
    {
      title: 'Idle Hours',
      key: 'idleHours',
      width: 130,
      render: (_, record) => {
        const idleHours = record.idleHours || 0;
        const ignitionHours = record.ignitionHours || 0;
        const idlePercentage = ignitionHours > 0 ? (idleHours / ignitionHours) * 100 : 0;
        const isHighIdle = idlePercentage > 50; // Highlight if idle time is more than 50% of ignition time
        return (
          <span style={{ color: isHighIdle ? '#ff4d4f' : '#333', fontWeight: isHighIdle ? 'bold' : 'normal' }}>
            {idleHours.toFixed(2)}
            {ignitionHours > 0 && (
              <span style={{ fontSize: '10px', color: '#999', marginLeft: '4px' }}>
                ({idlePercentage.toFixed(1)}%)
              </span>
            )}
          </span>
        );
      },
      sorter: (a, b) => (a.idleHours || 0) - (b.idleHours || 0)
    },
    {
      title: 'Fuel Change (Engine Off)',
      key: 'fuelChangeWhenIgnitionOff',
      width: 200,
      sorter: (a, b) => {
        if (a.type === 'stable' || b.type === 'stable') {
          if (a.type === 'stable' && b.type === 'stable') return 0;
          return a.type === 'stable' ? 1 : -1;
        }
        const aTotal = (a.fuelLossWhenIgnitionOff || 0) + (a.fuelIncreaseWhenIgnitionOff || 0);
        const bTotal = (b.fuelLossWhenIgnitionOff || 0) + (b.fuelIncreaseWhenIgnitionOff || 0);
        return aTotal - bTotal;
      },
      render: (_, record) => {
        if (record.type === 'stable') {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        const fuelLoss = record.fuelLossWhenIgnitionOff || 0;
        const fuelIncrease = record.fuelIncreaseWhenIgnitionOff || 0;
        const hasLoss = fuelLoss > 0.01;
        const hasIncrease = fuelIncrease > 0.01;
        
        if (hasLoss && hasIncrease) {
          return (
            <div style={{ fontSize: '11px' }}>
              <div 
                style={{ color: '#ff4d4f', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  setBreakdownData(record.fuelLossBreakdownWhenIgnitionOff || []);
                  setBreakdownTitle(`Fuel Loss Breakdown (Engine Off) - Period CI${(record.periodIndex ?? 0) + 1}`);
                  setBreakdownModalVisible(true);
                }}
              >
                -{fuelLoss.toFixed(2)} L <span style={{ fontSize: '9px' }}>(Loss)</span>
              </div>
              <div 
                style={{ color: '#52c41a', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  setBreakdownData(record.fuelIncreaseBreakdownWhenIgnitionOff || []);
                  setBreakdownTitle(`Fuel Increase Breakdown (Engine Off) - Period CI${(record.periodIndex ?? 0) + 1}`);
                  setBreakdownModalVisible(true);
                }}
              >
                +{fuelIncrease.toFixed(2)} L <span style={{ fontSize: '9px' }}>(Refuel)</span>
              </div>
            </div>
          );
        } else if (hasLoss) {
          return (
            <span 
              style={{ 
                color: '#ff4d4f', 
                fontWeight: 'bold',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={() => {
                setBreakdownData(record.fuelLossBreakdownWhenIgnitionOff || []);
                setBreakdownTitle(`Fuel Loss Breakdown (Engine Off) - Period CI${(record.periodIndex ?? 0) + 1}`);
                setBreakdownModalVisible(true);
              }}
            >
              -{fuelLoss.toFixed(2)} L
              <span style={{ fontSize: '10px', color: '#ff4d4f', marginLeft: '4px' }}>
                (Possible Leakage?)
              </span>
            </span>
          );
        } else if (hasIncrease) {
          return (
            <span 
              style={{ 
                color: '#52c41a', 
                fontWeight: 'bold',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={() => {
                setBreakdownData(record.fuelIncreaseBreakdownWhenIgnitionOff || []);
                setBreakdownTitle(`Fuel Increase Breakdown (Engine Off) - Period CI${(record.periodIndex ?? 0) + 1}`);
                setBreakdownModalVisible(true);
              }}
            >
              +{fuelIncrease.toFixed(2)} L <span style={{ fontSize: '10px' }}>(Refuel)</span>
            </span>
          );
        } else {
          return <span style={{ color: '#999' }}>0.00 L</span>;
        }
      }
    },
    {
      title: 'Fuel Change (Idling)',
      key: 'fuelChangeWhenIdling',
      width: 180,
      sorter: (a, b) => {
        if (a.type === 'stable' || b.type === 'stable') {
          if (a.type === 'stable' && b.type === 'stable') return 0;
          return a.type === 'stable' ? 1 : -1;
        }
        const aTotal = (a.fuelLossWhenIdling || 0) + (a.fuelIncreaseWhenIdling || 0);
        const bTotal = (b.fuelLossWhenIdling || 0) + (b.fuelIncreaseWhenIdling || 0);
        return aTotal - bTotal;
      },
      render: (_, record) => {
        if (record.type === 'stable') {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        const fuelLoss = record.fuelLossWhenIdling || 0;
        const fuelIncrease = record.fuelIncreaseWhenIdling || 0;
        const hasLoss = fuelLoss > 0.01;
        const hasIncrease = fuelIncrease > 0.01;
        
        if (hasLoss && hasIncrease) {
          return (
            <div style={{ fontSize: '11px' }}>
              <div 
                style={{ color: '#fa8c16', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  setBreakdownData(record.fuelLossBreakdownWhenIdling || []);
                  setBreakdownTitle(`Fuel Loss Breakdown (Idling) - Period CI${(record.periodIndex ?? 0) + 1}`);
                  setBreakdownModalVisible(true);
                }}
              >
                -{fuelLoss.toFixed(2)} L <span style={{ fontSize: '9px' }}>(Consumed)</span>
              </div>
              <div 
                style={{ color: '#52c41a', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  setBreakdownData(record.fuelIncreaseBreakdownWhenIdling || []);
                  setBreakdownTitle(`Fuel Increase Breakdown (Idling) - Period CI${(record.periodIndex ?? 0) + 1}`);
                  setBreakdownModalVisible(true);
                }}
              >
                +{fuelIncrease.toFixed(2)} L <span style={{ fontSize: '9px' }}>(Refuel)</span>
              </div>
            </div>
          );
        } else if (hasLoss) {
          return (
            <span 
              style={{ color: '#fa8c16', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                setBreakdownData(record.fuelLossBreakdownWhenIdling || []);
                setBreakdownTitle(`Fuel Loss Breakdown (Idling) - Period CI${(record.periodIndex ?? 0) + 1}`);
                setBreakdownModalVisible(true);
              }}
            >
              -{fuelLoss.toFixed(2)} L
            </span>
          );
        } else if (hasIncrease) {
          return (
            <span 
              style={{ color: '#52c41a', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                setBreakdownData(record.fuelIncreaseBreakdownWhenIdling || []);
                setBreakdownTitle(`Fuel Increase Breakdown (Idling) - Period CI${(record.periodIndex ?? 0) + 1}`);
                setBreakdownModalVisible(true);
              }}
            >
              +{fuelIncrease.toFixed(2)} L <span style={{ fontSize: '10px' }}>(Refuel)</span>
            </span>
          );
        } else {
          return <span style={{ color: '#999' }}>0.00 L</span>;
        }
      }
    },
    {
      title: 'Distance (km)',
      key: 'distance',
      width: 120,
      render: (_, record) => {
        const distance = record.distanceKm || 0;
        return distance.toFixed(2);
      },
      sorter: (a, b) => (a.distanceKm || 0) - (b.distanceKm || 0)
    },
    {
      title: 'Average Speed (KMPH)',
      key: 'averageSpeed',
      width: 150,
      render: (_, record) => {
        const distance = record.distanceKm || 0;
        const ignitionHours = record.ignitionHours || 0;
        if (ignitionHours > 0 && distance > 0) {
          const avgSpeed = distance / ignitionHours;
          return avgSpeed.toFixed(2);
        }
        return <span style={{ color: '#999' }}>N/A</span>;
      },
      sorter: (a, b) => {
        const aSpeed = (a.ignitionHours > 0 && a.distanceKm > 0) ? (a.distanceKm / a.ignitionHours) : 0;
        const bSpeed = (b.ignitionHours > 0 && b.distanceKm > 0) ? (b.distanceKm / b.ignitionHours) : 0;
        return aSpeed - bSpeed;
      }
    },
    {
      title: `Fuel Change (First ${(fuelChangeIntervalSeconds / 60).toFixed(1)} min)`,
      key: 'firstIntervalFuelChange',
      width: 200,
      render: (_, record) => {
        if (record.firstIntervalFuelChange === null || record.firstIntervalFuelChange === undefined) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        const change = record.firstIntervalFuelChange;
        const isLoss = change > 0;
        const isGain = change < 0;
        return (
          <span style={{ 
            color: isLoss ? '#ff4d4f' : isGain ? '#52c41a' : '#333',
            fontWeight: 'bold'
          }}>
            {isLoss ? '-' : isGain ? '+' : ''}{Math.abs(change).toFixed(2)} L
          </span>
        );
      },
      sorter: (a, b) => {
        const aValue = a.firstIntervalFuelChange ?? 0;
        const bValue = b.firstIntervalFuelChange ?? 0;
        return aValue - bValue;
      }
    },
    {
      title: `Fuel Change (Last ${(fuelChangeIntervalSeconds / 60).toFixed(1)} min)`,
      key: 'lastIntervalFuelChange',
      width: 200,
      render: (_, record) => {
        if (record.lastIntervalFuelChange === null || record.lastIntervalFuelChange === undefined) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        const change = record.lastIntervalFuelChange;
        const isLoss = change > 0;
        const isGain = change < 0;
        return (
          <span style={{ 
            color: isLoss ? '#ff4d4f' : isGain ? '#52c41a' : '#333',
            fontWeight: 'bold'
          }}>
            {isLoss ? '-' : isGain ? '+' : ''}{Math.abs(change).toFixed(2)} L
          </span>
        );
      },
      sorter: (a, b) => {
        const aValue = a.lastIntervalFuelChange ?? 0;
        const bValue = b.lastIntervalFuelChange ?? 0;
        return aValue - bValue;
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <style>{`
        .stable-period-row {
          background-color: #e6f7ff !important;
        }
        .consumption-interval-row {
          background-color: #fff7e6 !important;
        }
        .stable-period-row:hover {
          background-color: #bae7ff !important;
        }
        .consumption-interval-row:hover {
          background-color: #ffe7ba !important;
        }
      `}</style>
      <Card
        title="Stable Fuel Period Consumption Analysis"
      >
        {loading && <Spin size="large" />}
        
        {errors.length > 0 && (
          <Alert
            message="Errors"
            description={errors.map((e, i) => <div key={i}>{e}</div>)}
            type="error"
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Data Input Section */}
        <Card title="Data Input" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <div>
                <label>Asset Code <span style={{ color: 'red' }}>*</span>:</label>
                <Select
                  showSearch
                  placeholder="Search by Asset Code, Owner Code, or Alt Name"
                  value={assetCode || undefined}
                  onChange={(value) => setAssetCode(value)}
                  style={{ marginTop: 4, width: '100%' }}
                  loading={loadingAssets}
                  allowClear
                  filterOption={(input, option) => {
                    // If no input, show all options
                    if (!input || String(input).trim() === '') {
                      return true;
                    }
                    
                    const searchText = String(input).toLowerCase().trim();
                    
                    // Get the display text from the option
                    const displayText = option.children || option.props?.children || '';
                    const displayTextLower = String(displayText).toLowerCase();
                    
                    // Get the code value for direct matching
                    const code = option.value || option.props?.value || '';
                    const codeLower = String(code).toLowerCase();
                    
                    // Get asset info from map for additional search fields
                    const assetInfo = assetCodeMap[code] || {};
                    const ownerCode = String(assetInfo.ownerCode || '').toLowerCase();
                    const altName = String(assetInfo.altName || '').toLowerCase();
                    
                    // Search across all fields
                    return displayTextLower.includes(searchText) ||
                           codeLower.includes(searchText) ||
                           ownerCode.includes(searchText) ||
                           altName.includes(searchText);
                  }}
                >
                  {assetListForSearch.map(code => {
                    const assetInfo = assetCodeMap[code];
                    const displayText = assetInfo 
                      ? `${code}${assetInfo.ownerCode ? ` (${assetInfo.ownerCode})` : ''}${assetInfo.altName ? ` - ${assetInfo.altName}` : ''}`
                      : code;
                    return (
                      <Option key={code} value={code}>{displayText}</Option>
                    );
                  })}
                </Select>
                <small style={{ color: '#999' }}>Search by Asset Code, Owner Code, or Alt Name</small>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <label>Start Date/Time (IST) <span style={{ color: 'red' }}>*</span>:</label>
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  value={startDateTime}
                  onChange={(date) => {
                    if (date) {
                      // Ensure the moment is treated as IST and set time to 00:00:00
                      const istMoment = moment.tz(date, 'Asia/Kolkata')
                        .startOf('day'); // Set to 00:00:00
                      
                      setStartDateTime(istMoment);
                      
                      // Automatically set end date to same date with 23:59:59
                      const endMoment = istMoment.clone()
                        .endOf('day'); // Set to 23:59:59
                      setEndDateTime(endMoment);
                      
                      // Clear errors since we're setting valid dates
                      setErrors([]);
                    } else {
                      setStartDateTime(null);
                      // If start date is cleared, also clear end date
                      setEndDateTime(null);
                    }
                  }}
                  style={{ marginTop: 4, width: '100%' }}
                  placeholder="Select start date"
                />
                <small style={{ color: '#999' }}>Start of analysis period (auto-set to 00:00:00)</small>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <label>End Date/Time (IST) <span style={{ color: 'red' }}>*</span>:</label>
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  value={endDateTime}
                  onChange={(date) => {
                    if (date) {
                      // Ensure the moment is treated as IST
                      const istMoment = moment.tz(date, 'Asia/Kolkata');
                      
                      // Validate that end date/time is not less than start date/time
                      if (startDateTime) {
                        if (istMoment.isBefore(startDateTime)) {
                          setErrors(['End date/time cannot be less than start date/time']);
                          return; // Don't update end date if invalid
                        }
                        
                        // Validate 48-hour limit
                        const hoursDiff = istMoment.diff(startDateTime, 'hours', true);
                        if (hoursDiff > 48) {
                          setErrors([`Time difference cannot exceed 48 hours. Current: ${hoursDiff.toFixed(2)} hours`]);
                          return; // Don't update end date if exceeds 48 hours
                        }
                      }
                      
                      setEndDateTime(istMoment);
                      setErrors([]);
                    } else {
                      setEndDateTime(null);
                    }
                  }}
                  style={{ marginTop: 4, width: '100%' }}
                  placeholder="Select end date/time"
                  disabled={!startDateTime}
                  disabledDate={(current) => {
                    if (!startDateTime) return true; // Disable all dates if no start date
                    // Disable dates before start date
                    return current.isBefore(startDateTime, 'day');
                  }}
                  disabledTime={(current) => {
                    if (!startDateTime || !current) {
                      return {};
                    }
                    
                    // If same day as start date, disable times before start time
                    if (current.isSame(startDateTime, 'day')) {
                      const startHour = startDateTime.hour();
                      const startMinute = startDateTime.minute();
                      const startSecond = startDateTime.second();
                      
                      return {
                        disabledHours: () => {
                          const hours = [];
                          for (let i = 0; i < startHour; i++) {
                            hours.push(i);
                          }
                          return hours;
                        },
                        disabledMinutes: (selectedHour) => {
                          if (selectedHour === startHour) {
                            const minutes = [];
                            for (let i = 0; i < startMinute; i++) {
                              minutes.push(i);
                            }
                            return minutes;
                          }
                          return [];
                        },
                        disabledSeconds: (selectedHour, selectedMinute) => {
                          if (selectedHour === startHour && selectedMinute === startMinute) {
                            const seconds = [];
                            for (let i = 0; i <= startSecond; i++) {
                              seconds.push(i);
                            }
                            return seconds;
                          }
                          return [];
                        }
                      };
                    }
                    
                    return {};
                  }}
                />
                <small style={{ color: '#999' }}>End of analysis period (max 48 hours, auto-set to 23:59:59)</small>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ marginTop: 30 }}>
                <Button 
                  type="primary" 
                  onClick={loadData} 
                  loading={loading}
                  disabled={!assetCode || !startDateTime || !endDateTime}
                  block
                >
                  Load Data
                </Button>
              </div>
            </Col>
          </Row>
          {startDateTime && endDateTime && (
            <Row style={{ marginTop: 8 }}>
              <Col span={24}>
                <small style={{ color: '#666' }}>
                  Time difference: {endDateTime.diff(startDateTime, 'hours', true).toFixed(2)} hours
                  {endDateTime.diff(startDateTime, 'hours', true) > 48 && (
                    <span style={{ color: 'red', marginLeft: 8 }}>⚠ Exceeds 48-hour limit</span>
                  )}
                </small>
              </Col>
            </Row>
          )}
        </Card>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <div>
              <label>Stability Threshold (L):</label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={stabilityThreshold}
                onChange={(e) => setStabilityThreshold(parseFloat(e.target.value) || 0.1)}
                style={{ marginTop: 4 }}
              />
              <small style={{ color: '#999' }}>Max fuel change for stable period (min: 0.01)</small>
            </div>
          </Col>
          <Col span={6}>
            <div>
              <label>Min Stable Duration (sec):</label>
              <Input
                type="number"
                step="10"
                value={minStableDuration}
                onChange={(e) => setMinStableDuration(parseInt(e.target.value) || 60)}
                style={{ marginTop: 4 }}
              />
              <small style={{ color: '#999' }}>Minimum duration for stable period</small>
            </div>
          </Col>
          <Col span={6}>
            <div>
              <label>Fuel Change Interval (min):</label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                value={fuelChangeIntervalSeconds / 60}
                onChange={(e) => {
                  const minutes = parseFloat(e.target.value) || 2.5;
                  setFuelChangeIntervalSeconds(minutes * 60);
                }}
                style={{ marginTop: 4 }}
              />
              <small style={{ color: '#999' }}>Interval for fuel change calculation (default: 2.5 min)</small>
            </div>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic
              title="Stable Periods Found"
              value={stablePeriods.length}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Consumption Intervals"
              value={consumptionRates.length}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Data Points"
              value={rawData.length}
            />
          </Col>
        </Row>

        {/* Parent Asset Information */}
        {parentAsset && (
          <Row gutter={16} style={{ marginBottom: 16, padding: '16px', background: '#e6f7ff', borderRadius: '4px', border: '1px solid #91d5ff' }}>
            <Col span={24}>
              <div style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>Asset Information</div>
            </Col>
            <Col span={6}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Asset Code:</strong> {parentAsset['Asset Code'] || parentAsset.assetCode || parentAsset.asset_code || 'N/A'}
              </div>
            </Col>
            <Col span={6}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Owner Code:</strong> {parentAsset['Owner Code'] || parentAsset.ownerCode || parentAsset.owner_code || 'N/A'}
              </div>
            </Col>
            <Col span={6}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Alt Name:</strong> {parentAsset['Alt Name'] || parentAsset.altName || parentAsset.alt_name || 'N/A'}
              </div>
            </Col>
            <Col span={6} style={{ marginTop: '8px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Asset Model:</strong> {assetDisplayInfo.assetModel}
              </div>
            </Col>
            <Col span={6} style={{ marginTop: '8px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Asset Sub Type:</strong> {assetDisplayInfo.assetSubType}
              </div>
            </Col>
            <Col span={6} style={{ marginTop: '8px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Location:</strong> {assetDisplayInfo.location}
              </div>
            </Col>
            <Col span={6} style={{ marginTop: '8px' }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Sub Location:</strong> {assetDisplayInfo.subLocation}
              </div>
            </Col>
            {(resolvedStandardRates.kmsPerLtr != null || resolvedStandardRates.ltrPerHour != null) && (
              <Col span={6} style={{ marginTop: '8px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Standard Consumption Rate:</strong>{' '}
                  {[
                    resolvedStandardRates.kmsPerLtr != null ? `${resolvedStandardRates.kmsPerLtr.toFixed(2)} KM/L` : null,
                    resolvedStandardRates.ltrPerHour != null ? `${resolvedStandardRates.ltrPerHour.toFixed(2)} L/HOURS` : null
                  ].filter(Boolean).join(' | ')}
                </div>
              </Col>
            )}
            {resolvedStandardRates.kmsPerLtr == null && resolvedStandardRates.ltrPerHour == null && (
              <Col span={12} style={{ marginTop: '8px' }}>
                <div style={{ marginBottom: '8px', color: '#d46b08' }}>
                  <strong>Standard Consumption Rate:</strong> No standard consumption rate defined in asset model
                </div>
              </Col>
            )}
            {fuelMileageThreshold && (
              <Col span={6} style={{ marginTop: '8px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Fuel Mileage Threshold:</strong> {fuelMileageThresholdText}
                </div>
              </Col>
            )}
          </Row>
        )}

        {(() => {
          // Calculate totals from all combined data
          if (!combinedTableData || combinedTableData.length === 0) return null;
          
          const totals = combinedTableData.reduce((acc, record) => {
            // Sum all fuel consumed values (both positive consumption and negative refueling)
            // fuelConsumed is positive for consumption, negative for refueling
            acc.totalFuelConsumedFromTable += record.fuelConsumed || 0;
            
            // Distance: count from both stable periods and consumption intervals
            acc.totalDistance += record.distanceKm || 0;
            
            acc.totalIgnitionHours += record.ignitionHours || 0;
            acc.totalIdleHours += record.idleHours || 0;
            acc.totalDuration += record.timeDurationHours || 0;
            return acc;
          }, {
            totalFuelConsumedFromTable: 0,
            totalIgnitionHours: 0,
            totalIdleHours: 0,
            totalDistance: 0,
            totalDuration: 0
          });

          // Verify: Fuel consumed from table should equal (first fuel level - last fuel level)
          let verificationFuelConsumed = 0;
          if (originalRawData && originalRawData.length > 0) {
            const firstFuelLevel = parseFloat(originalRawData[0].fuelLevelLiters) || 0;
            const lastFuelLevel = parseFloat(originalRawData[originalRawData.length - 1].fuelLevelLiters) || 0;
            verificationFuelConsumed = firstFuelLevel - lastFuelLevel;
          }

          // Calculate detected refueling from original raw data
          // Check flag_to_check field for "Possible Refueling" or "Possible Refueling *"
          // Sum fuel_cons_ak for those records (values are negative, convert to positive)
          let totalDetectedRefuel = 0;
          if (originalRawData && originalRawData.length > 0) {
            originalRawData.forEach(row => {
              const flagToCheck = row.flag_to_check || '';
              if (flagToCheck && (flagToCheck.includes('Possible Refueling'))) {
                const fuelConsAk = parseFloat(row.fuel_cons_ak) || 0;
                // fuel_cons_ak is negative for refueling, convert to positive
                totalDetectedRefuel += Math.abs(fuelConsAk);
              }
            });
          }

          // Actual total fuel consumed = table fuel consumed + detected refuel (as positive values)
          const actualTotalFuelConsumed = totals.totalFuelConsumedFromTable + totalDetectedRefuel;

          // Calculate consumption rates based on actual total
          const totalLtrPerHour = totals.totalIgnitionHours > 0 && actualTotalFuelConsumed > 0
            ? actualTotalFuelConsumed / totals.totalIgnitionHours
            : null;
          const totalKmsPerLtr = actualTotalFuelConsumed > 0 && totals.totalDistance > 0
            ? totals.totalDistance / actualTotalFuelConsumed
            : null;

          const parsedStandard = resolvedStandardRates;

          const ltrPerHourComparison = buildConsumptionComparison(
            totalLtrPerHour,
            parsedStandard.ltrPerHour,
            'L/HOURS'
          );
          const kmsPerLtrComparison = buildConsumptionComparison(
            totalKmsPerLtr,
            parsedStandard.kmsPerLtr,
            'KMS/L'
          );

          return (
            <Row gutter={16} style={{ marginBottom: 16, padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
              <Col span={24}>
                <div style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Summary Totals</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Fuel Leakage Detection Status Button */}
                    {leakageDetectionLoading ? (
                      <Button loading disabled>
                        Analyzing for Fuel Leakage...
                      </Button>
                    ) : leakageDetectionResults ? (
                      <Button 
                        onClick={handleViewLeakageResults}
                        style={{ 
                          backgroundColor: (() => {
                            const leakageEvents = (leakageDetectionResults.summary?.confirmed_leakage || 0) + 
                                               (leakageDetectionResults.summary?.possible_leakage || 0);
                            return leakageEvents > 0 ? '#ff4d4f' : '#52c41a';
                          })(),
                          borderColor: (() => {
                            const leakageEvents = (leakageDetectionResults.summary?.confirmed_leakage || 0) + 
                                               (leakageDetectionResults.summary?.possible_leakage || 0);
                            return leakageEvents > 0 ? '#ff4d4f' : '#52c41a';
                          })(),
                          color: 'white'
                        }}
                      >
                        {(() => {
                          const confirmedLeakage = leakageDetectionResults.summary?.confirmed_leakage || 0;
                          const possibleLeakage = leakageDetectionResults.summary?.possible_leakage || 0;
                          const totalLeakageEvents = confirmedLeakage + possibleLeakage;
                          
                          if (totalLeakageEvents === 0) {
                            return '✓ No Fuel Leakage Detected';
                          } else if (totalLeakageEvents === 1) {
                            return `⚠ 1 Fuel Leakage Event Detected`;
                          } else {
                            return `⚠ ${totalLeakageEvents} Fuel Leakage Events Detected`;
                          }
                        })()}
                      </Button>
                    ) : (
                      <Button disabled style={{ color: '#999' }}>
                        Load Data to Analyze
                      </Button>
                    )}
                    <Button 
                      type="primary" 
                      icon="⬇"
                      onClick={() => {
                        if (originalRawData && originalRawData.length > 0) {
                          const filename = `complete_raw_data_${new Date().toISOString().split('T')[0]}.csv`;
                          downloadCSV(originalRawData, filename);
                        }
                      }}
                      disabled={!originalRawData || originalRawData.length === 0}
                    >
                      Download Complete Raw Data
                    </Button>
                  </div>
                </div>
                {parsedStandard.kmsPerLtr == null && parsedStandard.ltrPerHour == null && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 6, marginBottom: 6 }}
                    message="No standard consumption rate defined in asset model for this asset."
                  />
                )}
              </Col>
              <Col span={6}>
                <Statistic
                  title="Fuel Consumed (Table)"
                  value={totals.totalFuelConsumedFromTable.toFixed(2)}
                  suffix="L"
                  valueStyle={{ color: '#ff4d4f' }}
                />
                {verificationFuelConsumed !== 0 && (
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    (Check: {verificationFuelConsumed.toFixed(2)}L)
                  </div>
                )}
              </Col>
              <Col span={6}>
                <Statistic
                  title="Refuel Detected"
                  value={totalDetectedRefuel.toFixed(2)}
                  suffix="L"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Actual Total Fuel Consumed"
                  value={actualTotalFuelConsumed.toFixed(2)}
                  suffix="L"
                  valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Ignition Hours"
                  value={totals.totalIgnitionHours.toFixed(2)}
                  suffix="hrs"
                />
              </Col>
              <Col span={6} style={{ marginTop: '12px' }}>
                <Statistic
                  title="Total Distance"
                  value={totals.totalDistance.toFixed(2)}
                  suffix="km"
                />
              </Col>
              <Col span={6} style={{ marginTop: '12px' }}>
                <Statistic
                  title="Consumption Rate (L/Hr)"
                  value={totalLtrPerHour != null ? totalLtrPerHour.toFixed(2) : 'N/A'}
                  suffix={totalLtrPerHour != null ? 'L/hr' : ''}
                  valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                />
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  <div>
                    <strong>Standard:</strong>{' '}
                    {parsedStandard.ltrPerHour != null ? `${parsedStandard.ltrPerHour.toFixed(2)} L/hr` : 'N/A'}
                  </div>
                  <div style={{ color: ltrPerHourComparison?.color || '#8c8c8c' }}>
                    <strong>Difference:</strong>{' '}
                    {ltrPerHourComparison
                      ? `${ltrPerHourComparison.diffAbsolute >= 0 ? '+' : ''}${ltrPerHourComparison.diffAbsolute.toFixed(2)} L/hr (${ltrPerHourComparison.variancePercent >= 0 ? '+' : ''}${ltrPerHourComparison.variancePercent.toFixed(1)}%) ${ltrPerHourComparison.isWorse ? 'Worse' : ltrPerHourComparison.isBetter ? 'Better' : 'At Par'}`
                      : 'N/A'}
                  </div>
                </div>
              </Col>
              <Col span={6} style={{ marginTop: '12px' }}>
                <Statistic
                  title="Consumption Rate (Kms/Ltr)"
                  value={totalKmsPerLtr != null ? totalKmsPerLtr.toFixed(2) : 'N/A'}
                  suffix={totalKmsPerLtr != null ? 'km/L' : ''}
                  valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                />
                <div style={{ fontSize: 11, marginTop: 4 }}>
                  <div>
                    <strong>Standard:</strong>{' '}
                    {parsedStandard.kmsPerLtr != null ? `${parsedStandard.kmsPerLtr.toFixed(2)} km/L` : 'N/A'}
                  </div>
                  <div style={{ color: kmsPerLtrComparison?.color || '#8c8c8c' }}>
                    <strong>Difference:</strong>{' '}
                    {kmsPerLtrComparison
                      ? `${kmsPerLtrComparison.diffAbsolute >= 0 ? '+' : ''}${kmsPerLtrComparison.diffAbsolute.toFixed(2)} km/L (${kmsPerLtrComparison.variancePercent >= 0 ? '+' : ''}${kmsPerLtrComparison.variancePercent.toFixed(1)}%) ${kmsPerLtrComparison.isWorse ? 'Worse' : kmsPerLtrComparison.isBetter ? 'Better' : 'At Par'}`
                      : 'N/A'}
                  </div>
                </div>
              </Col>
              <Col span={6} style={{ marginTop: '12px' }}>
                <Statistic
                  title="Average Speed"
                  value={totals.totalIgnitionHours > 0 && totals.totalDistance > 0 
                    ? (totals.totalDistance / totals.totalIgnitionHours).toFixed(2) 
                    : 'N/A'}
                  suffix={totals.totalIgnitionHours > 0 && totals.totalDistance > 0 ? 'KMPH' : ''}
                />
              </Col>
            </Row>
          );
        })()}

        <Tabs defaultActiveKey="table">
          <TabPane tab="Table View" key="table">
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Select
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ width: 200 }}
                placeholder="Filter by type"
              >
                <Select.Option value="all">All Types</Select.Option>
                <Select.Option value="stable">Stable Periods Only</Select.Option>
                <Select.Option value="consumption">Consumption Intervals Only</Select.Option>
              </Select>
              <Button
                type="default"
                icon="download"
                onClick={() => {
                  const filtered = combinedTableData.filter(record => {
                    if (typeFilter === 'all') return true;
                    return record.type === typeFilter;
                  });
                  convertTableToCSV(columns, filtered, 'stable_fuel_consumption_analysis');
                }}
                disabled={!combinedTableData || combinedTableData.length === 0}
              >
                Download CSV
              </Button>
            </div>
            <div style={{ position: 'relative' }}>
              <style>{`
                .ant-table-footer {
                  padding: 0 !important;
                }
                .ant-table-footer > div {
                  overflow-x: auto;
                  width: 100%;
                }
                .ant-table-footer table {
                  width: 1580px;
                  min-width: 100%;
                }
                .ant-table-body {
                  overflow-x: auto !important;
                }
                .ant-table-footer > div::-webkit-scrollbar {
                  height: 8px;
                }
                .ant-table-body::-webkit-scrollbar {
                  height: 8px;
                }
              `}</style>
            <Table
              columns={columns}
              dataSource={(() => {
                const filtered = combinedTableData.filter(record => {
                  if (typeFilter === 'all') return true;
                  return record.type === typeFilter;
                });
                return filtered;
              })()}
              rowKey={(record, index) => `${record.type}_${index}`}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onShowSizeChange: (current, size) => {
                  setPageSize(size);
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`
              }}
              scroll={{ x: 2320 }}
              size="small"
              rowClassName={(record) => {
                return record.type === 'stable' ? 'stable-period-row' : 'consumption-interval-row';
              }}
            />
            </div>
          </TabPane>
          <TabPane tab="Graph View" key="graph">
            <Card title="Timeline: Stable Periods & Consumption Intervals" style={{ marginBottom: 16 }}>
              <TimelineChart
                stablePeriods={stablePeriods}
                consumptionRates={consumptionRates}
                rawData={rawData}
                width={Math.max(1200, stablePeriods.length * 20)}
                height={500}
              />
              <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>Legend:</strong>
                  <span style={{ marginLeft: 16, paddingLeft: 20, background: '#e6f7ff', borderLeft: '3px solid #91d5ff' }}>
                    Light Blue = Stable Periods (fuel level stable)
                  </span>
                  <span style={{ marginLeft: 16, paddingLeft: 20, background: '#1890ff', opacity: 0.2, borderLeft: '3px solid #1890ff' }}>
                    Blue/Green/Red = Consumption Intervals (between stable periods)
                  </span>
                  <span style={{ marginLeft: 16 }}>
                    <span style={{ color: '#1890ff' }}>●</span> Ltr/Hr
                  </span>
                  <span style={{ marginLeft: 16 }}>
                    <span style={{ color: '#52c41a' }}>●</span> Kms/Ltr
                  </span>
                </div>
              </div>
            </Card>

            <Card title="Fuel Consumed per Consumption Interval" style={{ marginBottom: 16 }}>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <SimpleBarChart
                  width={Math.max(800, consumptionRates.length * 60)}
                  height={300}
                  yLabel="Fuel Consumed (Liters)"
                  data={consumptionRates.map((rate, index) => ({
                    label: `${formatIST(rate.consumptionIntervalStart).substring(11, 16)}-${formatIST(rate.consumptionIntervalEnd).substring(11, 16)}`,
                    value: rate.fuelConsumed,
                    time: formatIST(rate.consumptionIntervalEnd)
                  }))}
                  getColor={() => '#ff4d4f'}
                />
              </div>
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* Raw Data Modal */}
      <Modal
        title={rawDataModalTitle}
        visible={rawDataModalVisible}
        onCancel={() => setRawDataModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRawDataModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1200}
      >
        {filteredRawData.length > 0 ? (
          <Table
            dataSource={filteredRawData}
            rowKey={(record, index) => `raw_${index}`}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            scroll={{ x: 1500, y: 500 }}
            size="small"
            columns={Object.keys(filteredRawData[0] || {}).map(key => ({
              title: key,
              dataIndex: key,
              key: key,
              width: 150,
              render: (value) => {
                if (key === 'timestamp' || key.includes('time') || key.includes('Time')) {
                  return formatIST(new Date(value).getTime());
                }
                if (typeof value === 'number') {
                  return value.toFixed(2);
                }
                return String(value);
              }
            }))}
          />
        ) : (
          <div>No raw data found for this period.</div>
        )}
      </Modal>

      {/* Breakdown Modal */}
      <Modal
        title={breakdownTitle}
        visible={breakdownModalVisible}
        onCancel={() => setBreakdownModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setBreakdownModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {breakdownData && breakdownData.length > 0 ? (
          <Table
            dataSource={breakdownData.map((item, idx) => ({ ...item, key: idx }))}
            columns={[
              {
                title: 'Start Time (IST)',
                key: 'startTime',
                dataIndex: 'startTime',
                render: (time) => formatIST(time)
              },
              {
                title: 'End Time (IST)',
                key: 'endTime',
                dataIndex: 'endTime',
                render: (time) => formatIST(time)
              },
              {
                title: 'Start Fuel (L)',
                key: 'startFuel',
                dataIndex: 'startFuel',
                render: (fuel) => fuel.toFixed(2)
              },
              {
                title: 'End Fuel (L)',
                key: 'endFuel',
                dataIndex: 'endFuel',
                render: (fuel) => fuel.toFixed(2)
              },
              {
                title: 'Fuel Change (L)',
                key: 'fuelChange',
                dataIndex: 'fuelChange',
                render: (change) => (
                  <span style={{ fontWeight: 'bold', color: change > 0 ? '#ff4d4f' : '#52c41a' }}>
                    {change > 0 ? '-' : '+'}{change.toFixed(2)}
                  </span>
                )
              },
              {
                title: 'Duration',
                key: 'duration',
                render: (_, record) => {
                  const duration = (record.endTime - record.startTime) / (1000 * 60 * 60);
                  if (duration < 1) {
                    return `${Math.round(duration * 60)} min`;
                  }
                  return `${duration.toFixed(2)} hrs`;
                }
              }
            ]}
            pagination={{ pageSize: 10 }}
            size="small"
          />
        ) : (
          <div>No breakdown data available</div>
        )}
      </Modal>

      {/* Fuel Leakage Detection Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#ff4d4f', fontSize: '20px' }}>⚠️</span>
            <span>Fuel Leakage Detection Results</span>
            {leakageDetectionResults && (
              <Tag color={leakageDetectionResults.summary.confirmed_leakage > 0 || leakageDetectionResults.summary.possible_leakage > 0 ? 'red' : 'green'}>
                {leakageDetectionResults.summary.total_events} Event(s) Detected
              </Tag>
            )}
          </div>
        }
        visible={leakageDetectionModalVisible}
        onCancel={() => setLeakageDetectionModalVisible(false)}
        footer={[
          <Button 
            key="toggle-chart" 
            onClick={() => setShowLeakageDebugChart(!showLeakageDebugChart)}
          >
            {showLeakageDebugChart ? 'Hide Debug Chart' : 'Show Debug Chart'}
          </Button>,
          <Button
            key="export-image"
            onClick={exportLeakageResultsImage}
            loading={exportingLeakageImage}
            disabled={!leakageDetectionResults}
          >
            Export Results (Image)
          </Button>,
          <Button 
            key="download" 
            type="primary"
            onClick={() => {
              if (leakageDetectionResults) {
                const filename = `fuel_leakage_detection_${assetCode}_${new Date().toISOString().split('T')[0]}.json`;
                const blob = new Blob([JSON.stringify(leakageDetectionResults, null, 2)], { type: 'application/json' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                link.click();
              }
            }}
            disabled={!leakageDetectionResults}
          >
            Download Results (JSON)
          </Button>,
          <Button key="close" onClick={() => setLeakageDetectionModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1400}
      >
        {leakageDetectionResults && (
          <div ref={leakageExportRef} style={{ background: '#ffffff', padding: '4px' }}>
            <div style={{ marginBottom: 10, padding: '10px 12px', border: '1px solid #d9d9d9', borderRadius: 4, background: '#fafafa' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#262626', lineHeight: 1.2 }}>
                Fuel Leakage Detection Report
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Row gutter={16} style={{ marginBottom: 10, padding: '14px', background: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                <Col span={24}>
                  <div style={{ marginBottom: 10, fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>Asset Information</div>
                </Col>
                <Col span={6}><div><strong>Asset Code:</strong> {selectedAssetInfo.assetCode}</div></Col>
                <Col span={6}><div><strong>Owner Code:</strong> {selectedAssetInfo.ownerCode}</div></Col>
                <Col span={6}><div><strong>Alt Name:</strong> {selectedAssetInfo.altName}</div></Col>
                <Col span={6}><div><strong>Asset Model:</strong> {assetDisplayInfo.assetModel}</div></Col>
                <Col span={6} style={{ marginTop: 8 }}><div><strong>Asset Sub Type:</strong> {assetDisplayInfo.assetSubType}</div></Col>
                <Col span={6} style={{ marginTop: 8 }}><div><strong>Location:</strong> {assetDisplayInfo.location}</div></Col>
                <Col span={6} style={{ marginTop: 8 }}><div><strong>Sub Location:</strong> {assetDisplayInfo.subLocation}</div></Col>
                <Col span={6} style={{ marginTop: 8 }}>
                  <div>
                    <strong>Standard Consumption Rate:</strong>{' '}
                    {[
                      resolvedStandardRates.kmsPerLtr != null ? `${resolvedStandardRates.kmsPerLtr.toFixed(2)} KM/L` : null,
                      resolvedStandardRates.ltrPerHour != null ? `${resolvedStandardRates.ltrPerHour.toFixed(2)} L/HOURS` : null
                    ].filter(Boolean).join(' | ') || 'No standard consumption rate defined in asset model'}
                  </div>
                </Col>
                <Col span={12} style={{ marginTop: 8 }}>
                  <div><strong>Fuel Mileage Threshold:</strong> {fuelMileageThresholdText}</div>
                </Col>
              </Row>

              {summarySnapshot && (
                <Row gutter={16} style={{ marginBottom: 10, padding: '14px', background: '#f5f5f5', borderRadius: 4 }}>
                  <Col span={24}>
                    <div style={{ marginBottom: 10, fontSize: 16, fontWeight: 'bold' }}>
                      Summary Totals (
                      Query Date Range: {startDateTime ? moment(startDateTime).format('DD-MM-YYYY HH:mm:ss') : 'N/A'} to {endDateTime ? moment(endDateTime).format('DD-MM-YYYY HH:mm:ss') : 'N/A'}
                      )
                    </div>
                  </Col>
                  <Col span={6}>
                    <div><strong>Fuel Consumed (Table):</strong> {summarySnapshot.totals.totalFuelConsumedFromTable.toFixed(2)} L</div>
                    <div style={{ color: '#8c8c8c' }}>(Check: {summarySnapshot.verificationFuelConsumed.toFixed(2)}L)</div>
                  </Col>
                  <Col span={6}><div><strong>Refuel Detected:</strong> {summarySnapshot.totalDetectedRefuel.toFixed(2)} L</div></Col>
                  <Col span={6}><div><strong>Actual Total Fuel Consumed:</strong> {summarySnapshot.actualTotalFuelConsumed.toFixed(2)} L</div></Col>
                  <Col span={6}><div><strong>Total Ignition Hours:</strong> {summarySnapshot.totals.totalIgnitionHours.toFixed(2)} hrs</div></Col>
                  <Col span={6} style={{ marginTop: 8 }}><div><strong>Total Distance:</strong> {summarySnapshot.totals.totalDistance.toFixed(2)} km</div></Col>
                  <Col span={6} style={{ marginTop: 8 }}>
                    <div><strong>Consumption Rate (L/Hr):</strong> {summarySnapshot.totalLtrPerHour != null ? `${summarySnapshot.totalLtrPerHour.toFixed(2)} L/hr` : 'N/A'}</div>
                    <div><strong>Standard:</strong> {resolvedStandardRates.ltrPerHour != null ? `${resolvedStandardRates.ltrPerHour.toFixed(2)} L/hr` : 'N/A'}</div>
                    <div style={{ color: summarySnapshot.ltrPerHourComparison?.color || '#8c8c8c' }}>
                      <strong>Difference:</strong> {summarySnapshot.ltrPerHourComparison
                        ? `${summarySnapshot.ltrPerHourComparison.diffAbsolute >= 0 ? '+' : ''}${summarySnapshot.ltrPerHourComparison.diffAbsolute.toFixed(2)} L/hr (${summarySnapshot.ltrPerHourComparison.variancePercent >= 0 ? '+' : ''}${summarySnapshot.ltrPerHourComparison.variancePercent.toFixed(1)}%) ${summarySnapshot.ltrPerHourComparison.isWorse ? 'Worse' : summarySnapshot.ltrPerHourComparison.isBetter ? 'Better' : 'At Par'}`
                        : 'N/A'}
                    </div>
                  </Col>
                  <Col span={6} style={{ marginTop: 8 }}>
                    <div><strong>Consumption Rate (Kms/Ltr):</strong> {summarySnapshot.totalKmsPerLtr != null ? `${summarySnapshot.totalKmsPerLtr.toFixed(2)} km/L` : 'N/A'}</div>
                    <div><strong>Standard:</strong> {resolvedStandardRates.kmsPerLtr != null ? `${resolvedStandardRates.kmsPerLtr.toFixed(2)} km/L` : 'N/A'}</div>
                    <div style={{ color: summarySnapshot.kmsPerLtrComparison?.color || '#8c8c8c' }}>
                      <strong>Difference:</strong> {summarySnapshot.kmsPerLtrComparison
                        ? `${summarySnapshot.kmsPerLtrComparison.diffAbsolute >= 0 ? '+' : ''}${summarySnapshot.kmsPerLtrComparison.diffAbsolute.toFixed(2)} km/L (${summarySnapshot.kmsPerLtrComparison.variancePercent >= 0 ? '+' : ''}${summarySnapshot.kmsPerLtrComparison.variancePercent.toFixed(1)}%) ${summarySnapshot.kmsPerLtrComparison.isWorse ? 'Worse' : summarySnapshot.kmsPerLtrComparison.isBetter ? 'Better' : 'At Par'}`
                        : 'N/A'}
                    </div>
                  </Col>
                  <Col span={6} style={{ marginTop: 8 }}>
                    <div><strong>Average Speed:</strong> {summarySnapshot.avgSpeed != null ? `${summarySnapshot.avgSpeed.toFixed(2)} KMPH` : 'N/A'}</div>
                  </Col>
                </Row>
              )}
            </div>

            {/* Summary Statistics */}
            <Row gutter={16} style={{ marginBottom: 16, padding: '16px', background: '#fafafa', borderRadius: '4px' }}>
              <Col span={24}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px' }}>Detection Summary</div>
              </Col>
              <Col span={4}>
                <Statistic
                  title="Total Events"
                  value={leakageDetectionResults.summary.total_events}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Confirmed Leakage"
                  value={leakageDetectionResults.summary.confirmed_leakage}
                  valueStyle={{ color: '#ff4d4f', fontWeight: 'bold' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Possible Leakage"
                  value={leakageDetectionResults.summary.possible_leakage}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Unconfirmed"
                  value={leakageDetectionResults.summary.possible_unconfirmed}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Dismissed"
                  value={
                    (leakageDetectionResults.summary.dismissed_tilt_slosh || 0) +
                    (leakageDetectionResults.summary.dismissed_settling || 0) +
                    (leakageDetectionResults.summary.dismissed_sensor_error || 0)
                  }
                  valueStyle={{ color: '#52c41a' }}
                  suffix={
                    [
                      leakageDetectionResults.summary.dismissed_settling > 0
                        ? `${leakageDetectionResults.summary.dismissed_settling} settling`
                        : null,
                      leakageDetectionResults.summary.dismissed_sensor_error > 0
                        ? `${leakageDetectionResults.summary.dismissed_sensor_error} sensor`
                        : null
                    ].filter(Boolean).join(', ')
                  }
                />
              </Col>
                  <Col span={4}>
                    <Statistic
                      title="Total Loss (L)"
                      value={leakageDetectionResults.summary.total_loss_liters?.toFixed(2) || '0'}
                      valueStyle={{ color: '#ff4d4f', fontWeight: 'bold' }}
                  suffix="L"
                />
              </Col>
            </Row>

            {/* Analysis Period Info */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Alert
                  type="info"
                  message={
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>
                        <strong>Analysis Period:</strong>{' '}
                        {leakageDetectionResults.summary.time_range?.start_formatted
                          ? moment(leakageDetectionResults.summary.time_range.start_formatted).format('YYYY-MM-DD HH:mm:ss')
                          : 'N/A'}{' '}
                        to{' '}
                        {leakageDetectionResults.summary.time_range?.end_formatted
                          ? moment(leakageDetectionResults.summary.time_range.end_formatted).format('YYYY-MM-DD HH:mm:ss')
                          : 'N/A'}
                      </span>
                      <span>
                        <strong>Data Points:</strong> {leakageDetectionResults.summary.data_points_analyzed?.toLocaleString() || 0}
                      </span>
                      <span>
                        <strong>Vibe Threshold:</strong> {leakageDetectionResults.summary.vibe_threshold_used || 'N/A'}
                      </span>
                      <span>
                        <strong>Baseline Consumption:</strong>{' '}
                        {leakageDrainMetrics.generalBaselineRateLph != null
                          ? `General ${leakageDrainMetrics.generalBaselineRateLph.toFixed(2)} L/h`
                          : 'General N/A'}{' '}
                        |{' '}
                        {leakageDrainMetrics.activeBaselineRateLph != null
                          ? `Active ${leakageDrainMetrics.activeBaselineRateLph.toFixed(2)} L/h`
                          : 'Active N/A'}
                      </span>
                    </div>
                  }
                />
              </Col>
            </Row>

            {/* Events Table */}
            {leakageDetectionResults.events.length > 0 ? (
              <Table
                dataSource={leakageDetectionResults.events}
                rowKey="event_id"
                size="small"
                scroll={{ x: 'max-content' }}
                pagination={{ pageSize: 10 }}
                columns={[
                  {
                    title: 'ID',
                    dataIndex: 'event_id',
                    key: 'event_id',
                    width: 60,
                    fixed: 'left'
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    width: 160,
                    fixed: 'left',
                    render: (status) => (
                      <Tag color={getLeakageStatusColor(status)} style={{ fontWeight: 'bold' }}>
                        {getLeakageStatusLabel(status)}
                      </Tag>
                    ),
                    filters: [
                      { text: 'Confirmed Leakage', value: EVENT_STATUS.CONFIRMED_LEAKAGE },
                      { text: 'Possible Leakage', value: EVENT_STATUS.POSSIBLE_LEAKAGE },
                      { text: 'Possible Leakage (Sensor Affected)', value: EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED },
                      { text: 'Unconfirmed', value: EVENT_STATUS.POSSIBLE_UNCONFIRMED },
                      { text: 'Dismissed (Tilt/Slosh)', value: EVENT_STATUS.DISMISSED_TILT_SLOSH },
                      { text: 'Dismissed (Settling)', value: EVENT_STATUS.DISMISSED_SETTLING },
                      { text: 'Dismissed (Sensor Error)', value: EVENT_STATUS.DISMISSED_SENSOR_ERROR }
                    ],
                    onFilter: (value, record) => record.status === value
                  },
                  {
                    title: 'Drop Time Range',
                    key: 'drop_time_range',
                    width: 220,
                    render: (_, record) => (
                      <div style={{ lineHeight: '1.3' }}>
                        <div>{moment(record.start_time_formatted).format('YYYY-MM-DD HH:mm:ss')}</div>
                        <div style={{ color: '#8c8c8c' }}>to</div>
                        <div>{moment(record.end_time_formatted).format('YYYY-MM-DD HH:mm:ss')}</div>
                      </div>
                    )
                  },
                  {
                    title: 'Location',
                    dataIndex: 'lat_long',
                    key: 'lat_long',
                    width: 160,
                    render: (latLong) => {
                      if (!latLong) return <span style={{ color: '#999' }}>N/A</span>;
                      const parts = latLong.split(',');
                      if (parts.length !== 2) return <span style={{ color: '#999' }}>{latLong}</span>;
                      const lat = parts[0].trim();
                      const lng = parts[1].trim();
                      const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
                      return (
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                          {parseFloat(lat).toFixed(4)}, {parseFloat(lng).toFixed(4)}
                        </a>
                      );
                    }
                  },
                  {
                    title: 'Fuel Loss (L)',
                    dataIndex: 'loss_liters',
                    key: 'loss_liters',
                    width: 100,
                    render: (loss) => (
                      <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        -{loss.toFixed(2)}L
                      </span>
                    ),
                    sorter: (a, b) => a.loss_liters - b.loss_liters
                  },
                  {
                    title: 'Possible Drain (L)',
                    key: 'possible_drain_liters',
                    width: 130,
                    render: (_, record) => {
                      const metric = leakageDrainMetrics.eventMetrics[record.event_id];
                      if (!metric || metric.possibleDrain == null) return <span style={{ color: '#999' }}>N/A</span>;
                      return (
                        <span style={{ color: '#cf1322', fontWeight: 'bold' }}>
                          {metric.possibleDrain.toFixed(2)}L
                        </span>
                      );
                    },
                    sorter: (a, b) => {
                      const aVal = leakageDrainMetrics.eventMetrics[a.event_id]?.possibleDrain ?? -1;
                      const bVal = leakageDrainMetrics.eventMetrics[b.event_id]?.possibleDrain ?? -1;
                      return aVal - bVal;
                    }
                  },
                  {
                    title: 'Expected Cons. (L)',
                    key: 'expected_loss_liters',
                    width: 130,
                    render: (_, record) => {
                      const metric = leakageDrainMetrics.eventMetrics[record.event_id];
                      if (!metric || metric.expectedLoss == null) return <span style={{ color: '#999' }}>N/A</span>;
                      return (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto' }}
                          onClick={() => openConsumptionCalculationModal(record)}
                        >
                          {metric.expectedLoss.toFixed(2)}L
                        </Button>
                      );
                    }
                  },
                  {
                    title: 'Pre-Level (L)',
                    dataIndex: 'pre_level_liters',
                    key: 'pre_level_liters',
                    width: 100,
                    render: (level) => level.toFixed(2)
                  },
                  {
                    title: 'Post-Level (L)',
                    dataIndex: 'post_level_liters',
                    key: 'post_level_liters',
                    width: 100,
                    render: (level) => level.toFixed(2)
                  },
                  {
                    title: 'Confidence',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 100,
                    render: (conf) => {
                      const percent = (conf * 100).toFixed(0);
                      let color = '#52c41a';
                      if (percent >= 70) color = '#ff4d4f';
                      else if (percent >= 50) color = '#fa8c16';
                      return (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          background: `linear-gradient(to right, ${color}22 ${percent}%, transparent ${percent}%)`,
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}>
                          <span style={{ color, fontWeight: 'bold' }}>{percent}%</span>
                        </div>
                      );
                    },
                    sorter: (a, b) => a.confidence - b.confidence
                  },
                  {
                    title: 'Recovery Type',
                    dataIndex: ['evidence', 'opportunity_type'],
                    key: 'opportunity_type',
                    width: 100,
                    render: (type) => type ? (
                      <Tag color={type === 'tilt' ? 'blue' : type === 'activity' ? 'orange' : 'green'}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Tag>
                    ) : <span style={{ color: '#999' }}>None</span>
                  },
                  {
                    title: 'Recovery Ratio',
                    dataIndex: ['evidence', 'recovery_ratio'],
                    key: 'recovery_ratio',
                    width: 110,
                    render: (ratio) => ratio != null ? (
                      <span style={{ 
                        color: ratio >= 0.7 ? '#52c41a' : ratio >= 0.2 ? '#fa8c16' : '#ff4d4f',
                        fontWeight: 'bold'
                      }}>
                        {(ratio * 100).toFixed(1)}%
                      </span>
                    ) : 'N/A'
                  },
                  {
                    title: 'Spike Ratio',
                    dataIndex: ['evidence', 'spike_loss_ratio'],
                    key: 'spike_loss_ratio',
                    width: 100,
                    render: (ratio, record) => {
                      if (!record.evidence.has_pre_spike || ratio == null) return <span style={{ color: '#999' }}>-</span>;
                      return (
                        <span style={{ 
                          color: ratio <= 2.0 ? '#52c41a' : '#ff4d4f',
                          fontWeight: 'bold'
                        }}>
                          {ratio.toFixed(1)}x
                        </span>
                      );
                    }
                  },
                  {
                    title: 'Tilt @ Drop',
                    dataIndex: ['evidence', 'tilt_near_drop'],
                    key: 'tilt_near_drop',
                    width: 90,
                    render: (tilt) => tilt ? 
                      <Tag color="orange">Yes</Tag> : 
                      <Tag color="green">No</Tag>
                  },
                  {
                    title: 'Duration',
                    key: 'duration',
                    width: 170,
                    render: (_, record) => {
                      const start = new Date(record.start_time_detected);
                      const end = new Date(record.end_time_detected);
                      const durationMs = end - start;
                      const minutes = Math.round(durationMs / 60000);
                      const durationText = minutes < 60 ? `${minutes} min` : `${(minutes / 60).toFixed(1)} hrs`;

                      const metric = leakageDrainMetrics.eventMetrics[record.event_id];
                      const ignitionPct = metric?.ignitionOnFraction != null ? (metric.ignitionOnFraction * 100) : null;
                      const idlePct = metric?.idleInIgnitionOnFraction != null ? (metric.idleInIgnitionOnFraction * 100) : null;

                      let stateText = 'State: N/A';
                      if (ignitionPct != null) {
                        if (ignitionPct <= 5) {
                          stateText = 'Ignition OFF';
                        } else {
                          const idlePart = idlePct != null ? ` | Idle ${idlePct.toFixed(0)}%` : '';
                          stateText = `Ignition ON ${ignitionPct.toFixed(0)}%${idlePart}`;
                        }
                      }

                      return (
                        <div style={{ lineHeight: 1.3 }}>
                          <div>{durationText}</div>
                          <div style={{ color: '#8c8c8c', fontSize: 11 }}>{stateText}</div>
                        </div>
                      );
                    }
                  }
                ]}
                expandable={{
                  expandedRowRender: (record) => (
                    <div style={{ padding: '12px', background: '#fafafa', borderRadius: '4px' }}>
                      <Row gutter={16}>
                        <Col span={8}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>Event Timeline:</strong>
                          </div>
                          <div style={{ fontSize: '12px' }}>
                            <div>Start: {moment(record.start_time_formatted).format('YYYY-MM-DD HH:mm:ss')}</div>
                            <div>Detected: {moment(record.t_detect_formatted).format('YYYY-MM-DD HH:mm:ss')}</div>
                            <div>End: {moment(record.end_time_formatted).format('YYYY-MM-DD HH:mm:ss')}</div>
                            {record.evidence.t_opportunity_formatted && (
                              <div style={{ color: '#1890ff' }}>
                                Recovery Opportunity: {moment(record.evidence.t_opportunity_formatted).format('YYYY-MM-DD HH:mm:ss')}
                              </div>
                            )}
                          </div>
                        </Col>
                        <Col span={8}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>Evidence Details:</strong>
                          </div>
                          <div style={{ fontSize: '12px' }}>
                            <div>Tilt at Opportunity: {record.evidence.tilt_deg_at_opportunity?.toFixed(2) || 'N/A'}°</div>
                            <div>Vibration at Opportunity: {record.evidence.vibe_at_opportunity?.toFixed(2) || 'N/A'}</div>
                            <div>Recovery Amount: {record.evidence.recovery_amount?.toFixed(2) || 'N/A'} L</div>
                            <div>Filter Lag Used: {record.evidence.filter_lag_used_minutes} min</div>
                            <div>Idle Before Drop: {record.evidence.idle_duration_before_drop != null ? `${record.evidence.idle_duration_before_drop} min` : 'N/A'}</div>
                            <div>
                              Expected Consumption: {leakageDrainMetrics.eventMetrics[record.event_id]?.expectedLoss != null ? (
                                <Button
                                  type="link"
                                  size="small"
                                  style={{ padding: 0, height: 'auto', verticalAlign: 'baseline' }}
                                  onClick={() => openConsumptionCalculationModal(record)}
                                >
                                  {leakageDrainMetrics.eventMetrics[record.event_id].expectedLoss.toFixed(2)}L
                                </Button>
                              ) : 'N/A'}
                            </div>
                            <div>
                              Possible Drain: {leakageDrainMetrics.eventMetrics[record.event_id]?.possibleDrain != null
                                ? `${leakageDrainMetrics.eventMetrics[record.event_id].possibleDrain.toFixed(2)}L`
                                : 'N/A'}
                            </div>
                            <div>
                              Activity Resumed at Drop: {record.evidence.activity_resumed_at_drop ? 
                                <Tag color="orange" style={{ fontSize: '10px' }}>Yes</Tag> : 
                                <Tag color="green" style={{ fontSize: '10px' }}>No</Tag>}
                            </div>
                            <div>
                              Sensor Error Near Drop: {record.evidence.sensor_error_near_drop ? (
                                <Tag color="orange" style={{ fontSize: '10px' }}>
                                  Yes ({record.evidence.sensor_error_packets_near_drop || 0} packets)
                                </Tag>
                              ) : (
                                <Tag color="green" style={{ fontSize: '10px' }}>No</Tag>
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col span={8}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>Confidence Factors:</strong>
                          </div>
                          <div style={{ fontSize: '12px' }}>
                            <div>
                              Tilt Near Drop: {record.evidence.tilt_near_drop ? 
                                <Tag color="orange" style={{ fontSize: '10px' }}>Yes (-confidence)</Tag> : 
                                <Tag color="green" style={{ fontSize: '10px' }}>No (+confidence)</Tag>}
                            </div>
                            <div>
                              Low Vibration at Drop: {record.evidence.low_vibe_at_drop ? 
                                <Tag color="green" style={{ fontSize: '10px' }}>Yes (+confidence)</Tag> : 
                                <Tag color="orange" style={{ fontSize: '10px' }}>No</Tag>}
                            </div>
                            <div>
                              Pre-Drop Spike: {record.evidence.has_pre_spike ? 
                                <Tag color="orange" style={{ fontSize: '10px' }}>Yes (+{record.evidence.pre_spike_amount}L above baseline)</Tag> : 
                                <Tag color="green" style={{ fontSize: '10px' }}>No</Tag>}
                            </div>
                            {record.evidence.stable_baseline != null && (
                              <div>
                                Stable Baseline: {record.evidence.stable_baseline}L
                                {record.evidence.post_vs_baseline_diff != null && 
                                  <span> (post-drop diff: {record.evidence.post_vs_baseline_diff}L)</span>}
                              </div>
                            )}
                            {record.evidence.spike_loss_ratio != null && (
                              <div>
                                Spike Symmetry: {record.evidence.spike_is_symmetric ? 
                                  <Tag color="green" style={{ fontSize: '10px' }}>Symmetric ({record.evidence.spike_loss_ratio}x)</Tag> :
                                  <Tag color="red" style={{ fontSize: '10px' }}>Asymmetric ({record.evidence.spike_loss_ratio}x - refuel suspected)</Tag>}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={16} style={{ marginTop: '8px' }}>
                        <Col span={8}>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>Operational Context:</strong>
                          </div>
                          <div style={{ fontSize: '12px' }}>
                            <div>
                              Fuel Volatility: {record.evidence.pre_drop_volatility != null ? (
                                <Tag color={record.evidence.pre_drop_volatility >= 3 ? 'green' : 'red'} style={{ fontSize: '10px' }}>
                                  {record.evidence.pre_drop_volatility}L stddev {record.evidence.pre_drop_volatility >= 3 ? '(sloshing)' : '(stable)'}
                                </Tag>
                              ) : <span style={{ color: '#999' }}>N/A</span>}
                            </div>
                            <div>
                              Equipment State: {record.evidence.equipment_state === 'working' ? 
                                <Tag color="green" style={{ fontSize: '10px' }}>Working (ign=1, idle=0)</Tag> :
                                record.evidence.equipment_state === 'idle' ?
                                <Tag color="orange" style={{ fontSize: '10px' }}>Idle (ign=1, idle=1)</Tag> :
                                record.evidence.equipment_state === 'off' ?
                                <Tag color="red" style={{ fontSize: '10px' }}>Off (ign=0)</Tag> :
                                <span style={{ color: '#999' }}>N/A</span>}
                            </div>
                            <div>
                              Working Fraction: {record.evidence.working_fraction != null ? 
                                `${Math.round(record.evidence.working_fraction * 100)}%` : 'N/A'}
                            </div>
                            <div>
                              Idle Near Drop: {record.evidence.idle_near_drop ? 
                                <Tag color="red" style={{ fontSize: '10px' }}>Yes (equipment idle/off near drop)</Tag> : 
                                <Tag color="green" style={{ fontSize: '10px' }}>No</Tag>}
                            </div>
                            <div>
                              Near Refuel: {record.evidence.near_refuel ? 
                                <Tag color="orange" style={{ fontSize: '10px' }}>Yes</Tag> : 
                                <Tag color="default" style={{ fontSize: '10px' }}>No</Tag>}
                            </div>
                            <div>
                              Lag Window State: {record.evidence.state_lag_window?.dominantState ? (
                                <Tag color={record.evidence.state_lag_window.dominantState === 'working' ? 'green' : 'orange'} style={{ fontSize: '10px' }}>
                                  {record.evidence.state_lag_window.dominantState}
                                </Tag>
                              ) : <span style={{ color: '#999' }}>N/A</span>}
                            </div>
                            <div>
                              Recent Window State: {record.evidence.state_recent_window?.dominantState ? (
                                <Tag color={record.evidence.state_recent_window.dominantState === 'working' ? 'green' : 'orange'} style={{ fontSize: '10px' }}>
                                  {record.evidence.state_recent_window.dominantState}
                                </Tag>
                              ) : <span style={{ color: '#999' }}>N/A</span>}
                            </div>
                            <div>
                              Context Priority: {record.evidence.theft_possible_context ? (
                                <Tag color="red" style={{ fontSize: '10px' }}>Theft-Possible (off/idle)</Tag>
                              ) : record.evidence.working_context ? (
                                <Tag color="green" style={{ fontSize: '10px' }}>Working-Context</Tag>
                              ) : (
                                <Tag color="default" style={{ fontSize: '10px' }}>Neutral</Tag>
                              )}
                            </div>
                            {Array.isArray(record.evidence.reason_tags) && record.evidence.reason_tags.length > 0 && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ marginBottom: 4 }}><strong>Classification Reasons:</strong></div>
                                {record.evidence.reason_tags.map((reason, idx) => (
                                  <Tag key={`${record.event_id}-reason-${idx}`} style={{ marginBottom: 4 }}>
                                    {reason}
                                  </Tag>
                                ))}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )
                }}
              />
            ) : (
              <Alert
                type="success"
                message="No Fuel Leakage Events Detected"
                description="The analysis did not detect any significant fuel loss events during the selected period that meet the leakage criteria."
                showIcon
              />
            )}

            {/* Debug Chart */}
            {showLeakageDebugChart && leakageDetectionResults.debug_series && (
              <div style={{ marginTop: '24px' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px' }}>
                  Debug Visualization
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <FuelLeakageDebugChart 
                    debugSeries={leakageDetectionResults.debug_series}
                    events={leakageDetectionResults.events}
                    width={1350}
                    height={400}
                  />
                </div>
              </div>
            )}

            {/* Configuration Used */}
            <div style={{ marginTop: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                Detection Configuration Used
              </div>
              <Row gutter={16} style={{ fontSize: '11px' }}>
                <Col span={6}>
                  <div>Min Loss: {leakageDetectionResults.config_used.D_min_liters}L</div>
                  <div>Pre-Window: {leakageDetectionResults.config_used.pre_window_minutes} min</div>
                </Col>
                <Col span={6}>
                  <div>Post-Window: {leakageDetectionResults.config_used.post_window_minutes} min</div>
                  <div>Filter Lag: {leakageDetectionResults.config_used.filter_lag_minutes} min</div>
                </Col>
                <Col span={6}>
                  <div>Tilt Threshold: {leakageDetectionResults.config_used.T_tilt_deg}°</div>
                  <div>Recovery Window: {leakageDetectionResults.config_used.recovery_window_minutes} min</div>
                </Col>
                <Col span={6}>
                  <div>Dismiss Ratio: {(leakageDetectionResults.config_used.recovery_ratio_dismiss * 100).toFixed(0)}%</div>
                  <div>Confirm Ratio: {(leakageDetectionResults.config_used.recovery_ratio_confirm * 100).toFixed(0)}%</div>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>

      {/* Expected Consumption Calculation Modal */}
      <Modal
        title={selectedConsumptionCalc ? `Expected Consumption Calculation - Event ${selectedConsumptionCalc.eventId}` : 'Expected Consumption Calculation'}
        visible={consumptionCalcModalVisible}
        onCancel={() => {
          setConsumptionCalcModalVisible(false);
          setBaselineTrendModalVisible(false);
          setSelectedConsumptionCalc(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setConsumptionCalcModalVisible(false);
              setBaselineTrendModalVisible(false);
              setSelectedConsumptionCalc(null);
            }}
          >
            Close
          </Button>
        ]}
        width={760}
      >
        {selectedConsumptionCalc && (
          <div>
            <Alert
              type="info"
              style={{ marginBottom: 16 }}
              message={
                <div style={{ fontSize: 13 }}>
                  <div><strong>Drop Range:</strong> {moment(selectedConsumptionCalc.startTime).format('YYYY-MM-DD HH:mm:ss')} to {moment(selectedConsumptionCalc.endTime).format('YYYY-MM-DD HH:mm:ss')}</div>
                  <div><strong>Detected Time:</strong> {moment(selectedConsumptionCalc.detectedTime).format('YYYY-MM-DD HH:mm:ss')}</div>
                  <div><strong>Status:</strong> {getLeakageStatusLabel(selectedConsumptionCalc.status)}</div>
                </div>
              }
            />

            <div style={{ padding: '12px 14px', background: '#fafafa', borderRadius: 4, border: '1px solid #f0f0f0' }}>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>Formula</div>
              <div style={{ fontFamily: 'monospace', fontSize: 13 }}>
                Expected (L) = (General Baseline × Ignition-ON Idle Hours) + (Active Baseline × Ignition-ON Non-Idle Hours)
              </div>
            </div>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Statistic
                  title={(
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto' }}
                      disabled={!selectedConsumptionCalc.baselineSeries || (selectedConsumptionCalc.generalBaselineRateLph == null && selectedConsumptionCalc.activeBaselineRateLph == null)}
                      onClick={() => setBaselineTrendModalVisible(true)}
                    >
                      General Baseline
                    </Button>
                  )}
                  value={selectedConsumptionCalc.generalBaselineRateLph?.toFixed(2) || 'N/A'}
                  suffix={selectedConsumptionCalc.generalBaselineRateLph != null ? 'L/h' : ''}
                />
                {selectedConsumptionCalc.baselineSeries?.baselineMethod?.general && (
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    Method: {selectedConsumptionCalc.baselineSeries.baselineMethod.general}
                  </div>
                )}
                {(selectedConsumptionCalc.baselineSeries?.generalBaseline?.windows?.length || 0) > 0 && (
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                    Stable windows used: {selectedConsumptionCalc.baselineSeries.generalBaseline.windows.length}
                  </div>
                )}
              </Col>
              <Col span={8}>
                <Statistic
                  title={(
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto' }}
                      disabled={!selectedConsumptionCalc.baselineSeries || (selectedConsumptionCalc.generalBaselineRateLph == null && selectedConsumptionCalc.activeBaselineRateLph == null)}
                      onClick={() => setBaselineTrendModalVisible(true)}
                    >
                      Active Baseline
                    </Button>
                  )}
                  value={selectedConsumptionCalc.activeBaselineRateLph?.toFixed(2) || 'N/A'}
                  suffix={selectedConsumptionCalc.activeBaselineRateLph != null ? 'L/h' : ''}
                />
                {selectedConsumptionCalc.baselineSeries?.baselineMethod?.active && (
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                    Method: {selectedConsumptionCalc.baselineSeries.baselineMethod.active}
                  </div>
                )}
                {((selectedConsumptionCalc.baselineSeries?.activeBaseline?.windows?.length || 0) > 0) && (
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                    Stable windows used: {selectedConsumptionCalc.baselineSeries.activeBaseline.windows.length}
                  </div>
                )}
              </Col>
              <Col span={8}>
                <Statistic
                  title="Drop Duration (Total)"
                  value={selectedConsumptionCalc.durationHours?.toFixed(3) || 'N/A'}
                  suffix="h"
                />
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Statistic
                  title="Ignition-ON Duration"
                  value={selectedConsumptionCalc.ignitionOnHours != null ? selectedConsumptionCalc.ignitionOnHours.toFixed(3) : 'N/A'}
                  suffix="h"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Ignition-ON Idle Hours"
                  value={selectedConsumptionCalc.ignitionOnIdleHours != null ? selectedConsumptionCalc.ignitionOnIdleHours.toFixed(3) : 'N/A'}
                  suffix="h"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Ignition-ON Non-Idle Hours"
                  value={selectedConsumptionCalc.ignitionOnActiveHours != null ? selectedConsumptionCalc.ignitionOnActiveHours.toFixed(3) : 'N/A'}
                  suffix="h"
                />
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}>
                <Statistic
                  title="Idle in Ignition-ON"
                  value={selectedConsumptionCalc.idleInIgnitionOnFraction != null ? (selectedConsumptionCalc.idleInIgnitionOnFraction * 100).toFixed(1) : 'N/A'}
                  suffix="%"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Non-Idle in Ignition-ON"
                  value={selectedConsumptionCalc.activeInIgnitionOnFraction != null ? (selectedConsumptionCalc.activeInIgnitionOnFraction * 100).toFixed(1) : 'N/A'}
                  suffix="%"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Expected Consumption (Weighted)"
                  value={selectedConsumptionCalc.expectedLoss?.toFixed(2) || 'N/A'}
                  suffix="L"
                  valueStyle={{ color: '#595959', fontWeight: 600 }}
                />
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={8}>
                {selectedConsumptionCalc.ignitionOnHours === 0 && (
                  <Alert
                    type="warning"
                    message="Ignition stayed OFF in this drop interval. Expected consumption is 0."
                    showIcon
                  />
                )}
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Statistic
                  title="Actual Loss"
                  value={selectedConsumptionCalc.actualLoss?.toFixed(2) || 'N/A'}
                  suffix="L"
                  valueStyle={{ color: '#ff4d4f', fontWeight: 700 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Possible Drain (Unexplained)"
                  value={selectedConsumptionCalc.possibleDrain?.toFixed(2) || 'N/A'}
                  suffix="L"
                  valueStyle={{ color: '#cf1322', fontWeight: 700 }}
                />
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      <Modal
        title={selectedConsumptionCalc ? `Baseline Consumption Trend - Event ${selectedConsumptionCalc.eventId}` : 'Baseline Consumption Trend'}
        visible={baselineTrendModalVisible}
        onCancel={() => setBaselineTrendModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setBaselineTrendModalVisible(false)}>
            Close
          </Button>
        ]}
        width={980}
      >
        {selectedConsumptionCalc && (
          <div>
            <Alert
              type="info"
              style={{ marginBottom: 12 }}
              message={
                <div style={{ fontSize: 13 }}>
                  <div>
                    <strong>General Baseline:</strong>{' '}
                    {selectedConsumptionCalc.generalBaselineRateLph != null ? `${selectedConsumptionCalc.generalBaselineRateLph.toFixed(2)} L/h` : 'N/A'}
                  </div>
                  <div>
                    <strong>Active Baseline:</strong>{' '}
                    {selectedConsumptionCalc.activeBaselineRateLph != null ? `${selectedConsumptionCalc.activeBaselineRateLph.toFixed(2)} L/h` : 'N/A'}
                  </div>
                  <div>
                    <strong>Methods:</strong>{' '}
                    General={selectedConsumptionCalc.baselineSeries?.baselineMethod?.general || 'N/A'} | Active={selectedConsumptionCalc.baselineSeries?.baselineMethod?.active || 'N/A'}
                  </div>
                  <div>
                    <strong>Drop Range:</strong>{' '}
                    {moment(selectedConsumptionCalc.startTime).format('YYYY-MM-DD HH:mm:ss')} to {moment(selectedConsumptionCalc.endTime).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                </div>
              }
            />

            <BaselineConsumptionTrendChart
              baselineSeries={selectedConsumptionCalc.baselineSeries}
              generalBaselineRateLph={selectedConsumptionCalc.generalBaselineRateLph}
              activeBaselineRateLph={selectedConsumptionCalc.activeBaselineRateLph}
              dropStartTs={selectedConsumptionCalc.dropStartTs}
              dropEndTs={selectedConsumptionCalc.dropEndTs}
              width={920}
              height={340}
            />

            {((selectedConsumptionCalc.baselineSeries?.generalBaseline?.windows?.length || 0) > 0 || (selectedConsumptionCalc.baselineSeries?.activeBaseline?.windows?.length || 0) > 0) && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Stable Windows Used</div>
                <Table
                  size="small"
                  rowKey={(row, idx) => `${row.startTs}-${idx}`}
                  pagination={false}
                  dataSource={[
                    ...((selectedConsumptionCalc.baselineSeries?.generalBaseline?.windows || []).map(w => ({ ...w, baselineType: 'General' }))),
                    ...((selectedConsumptionCalc.baselineSeries?.activeBaseline?.windows || []).map(w => ({ ...w, baselineType: 'Active' })))
                  ]}
                  columns={[
                    {
                      title: 'Baseline Type',
                      key: 'baselineType',
                      render: (_, row) => row.baselineType
                    },
                    {
                      title: 'Window Start',
                      key: 'startTs',
                      render: (_, row) => moment(row.startTs).format('YYYY-MM-DD HH:mm:ss')
                    },
                    {
                      title: 'Window End',
                      key: 'endTs',
                      render: (_, row) => moment(row.endTs).format('YYYY-MM-DD HH:mm:ss')
                    },
                    {
                      title: 'Duration (min)',
                      key: 'durationMinutes',
                      render: (_, row) => row.durationMinutes != null ? row.durationMinutes.toFixed(1) : 'N/A'
                    },
                    {
                      title: 'Median Rate (L/h)',
                      key: 'medianRateLph',
                      render: (_, row) => row.medianRateLph != null ? row.medianRateLph.toFixed(2) : 'N/A'
                    },
                    {
                      title: 'Spread P10-P90',
                      key: 'spreadRateLph',
                      render: (_, row) => row.spreadRateLph != null ? row.spreadRateLph.toFixed(2) : 'N/A'
                    }
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// Debug Chart Component for Fuel Leakage Visualization
const FuelLeakageDebugChart = ({ debugSeries, events, width = 1200, height = 400 }) => {
  if (!debugSeries || !debugSeries.timestamps || debugSeries.timestamps.length === 0) {
    return <div>No debug data available</div>;
  }

  const margin = { top: 20, right: 80, left: 60, bottom: 40 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const legendWidth = 760;
  const legendHeight = 28;
  const legendGap = 34;
  const extraLegendSpace = legendHeight + legendGap;

  // Scale functions
  const timestamps = debugSeries.timestamps;
  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  const timeRange = maxTime - minTime || 1;

  const scaleX = (t) => ((t - minTime) / timeRange) * chartWidth;

  // Fuel level scale
  const fuelSmooth = debugSeries.fuel_smooth.filter(v => v != null && !isNaN(v));
  const minFuel = Math.min(...fuelSmooth);
  const maxFuel = Math.max(...fuelSmooth);
  const fuelRange = maxFuel - minFuel || 1;
  const scaleYFuel = (v) => chartHeight - ((v - minFuel) / fuelRange) * chartHeight;

  // Vibration scale (secondary axis)
  const vibeValues = debugSeries.vibe.filter(v => v != null && !isNaN(v));
  const maxVibe = Math.max(...vibeValues, 1);
  const scaleYVibe = (v) => chartHeight - (v / maxVibe) * chartHeight * 0.3;

  // Tilt scale
  const tiltValues = debugSeries.tilt_deg.filter(v => v != null && !isNaN(v));
  const maxTilt = Math.max(...tiltValues, 15);
  const scaleYTilt = (v) => chartHeight - (v / maxTilt) * chartHeight * 0.25;

  const consumptionSeries = buildConsumptionRateSeries(debugSeries, events || []);
  const consumptionRates = consumptionSeries.rates;
  const validConsumptionRates = consumptionRates.filter(v => v != null && !isNaN(v));
  const maxConsumption = validConsumptionRates.length > 0
    ? Math.max(...validConsumptionRates, (consumptionSeries.suddenChangeThresholdLph || 1))
    : 1;
  const scaleYConsumption = (v) => chartHeight - (v / maxConsumption) * chartHeight * 0.35;

  // Create paths
  const fuelPath = timestamps.map((t, i) => {
    const y = debugSeries.fuel_smooth[i];
    if (y == null || isNaN(y)) return null;
    return `${i === 0 ? 'M' : 'L'} ${scaleX(t)} ${scaleYFuel(y)}`;
  }).filter(Boolean).join(' ');

  const vibePath = timestamps.map((t, i) => {
    const y = debugSeries.vibe[i];
    if (y == null || isNaN(y)) return null;
    return `${i === 0 ? 'M' : 'L'} ${scaleX(t)} ${scaleYVibe(y)}`;
  }).filter(Boolean).join(' ');

  const tiltPath = timestamps.map((t, i) => {
    const y = debugSeries.tilt_deg[i];
    if (y == null || isNaN(y)) return null;
    return `${i === 0 ? 'M' : 'L'} ${scaleX(t)} ${scaleYTilt(y)}`;
  }).filter(Boolean).join(' ');

  const consumptionPath = timestamps.map((t, i) => {
    const y = consumptionRates[i];
    if (y == null || isNaN(y)) return null;
    return `${i === 0 ? 'M' : 'L'} ${scaleX(t)} ${scaleYConsumption(y)}`;
  }).filter(Boolean).join(' ');

  // Format time for display
  const formatTime = (ts) => {
    return moment(ts).format('HH:mm');
  };

  // Time labels
  const timeLabels = [];
  const labelCount = 12;
  for (let i = 0; i <= labelCount; i++) {
    const time = minTime + (timeRange * i) / labelCount;
    timeLabels.push({
      time,
      x: scaleX(time),
      label: formatTime(time)
    });
  }

  // Event markers
  const eventMarkers = events.map(event => ({
    x: scaleX(event.t_detect),
    status: event.status,
    loss: event.loss_liters,
    confidence: event.confidence
  }));

  return (
    <svg width={width} height={height + extraLegendSpace} style={{ border: '1px solid #e8e8e8', borderRadius: '4px', background: '#fafafa' }}>
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
          const y = chartHeight * (1 - ratio);
          const value = minFuel + (maxFuel - minFuel) * ratio;
          return (
            <g key={ratio}>
              <line x1={0} y1={y} x2={chartWidth} y2={y} stroke="#e8e8e8" strokeWidth={1} />
              <text x={-8} y={y + 4} textAnchor="end" fontSize="10" fill="#666">
                {value.toFixed(0)}L
              </text>
            </g>
          );
        })}

        {/* Time labels */}
        {timeLabels.map((tl, i) => (
          <g key={i}>
            <line x1={tl.x} y1={0} x2={tl.x} y2={chartHeight} stroke="#f0f0f0" strokeWidth={1} strokeDasharray="2,2" />
            <text x={tl.x} y={chartHeight + 15} textAnchor="middle" fontSize="9" fill="#666">
              {tl.label}
            </text>
          </g>
        ))}

        {/* Event markers (vertical bands) */}
        {eventMarkers.map((marker, i) => {
          let fillColor = '#ff4d4f';
          if (
            marker.status === EVENT_STATUS.DISMISSED_TILT_SLOSH ||
            marker.status === EVENT_STATUS.DISMISSED_SETTLING
          ) fillColor = '#52c41a';
          else if (marker.status === EVENT_STATUS.DISMISSED_SENSOR_ERROR) fillColor = '#13c2c2';
          else if (marker.status === EVENT_STATUS.POSSIBLE_LEAKAGE_SENSOR_AFFECTED) fillColor = '#d46b08';
          else if (marker.status === EVENT_STATUS.POSSIBLE_LEAKAGE) fillColor = '#fa8c16';
          else if (marker.status === EVENT_STATUS.POSSIBLE_UNCONFIRMED) fillColor = '#faad14';
          
          return (
            <g key={`event-${i}`}>
              <rect
                x={marker.x - 15}
                y={0}
                width={30}
                height={chartHeight}
                fill={fillColor}
                opacity={0.2}
              />
              <line
                x1={marker.x}
                y1={0}
                x2={marker.x}
                y2={chartHeight}
                stroke={fillColor}
                strokeWidth={2}
                strokeDasharray="4,4"
              />
              <text x={marker.x} y={-5} textAnchor="middle" fontSize="9" fill={fillColor} fontWeight="bold">
                -{marker.loss.toFixed(1)}L
              </text>
            </g>
          );
        })}

        {/* Fuel level line */}
        <path
          d={fuelPath}
          fill="none"
          stroke="#722ed1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Vibration line */}
        <path
          d={vibePath}
          fill="none"
          stroke="#13c2c2"
          strokeWidth="1"
          strokeOpacity={0.7}
        />

        {/* Tilt line */}
        <path
          d={tiltPath}
          fill="none"
          stroke="#eb2f96"
          strokeWidth="1"
          strokeOpacity={0.7}
        />

        {/* Consumption rate line */}
        <path
          d={consumptionPath}
          fill="none"
          stroke="#fa8c16"
          strokeWidth="1.3"
          strokeOpacity={0.9}
        />

        {/* Sudden consumption-change markers */}
        {consumptionSeries.suddenChangeThresholdLph != null && timestamps.map((t, i) => {
          const rate = consumptionRates[i];
          if (rate == null || rate < consumptionSeries.suddenChangeThresholdLph) return null;
          return (
            <circle
              key={`rate-spike-${i}`}
              cx={scaleX(t)}
              cy={scaleYConsumption(rate)}
              r={2.5}
              fill="#cf1322"
              opacity={0.9}
            />
          );
        })}

        {/* Legend (outside chart area, below plot) */}
        <g transform={`translate(${(chartWidth - legendWidth) / 2}, ${chartHeight + legendGap})`}>
          <rect x={0} y={0} width={legendWidth} height={legendHeight} fill="white" stroke="#e8e8e8" rx={4} />
          <line x1={12} y1={14} x2={30} y2={14} stroke="#722ed1" strokeWidth={2} />
          <text x={34} y={17} fontSize="10" fill="#666">Fuel Level (L)</text>

          <line x1={130} y1={14} x2={148} y2={14} stroke="#13c2c2" strokeWidth={1} />
          <text x={152} y={17} fontSize="10" fill="#666">Vibration</text>

          <line x1={232} y1={14} x2={250} y2={14} stroke="#eb2f96" strokeWidth={1} />
          <text x={254} y={17} fontSize="10" fill="#666">Tilt (°)</text>

          <line x1={322} y1={14} x2={340} y2={14} stroke="#fa8c16" strokeWidth={1.3} />
          <text x={344} y={17} fontSize="10" fill="#666">Consumption (L/h)</text>

          <line x1={470} y1={14} x2={488} y2={14} stroke="#ff4d4f" strokeWidth={2} strokeDasharray="4,4" />
          <text x={492} y={17} fontSize="10" fill="#666">Leakage Event</text>

          {(consumptionSeries.generalBaselineRateLph != null || consumptionSeries.activeBaselineRateLph != null) && (
            <text x={596} y={17} fontSize="9" fill="#8c8c8c">
              G {consumptionSeries.generalBaselineRateLph != null ? consumptionSeries.generalBaselineRateLph.toFixed(1) : 'N/A'} | A {consumptionSeries.activeBaselineRateLph != null ? consumptionSeries.activeBaselineRateLph.toFixed(1) : 'N/A'} | Spike {(consumptionSeries.suddenChangeThresholdLph || 0).toFixed(1)}
            </text>
          )}
        </g>

        {/* Axes labels */}
        <text x={-40} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, -40, ${chartHeight / 2})`}>
          Fuel Level (Liters)
        </text>
        <text x={chartWidth / 2} y={chartHeight + 28} textAnchor="middle" fontSize="11" fill="#666">
          Time
        </text>
      </g>
    </svg>
  );
};

export default StableFuelConsumptionAnalysis;
